<template>
  <link
    rel="stylesheet"
    href="https://cdnjs.cloudflare.com/ajax/libs/font-awesome/5.15.4/css/all.min.css"
  />

  <div class="analysis-shell">
    <div class="bg-grid"></div>
    <div class="bg-glow"></div>

    <header class="page-header">
    </header>

    <div class="analysis-card" :class="['trend-' + getTrendCssKey(longTerm), `token-${String(selectedToken || '').toLowerCase()}`]">
      <div class="card-top">
        <div class="pair-row">
          <div class="asset-selector">
            <button
              ref="tokenToggle"
              type="button"
              class="asset-box asset-toggle"
              :class="[`asset-${String(selectedToken || '').toLowerCase()}`, { open: isTokenMenuOpen }]"
              @click.stop="toggleTokenMenu"
            >
              <span class="pair-icon" aria-hidden="true">
                <i v-if="selectedToken === 'BTC'" class="fab fa-bitcoin"></i>
                <i v-else-if="selectedToken === 'ETH'" class="fab fa-ethereum"></i>
                <img
                  v-else-if="selectedToken === 'SOL'"
                  class="solana-icon"
                  src="https://solana.com/src/img/branding/solanaLogoMark.svg"
                  alt=""
                />
                <img
                  v-else
                  class="xrp-symbol"
                  src="https://cdn.simpleicons.org/xrp/ffffff"
                  alt=""
                />
              </span>
              <span class="pair">{{ selectedTokenLabel }}</span>
              <i class="fas fa-chevron-down token-chevron" aria-hidden="true"></i>
            </button>

            <transition name="menu-fade">
              <div v-if="isTokenMenuOpen" ref="tokenMenu" class="token-menu">
                <button
                  v-for="token in tokens"
                  :key="token.key"
                  type="button"
                  class="token-menu-item"
                  :class="[`asset-${String(token.key || '').toLowerCase()}`, { active: token.key === selectedToken }]"
                  @click="selectTokenFromMenu(token.key)"
                >
                  <span class="pair-icon" aria-hidden="true">
                    <i v-if="token.key === 'BTC'" class="fab fa-bitcoin"></i>
                    <i v-else-if="token.key === 'ETH'" class="fab fa-ethereum"></i>
                    <img
                      v-else-if="token.key === 'SOL'"
                      class="solana-icon"
                      src="https://solana.com/src/img/branding/solanaLogoMark.svg"
                      alt=""
                    />
                    <img
                      v-else
                      class="xrp-symbol"
                      src="https://cdn.simpleicons.org/xrp/ffffff"
                      alt=""
                    />
                  </span>
                  <span>{{ token.label }}</span>
                </button>
              </div>
            </transition>
          </div>
          <div class="trend-box">
            <button
              type="button"
              class="trend-pill"
              :class="[
                'pill-' + getTrendCssKey(shortTerm),
                selectedRange === 'shortTerm' ? 'is-active' : ''
              ]"
              @click="setRange('shortTerm')"
            >
              <span class="pill-copy">
                <span class="pill-label">Curto prazo</span>
                <span class="pill-timeframe">15m</span>
              </span>
              <span class="pill-value" :title="getTrendText(shortTerm)">
                <i :class="getTrendIcon(shortTerm)"></i>
              </span>
            </button>
            <button
              type="button"
              class="trend-pill"
              :class="[
                'pill-' + getTrendCssKey(midTerm),
                selectedRange === 'midTerm' ? 'is-active' : ''
              ]"
              @click="setRange('midTerm')"
            >
              <span class="pill-copy">
                <span class="pill-label">Médio prazo</span>
                <span class="pill-timeframe">4h</span>
              </span>
              <span class="pill-value" :title="getTrendText(midTerm)">
                <i :class="getTrendIcon(midTerm)"></i>
              </span>
            </button>
            <button
              type="button"
              class="trend-pill"
              :class="[
                'pill-' + getTrendCssKey(longTerm),
                selectedRange === 'longTerm' ? 'is-active' : ''
              ]"
              @click="setRange('longTerm')"
            >
              <span class="pill-copy">
                <span class="pill-label">Longo prazo</span>
                <span class="pill-timeframe">1D</span>
              </span>
              <span class="pill-value" :title="getTrendText(longTerm)">
                <i :class="getTrendIcon(longTerm)"></i>
              </span>
            </button>
          </div>
        </div>
        <section
          class="general-trend-card"
          :class="`trend-${getTrendCssKey(generalTrendCard.state)}`"
        >
          <h3 class="general-title"><span>{{ generalTrendCard.title }}</span></h3>
          <p class="general-text">{{ formatCardBodyText(generalTrendCard.text) }}</p>
        </section>
      </div>

      <div class="content-grid">
        <div ref="pricePanel" class="price-panel" :class="pricePanelThemeClass">
          <div class="analysis-spotlight">
            <div class="price-line spotlight-price-line">
              <div class="price-main">
                <div class="price-value-row">
                  <h2 class="price-value">
                    ${{ formatPrice(currentPrice) }}
                  </h2>
                  <span class="price-main-emblem" aria-hidden="true">
                    <i v-if="selectedToken === 'BTC'" class="fab fa-bitcoin"></i>
                    <i v-else-if="selectedToken === 'ETH'" class="fab fa-ethereum"></i>
                    <img
                      v-else-if="selectedToken === 'SOL'"
                      class="solana-icon"
                      src="https://solana.com/src/img/branding/solanaLogoMark.svg"
                      alt=""
                    />
                    <img
                      v-else
                      class="xrp-symbol"
                      src="https://cdn.simpleicons.org/xrp/ffffff"
                      alt=""
                    />
                  </span>
                </div>
                <p v-if="hasDayChange" class="day-change" :class="dayChangeClass">
                  <i :class="dayChangeIcon"></i>
                  <span class="day-change-value">{{ formattedDayDelta }}</span>
                  <span class="day-change-pct">{{ formattedDayPct }}</span>
                </p>
                <p v-else class="day-change day-change-neutral">
                  <i class="fas fa-minus"></i>
                  <span class="day-change-value">--</span>
                  <span class="day-change-pct">--</span>
                </p>
              </div>
              <div class="day-levels">
                <div
                  v-for="level in dayLevelItems"
                  :key="level.key"
                  class="day-level-chip"
                >
                  <span class="day-level-label">{{ level.label }}</span>
                  <span class="day-level-value">{{ level.value }}</span>
                </div>
              </div>
            </div>

            <section class="spotlight-mini-chart" :class="{ 'is-empty': !sparklineHasData }">
              <div class="spotlight-mini-chart-head">
                <div class="spotlight-mini-chart-kicker">
                  <span class="spotlight-mini-chart-kicker-dot" :class="`trend-${sparklineTrendKey}`"></span>
                  <span>{{ sparklineRangeMeta.label }}</span>
                </div>
                <div class="spotlight-mini-chart-stats" :class="[sparklineDeltaClass, `trend-${sparklineTrendKey}`]">
                  <span class="spotlight-mini-chart-price">{{ sparklineHeaderPrice }}</span>
                  <span class="spotlight-mini-chart-delta">{{ sparklineHeaderDelta }}</span>
                  <span class="spotlight-mini-chart-window">{{ sparklineRangeMeta.changeLabel }}</span>
                </div>
              </div>

              <div v-if="sparklineHasData" class="spotlight-mini-chart-stage">
                <svg
                  class="spotlight-mini-chart-svg"
                  viewBox="0 0 320 98"
                  preserveAspectRatio="none"
                  aria-hidden="true"
                >
                  <defs>
                    <linearGradient :id="sparklineAreaGradientId" x1="0%" y1="0%" x2="0%" y2="100%">
                      <stop offset="0%" :stop-color="sparklineVisualStops[0]" stop-opacity="0.42" />
                      <stop offset="55%" :stop-color="sparklineVisualStops[1]" stop-opacity="0.16" />
                      <stop offset="100%" :stop-color="sparklineVisualStops[2]" stop-opacity="0" />
                    </linearGradient>
                  </defs>

                  <path
                    v-for="guide in sparklineGuides"
                    :key="guide.key"
                    class="spotlight-mini-chart-guide"
                    :d="guide.path"
                  />

                  <path
                    class="spotlight-mini-chart-area"
                    :d="sparklineGeometry.areaPath"
                    :fill="`url(#${sparklineAreaGradientId})`"
                  />
                  <path
                    class="spotlight-mini-chart-line"
                    :d="sparklineGeometry.linePath"
                    :style="sparklineLineStyle"
                  />

                  <circle
                    class="spotlight-mini-chart-point-pulse"
                    :cx="sparklineGeometry.lastPoint.x"
                    :cy="sparklineGeometry.lastPoint.y"
                    r="4.4"
                  />
                  <circle
                    class="spotlight-mini-chart-point-glow"
                    :cx="sparklineGeometry.lastPoint.x"
                    :cy="sparklineGeometry.lastPoint.y"
                    r="7"
                  />
                  <circle
                    class="spotlight-mini-chart-point"
                    :cx="sparklineGeometry.lastPoint.x"
                    :cy="sparklineGeometry.lastPoint.y"
                    r="3.4"
                  />
                </svg>
              </div>

              <div v-else class="spotlight-mini-chart-empty">
                <span>{{ sparklineLoading ? 'Carregando estrutura do gráfico...' : 'Gráfico temporariamente indisponível.' }}</span>
              </div>
            </section>

            <button
              type="button"
              class="spotlight-action"
              @click="openSharedChart"
            >
              <i class="fas fa-chart-line"></i>
              Abrir gráfico
            </button>
          </div>

          <section class="quick-narrative">
            <div class="narrative-list">
              <article
                class="narrative-item"
                :class="'trend-' + getTrendCssKey(selectedNarrativeItem.trend)"
              >
                <div class="narrative-item-head">
                  <span class="range">{{ selectedNarrativeItem.headline }}</span>
                </div>
                <p class="narrative-item-text">{{ formatCardBodyText(selectedNarrativeItem.text) }}</p>
              </article>
            </div>
          </section>

          <section
            class="narrative-insight-strip"
            :class="'trend-' + getTrendCssKey(selectedNarrativeItem.trend)"
          >
            <transition name="insight-carousel" mode="out-in">
              <p
                :key="`${getInsightContextKey()}|${insightText}`"
                class="narrative-insight-strip-text"
              >
                {{ insightText }}
              </p>
            </transition>
          </section>

        </div>

        <div
          ref="libraryShell"
          class="analysis-library-shell"
          :class="pricePanelThemeClass"
          :style="libraryShellStyle"
        >
          <section class="library-archive-panel">
            <div
              v-if="selectedTokenAnalyses.length"
              class="analysis-library-grid analysis-library-grid-inline"
            >
              <button
                type="button"
                class="analysis-library-card analysis-library-card-featured"
                @click="openAnalysisVideoModal"
              >
                <div class="analysis-library-card-body">
                  <span class="analysis-library-watermark" aria-hidden="true">
                    <i v-if="selectedToken === 'BTC'" class="fab fa-bitcoin"></i>
                    <i v-else-if="selectedToken === 'ETH'" class="fab fa-ethereum"></i>
                    <img
                      v-else-if="selectedToken === 'SOL'"
                      class="solana-icon"
                      src="https://solana.com/src/img/branding/solanaLogoMark.svg"
                      alt=""
                    />
                    <img
                      v-else
                      class="xrp-symbol"
                      src="https://cdn.simpleicons.org/xrp/ffffff"
                      alt=""
                    />
                  </span>
                  <div class="analysis-library-card-topline">
                    <span class="analysis-library-card-meta analysis-library-card-date">
                      <i class="far fa-calendar-alt" aria-hidden="true"></i>
                      <span>{{ selectedAnalysis.date }}</span>
                    </span>
                    <span class="analysis-library-card-meta analysis-library-card-time">
                      <i class="far fa-clock" aria-hidden="true"></i>
                      <span>{{ selectedAnalysis.time }}</span>
                    </span>
                    <span class="analysis-library-card-meta analysis-library-card-duration">
                      <i class="far fa-play-circle" aria-hidden="true"></i>
                      <span>{{ getAnalysisDurationLabel(selectedAnalysis) }}</span>
                    </span>
                  </div>
                  <p class="analysis-library-card-title">{{ cleanAnalysisTitle }}</p>
                  <div class="analysis-library-card-summary analysis-library-card-summary-featured">
                    <span class="analysis-library-card-summary-featured-action">
                      <span class="analysis-library-card-summary-dot"></span>
                      <span>Abrir análise</span>
                    </span>
                    <span class="library-feature-current">Última análise</span>
                  </div>
                </div>
              </button>

              <template v-if="analysisArchiveItems.length">
              <button
                v-for="(item, index) in analysisArchiveItems"
                :key="item.id"
                type="button"
                :class="['analysis-library-card', `variant-${index % 3}`]"
                @click="openAnalysisVideoModalForItem(item)"
              >
                <div class="analysis-library-card-body">
                  <span class="analysis-library-watermark" aria-hidden="true">
                    <i v-if="selectedToken === 'BTC'" class="fab fa-bitcoin"></i>
                    <i v-else-if="selectedToken === 'ETH'" class="fab fa-ethereum"></i>
                    <img
                      v-else-if="selectedToken === 'SOL'"
                      class="solana-icon"
                      src="https://solana.com/src/img/branding/solanaLogoMark.svg"
                      alt=""
                    />
                    <img
                      v-else
                      class="xrp-symbol"
                      src="https://cdn.simpleicons.org/xrp/ffffff"
                      alt=""
                    />
                  </span>
                  <div class="analysis-library-card-topline">
                    <span class="analysis-library-card-meta analysis-library-card-date">
                      <i class="far fa-calendar-alt" aria-hidden="true"></i>
                      <span>{{ item.date }}</span>
                    </span>
                    <span class="analysis-library-card-meta analysis-library-card-time">
                      <i class="far fa-clock" aria-hidden="true"></i>
                      <span>{{ item.time }}</span>
                    </span>
                    <span class="analysis-library-card-meta analysis-library-card-duration">
                      <i class="far fa-play-circle" aria-hidden="true"></i>
                      <span>{{ getAnalysisDurationLabel(item) }}</span>
                    </span>
                  </div>
                  <p class="analysis-library-card-title">{{ item.title }}</p>
                  <div class="analysis-library-card-summary">
                    <span class="analysis-library-card-summary-dot"></span>
                    <span>Abrir análise</span>
                  </div>
                </div>
              </button>
              </template>
            </div>

            <div v-else class="library-archive-empty">
              <p>Nenhuma aula anterior publicada para este ativo ainda.</p>
            </div>
          </section>
        </div>
      </div>

      <div class="footer-grid">
      </div>
    </div>

    <teleport to="body">
      <transition name="analysis-video-fade">
        <div
          v-if="isAnalysisVideoModalOpen"
          class="analysis-video-overlay"
          @click.self="closeAnalysisVideoModal"
        >
          <div
            class="analysis-video-modal"
            :class="analysisVideoThemeClass"
            role="dialog"
            aria-modal="true"
            aria-labelledby="analysis-video-title"
          >
            <button
              class="analysis-video-close"
              type="button"
              @click="closeAnalysisVideoModal"
              aria-label="Fechar análise em vídeo"
            >
              <i class="fas fa-times"></i>
            </button>
            <p class="analysis-video-kicker">
              <span class="analysis-video-asset">
                <span class="asset-icon" aria-hidden="true">
                  <i v-if="selectedToken === 'BTC'" class="fab fa-bitcoin"></i>
                  <i v-else-if="selectedToken === 'ETH'" class="fab fa-ethereum"></i>
                  <img
                    v-else-if="selectedToken === 'SOL'"
                    class="solana-icon"
                    src="https://solana.com/src/img/branding/solanaLogoMark.svg"
                    alt=""
                  />
                  <img
                    v-else
                    class="xrp-symbol"
                    src="https://cdn.simpleicons.org/xrp/ffffff"
                    alt=""
                  />
                </span>
                <span class="asset-label">{{ selectedTokenLabel }}</span>
              </span>
              <span class="analysis-video-kicker-sep">•</span>
              <span>{{ analysisVideoPublishedText }}</span>
            </p>
            <h2 id="analysis-video-title" class="analysis-video-title">{{ modalAnalysisTitle }}</h2>
            <div class="analysis-video-frame-shell">
              <iframe
                v-if="analysisVideoEmbedUrl"
                :src="analysisVideoEmbedUrl"
                title="Vídeo da análise selecionada"
                frameborder="0"
                allow="autoplay; fullscreen; picture-in-picture; clipboard-write; encrypted-media"
                referrerpolicy="strict-origin-when-cross-origin"
                allowfullscreen
              ></iframe>
              <div v-else class="analysis-video-empty">
                <p>Vídeo indisponível para esta análise.</p>
                <a
                  v-if="modalAnalysis.chartUrl && modalAnalysis.chartUrl !== '#'"
                  :href="modalAnalysis.chartUrl"
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Abrir gráfico relacionado
                </a>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </teleport>

    <div v-if="error" class="error-message">
      <i class="fas fa-exclamation-triangle"></i>
      {{ error }}
    </div>
  </div>
</template>

<script>
import { FALLBACK_TREND_INSIGHT, getTrendInsightCandidates } from '@/constants/trendInsights';
import { GRAFICOSWS_API_URL } from '@/config/env';

export default {
  name: 'Analysis',
  data() {
    return {
      analises: [],
      tokens: [
        { key: 'BTC', label: 'Bitcoin', pair: 'BTCUSDT.P' },
        { key: 'ETH', label: 'Ethereum', pair: 'ETHUSDT.P' },
        { key: 'SOL', label: 'Solana', pair: 'SOLUSDT.P' },
        { key: 'XRP', label: 'XRP', pair: 'XRPUSDT.P' }
      ],
      selectedToken: 'BTC',
      chartSymbol: null,
      chartInterval: null,
      tvWidget: null,
      chartApi: null,
      pendingInterval: null,
      intervalRetryTimer: null,
      selectedRange: 'shortTerm',
      userSelected: false,
      loading: false,
      error: null,
      livePrice: null,
      liveSyncTimer: null,
      analysesRefreshTimer: null,
      fetchAnalysesInFlight: false,
      dayLevelsTimer: null,
      dailyCloseCache: {},
      dayChange: {
        delta: null,
        pct: null,
        open: null
      },
      dayLevels: {
        open: null,
        high: null,
        low: null
      },
      isTokenMenuOpen: false,
      isHistoryOpen: false,
      currentInsightText: '',
      insightCycleByContext: {},
      narrativeInsightTimer: null,
      pinnedAnalysisByToken: {
        BTC: 'btc-20260728-1630',
        ETH: 'eth-20260728-1630',
        SOL: 'sol-20260728-1630',
        XRP: 'xrp-20260420-1530'
      },
      analysisLibrary: {
        BTC: [
          {
            id: 'btc-20260728-1630',
            title: 'Sem convicção para nenhum lado...',
            date: '28/07/2026',
            time: '16:30',
            chartUrl: '#',
            videoUrl: '1213678862'
          },
          {
            id: 'btc-20260714-1730',
            title: 'Visão ampla do mercado.',
            date: '14/07/2026',
            time: '17:30',
            chartUrl: '#',
            videoUrl: '1209984942'
          },
          {
            id: 'btc-20260707-1730',
            title: 'Estrutura de baixa no fundo?',
            date: '07/07/2026',
            time: '17:30',
            chartUrl: '#',
            videoUrl: '1207944487'
          },
          {
            id: 'btc-20260702-1730',
            title: 'Renovando fundos e mais fraco que alts',
            date: '02/07/2026',
            time: '17:30',
            chartUrl: '#',
            videoUrl: '1206596716'
          },
          {
            id: 'btc-20260608-1600',
            title: 'Reclaim sem convicção',
            date: '08/06/2026',
            time: '16:00',
            chartUrl: '#',
            videoUrl: '1200148142'
          },
          {
            id: 'btc-202600601-1830',
            title: 'BTC despecando com vendas de Saylor',
            date: '01/06/2026',
            time: '18:30',
            chartUrl: '#',
            videoUrl: '1197520836'
          },
          {
            id: 'btc-20260525-1830',
            title: 'Fundo varrido, diário Intacto, 77K como zona de decisão',
            date: '25/05/2026',
            time: '18:30',
            chartUrl: '#',
            videoUrl: '1195423579'
          },
          {
            id: 'btc-20260518-1800',
            title: 'A peça que faltava no tabuleiro',
            date: '18/05/2026',
            time: '18:00',
            chartUrl: '#',
            videoUrl: '1193384658'
          },
          {
            id: 'btc-20260516-1515',
            title: 'Estrutura do curto prazo cedida? E atualização da BONK',
            date: '16/05/2026',
            time: '15:15',
            chartUrl: '#',
            videoUrl: '1192867531'
          },
          {
            id: 'btc-20260511-2350',
            title: 'Estrutura completa: Liquidez, volume, nuvem e proxies',
            date: '11/05/2026',
            time: '23:50',
            chartUrl: '#',
            videoUrl: '1191395677'
          },
          {
            id: 'btc-20260504-1300',
            title: 'Bounce real ou squeeze? O que os proxies estão dizendo',
            date: '04/05/2026',
            time: '13:00',
            chartUrl: '#',
            videoUrl: '1189072861'
          },
          {
            id: 'btc-20260427-1910',
            title: 'Estrutura semanal desenhada, zonas para trades no curto prazo após validação bearish',
            date: '27/04/2026',
            time: '19:10',
            chartUrl: '#',
            videoUrl: '1187114266'
          },
          {
            id: 'btc-20260420-1530',
            title: 'O short que stopou e o que o mercado está montando agora',
            date: '20/04/2026',
            time: '15:30',
            chartUrl: '#',
            videoUrl: '1184873784'
          },
          {
            id: 'btc-20260415-2000',
            title: 'Lendo Liquidez, FVG e o Setup de Short na HTF Supply',
            date: '15/04/2026',
            time: '20:00',
            chartUrl: '#',
            videoUrl: '1183556943'
          },
          {
            id: 'btc-20260410-1830',
            title: 'Estrutura pós cessar fogo no Irã',
            date: '10/04/2026',
            time: '18:30',
            chartUrl: '#',
            videoUrl: '1182068977'
          },
          {
            id: 'btc-20260330-2000',
            title: 'A estrutura atual do BTC sob a lente de SPX e Oil',
            date: '30/03/2026',
            time: '20:00',
            chartUrl: '#',
            videoUrl: '1178633137'
          },
          {
            id: 'btc-20260304-1730',
            title: 'Zona S7/S8: onde o ciclo decide continuidade ou reset',
            date: '04/03/2026',
            time: '17:30',
            chartUrl: '#',
            videoUrl: '1170427650'
          },
          {
            id: 'btc-20260205-1500',
            title: 'Liquidez e psicologia: leitura estrutural de BTC/ETH/SOL',
            date: '05/02/2026',
            time: '15:00',
            chartUrl: '#',
            videoUrl: '1167874774'
          }
        ],
        ETH: [
          {
            id: 'eth-20260728-1630',
            title: 'Força relativa se confirma: nuvem reconquistada',
            date: '28/07/2026',
            time: '16:30',
            chartUrl: '#',
            videoUrl: '1213683317'
          },
          {
            id: 'eth-20260707-1730',
            title: 'Choque de curto prazo',
            date: '07/07/2026',
            time: '17:30',
            chartUrl: '#',
            videoUrl: '1207944590'
          },
          {
            id: 'eth-20260702-1730',
            title: 'Fraqueza se esvaindo aos poucos',
            date: '02/07/2026',
            time: '17:30',
            chartUrl: '#',
            videoUrl: '1206597967'
          },
          {
            id: 'eth-20260608-1600',
            title: 'O mais fraco do tabuleiro',
            date: '08/06/2026',
            time: '16:00',
            chartUrl: '#',
            videoUrl: '1200148197'
          },
          {
            id: 'eth-20260601-1830',
            title: 'A primeira força depois da fraqueza',
            date: '01/06/2026',
            time: '18:30',
            chartUrl: '#',
            videoUrl: '1197511326'
          },
          {
            id: 'eth-20260525-1830',
            title: 'Fraqueza Estrutural, proxies e zonas de long no radar',
            date: '25/05/2026',
            time: '18:30',
            chartUrl: '#',
            videoUrl: '1195423554'
          },
          {
            id: 'eth-20260518-1800',
            title: 'Fraqueza recorrente e proxies',
            date: '18/05/2026',
            time: '18:00',
            chartUrl: '#',
            videoUrl: '1193386937'
          },
          {
            id: 'eth-202605012-1630',
            title: 'Estrutura atual, dominância e zonas de suporte',
            date: '12/05/2026',
            time: '16:30',
            chartUrl: '#',
            videoUrl: '1191587791'
          },
          {
            id: 'eth-20260504-1300',
            title: 'Mais fraco que o bitcoin e os motivos estruturais por trás',
            date: '04/05/2026',
            time: '13:00',
            chartUrl: '#',
            videoUrl: '1189083968'
          },
          {
            id: 'eth-20260427-1910',
            title: 'Estrutura semanal desenhada, zonas para trades no curto prazo após validação bearish',
            date: '27/04/2026',
            time: '19:10',
            chartUrl: '#',
            videoUrl: '1187114074'
          },
          {
            id: 'eth-20260426-1815',
            title: 'Como uma invalidação salvou de um stop!',
            date: '26/04/2026',
            time: '18:15',
            chartUrl: '#',
            videoUrl: '1186760180'
          },
          {
            id: 'eth-20260420-1530',
            title: 'ETH ainda não deu setup. Mas está montando um.',
            date: '20/04/2026',
            time: '15:30',
            chartUrl: '#',
            videoUrl: '1184883873'
          },
          {
            id: 'eth-20260416-1900',
            title: 'Estrutural atual após caça de liquidez, ideias de setups e comportamento da nuvem.',
            date: '16/04/2026',
            time: '19:00',
            chartUrl: '#',
            videoUrl: '1183923418'
          },
          {
            id: 'eth-20260410-1830',
            title: 'Como está performando a líder das alts?',
            date: '10/04/2026',
            time: '18:30',
            chartUrl: '#',
            videoUrl: '1182069360'
          },
          {
            id: 'eth-20260317-1730',
            title: 'ETH/BTC: Estrutura, Rotação e Zonas de Interesse para Trades.',
            date: '17/03/2026',
            time: '17:30',
            chartUrl: '#',
            videoUrl: '1174532786'
          },
          {
            id: 'eth-20260304-1730',
            title: 'O gatilho da próxima rotação',
            date: '04/03/2026',
            time: '17:30',
            chartUrl: '#',
            videoUrl: '1170412735'
          },
          {
            id: 'eth-20260205-1500',
            title: 'Liquidez e psicologia: leitura estrutural de BTC/ETH/SOL',
            date: '05/02/2026',
            time: '15:00',
            chartUrl: '#',
            videoUrl: '1167874774'
          }
        ],
        SOL: [
          {
            id: 'sol-20260728-1630',
            title: 'Eco do ETH sem a mesma convicção',
            date: '28/07/2026',
            time: '16:30',
            chartUrl: '#',
            videoUrl: '1213688509'
          },
          {
            id: 'sol-20260702-1730',
            title: 'Sinais de força no preço e na dominância',
            date: '02/07/2026',
            time: '17:30',
            chartUrl: '#',
            videoUrl: '1206599142'
          },
          {
            id: 'sol-20260611-2130',
            title: 'Fundo de fevereiro como resistência',
            date: '11/06/2026',
            time: '21:30',
            chartUrl: '#',
            videoUrl: '1200623409'
          },
          {
            id: 'sol-20260602-1600',
            title: 'O que precisa acontecer antes de qualquer recuperação',
            date: '02/06/2026',
            time: '16:00',
            chartUrl: '#',
            videoUrl: '1198068194'
          },
          {
            id: 'sol-20260526-1600',
            title: 'Zonas de demanda e o que monitorar',
            date: '26/05/2026',
            time: '16:00',
            chartUrl: '#',
            videoUrl: '1196127022'
          },
          {
            id: 'sol-20260519-1415',
            title: 'Range, como está a fraqueza relativa e o que observar',
            date: '19/05/2026',
            time: '14:15',
            chartUrl: '#',
            videoUrl: '1193668382'
          },
          {
            id: 'sol-20260512-1630',
            title: 'Fraqueza relativa se esvaindo, zonas de decisão e update da BONK',
            date: '12/05/2026',
            time: '16:30',
            chartUrl: '#',
            videoUrl: '1191657941'
          },
          {
            id: 'sol-20260505-1600',
            title: 'Fraqueza relativa continua, liquidez Mapeada e o setup na BONK',
            date: '05/05/2026',
            time: '16:00',
            chartUrl: '#',
            videoUrl: '1189487416'
          },
          {
            id: 'sol-20260420-1530',
            title: 'Fraqueza relativa e onde ainda pode surgir oportunidade',
            date: '20/04/2026',
            time: '15:30',
            chartUrl: '#',
            videoUrl: '1184894632'
          },
          {
            id: 'sol-20260410-1830',
            title: 'SOL performando pior que os demais ativos',
            date: '10/04/2026',
            time: '18:30',
            chartUrl: '#',
            videoUrl: '1182078463'
          },
          {
            id: 'sol-20260402-2030',
            title: 'Dominância, rotação e fraqueza: o que está pressionando a SOL',
            date: '02/04/2026',
            time: '20:30',
            chartUrl: '#',
            videoUrl: '1179728049'
          },
          {
            id: 'sol-20260304-1730',
            title: 'Range de base formado — gatilhos pra retomar 100/115',
            date: '04/03/2026',
            time: '17:30',
            chartUrl: '#',
            videoUrl: '1170412020'
          },
          {
            id: 'sol-20260205-1500',
            title: 'Liquidez e psicologia: leitura estrutural de BTC/ETH/SOL',
            date: '05/02/2026',
            time: '15:00',
            chartUrl: '#',
            videoUrl: '1167874774'
          }
        ],
        XRP: [
          {
            id: 'xrp-20260420-1530',
            title: 'Base sustentada e próximas oportunidades',
            date: '20/04/2026',
            time: '15:30',
            chartUrl: '#',
            videoUrl: '1184912120'
          },
          {
            id: 'xrp-20260308-0200',
            title: 'Exaustão em S2, base em construção na S1',
            date: '08/03/2026',
            time: '02:00',
            chartUrl: '#',
            videoUrl: '1171438938'
          }
        ]
      },
      sharedChartUrls: {
        BTC: 'https://www.tradingview.com/chart/uCSD2Q7Q/',
        ETH: 'https://www.tradingview.com/chart/uCSD2Q7Q/?symbol=BYBIT%3AETHUSDT.P',
        SOL: 'https://www.tradingview.com/chart/uCSD2Q7Q/?symbol=BYBIT%3ASOLUSDT.P',
        XRP: 'https://www.tradingview.com/chart/uCSD2Q7Q/?symbol=BYBIT%3AXRPUSDT.P'
      },
      apiUrl: `${GRAFICOSWS_API_URL}/api/analysis`,
      isAnalysisVideoModalOpen: false,
      analysisVideoEmbedUrl: '',
      activeModalAnalysisId: null,
      sparklineSeries: [],
      sparklineLoading: false,
      sparklineSyncTimer: null,
      videoDurationById: {},
      videoDurationRequests: {},
      libraryShellHeight: null,
      pricePanelResizeObserver: null
    };
  },
  computed: {
    activeAnalise() {
      const target = this.selectedToken;
      return this.analises.find((a) => this.getTokenKeyFromPair(a.par) === target) || null;
    },
    selectedTokenMeta() {
      return this.tokens.find((t) => t.key === this.selectedToken) || this.tokens[0];
    },
    selectedTokenLabel() {
      return this.selectedTokenMeta?.label || 'Bitcoin';
    },
    selectedTokenPair() {
      return this.selectedTokenMeta?.pair || 'BTCUSDT.P';
    },
    currentPrice() {
      const live = this.toNumber(this.livePrice);
      if (live !== null) return live;
      const fallback = this.toNumber(this.activeAnalise?.preco_atual);
      return fallback !== null ? fallback : 0;
    },
    pricePanelThemeClass() {
      return `theme-${String(this.selectedToken || 'BTC').toLowerCase()}`;
    },
    selectedTokenAnalyses() {
      return this.analysisLibrary[this.selectedToken] || [];
    },
    analysisArchiveItems() {
      return this.selectedTokenAnalyses.slice(1);
    },
    selectedAnalysis() {
      const list = this.selectedTokenAnalyses;
      if (!list.length) {
        return {
          id: 'none',
          title: 'Sem analises publicadas',
          date: '--/--/----',
          time: '--:--',
          chartUrl: '#',
          videoUrl: ''
        };
      }
      return list[0];
    },
    cleanAnalysisTitle() {
      const rawTitle = this.selectedAnalysis?.title || '';
      return rawTitle.replace(/^\s*(BTC|ETH|SOL|XRP|RIPPLE)\s*:\s*/i, '').trim() || rawTitle;
    },
    modalAnalysis() {
      const fallback = this.selectedAnalysis;
      const modalId = String(this.activeModalAnalysisId || '').trim();
      if (!modalId) return fallback;
      return this.selectedTokenAnalyses.find((item) => item.id === modalId) || fallback;
    },
    modalAnalysisTitle() {
      const rawTitle = this.modalAnalysis?.title || '';
      return rawTitle.replace(/^\s*(BTC|ETH|SOL|XRP|RIPPLE)\s*:\s*/i, '').trim() || rawTitle;
    },
    selectedRangeLabel() {
      const labels = {
        shortTerm: 'Curto prazo',
        midTerm: 'Médio prazo',
        longTerm: 'Longo prazo'
      };
      return labels[this.selectedRange] || 'Leitura ativa';
    },
    shortTerm() {
      return this.resolveTrendState('shortTerm');
    },
    midTerm() {
      return this.resolveTrendState('midTerm');
    },
    longTerm() {
      return this.resolveTrendState('longTerm');
    },
    activeTimeframe() {
      return this.selectedRange;
    },
    comboKey() {
      return `${this.shortTerm}_${this.midTerm}_${this.longTerm}`;
    },
    generalTrendCard() {
      const map = {
        BULL_BULL_BULL: {
          title: 'Tendência Geral de Alta',
          text: 'Alinhamento total comprador. A direção está definida - correções são oportunidade, não ameaça.'
        },
        NEUTRAL_BULL_BULL: {
          title: 'Alta com Pausa no Curto',
          text: 'Estrutura de alta intacta no médio e longo. O curto está digerindo - a retomada tende a continuar a direção maior.'
        },
        BEAR_BULL_BULL: {
          title: 'Alta em Pullback',
          text: 'Queda no curto contra tendência maior de alta. O recuo é saudável enquanto os prazos maiores não cederem.'
        },
        BULL_NEUTRAL_BULL: {
          title: 'Alta em Transição no Swing',
          text: 'Longo e curto comprados, médio ainda indefinido. A tendência maior valida longs - aguarda o swing confirmar para aumentar convicção.'
        },
        NEUTRAL_NEUTRAL_BULL: {
          title: 'Alta Macro com Consolidação',
          text: 'Longo define o regime comprador, curto e médio ainda laterais. Paciência - o regime maior é o filtro que vale.'
        },
        BEAR_NEUTRAL_BULL: {
          title: 'Alta Macro Pressionada no Curto',
          text: 'Tendência de longo intacta, mas curto recua. A queda é teste da estrutura maior, não reversão - enquanto o longo segurar.'
        },
        BULL_BEAR_BULL: {
          title: 'Alta com Correção no Swing',
          text: 'Curto e longo apontam para cima, médio corrige. Padrão sugere retomada após exaustão vendedora no 4H.'
        },
        NEUTRAL_BEAR_BULL: {
          title: 'Alta de Longo com Swing Cedendo',
          text: 'O longo ainda segura o regime, mas o médio pressiona. A retomada depende do curto confirmar direção primeiro.'
        },
        BEAR_BEAR_BULL: {
          title: 'Alta Estrutural sob Pressão',
          text: 'Curto e médio vendidos, mas o longo ainda sustenta. Exige paciência - o setup melhora quando o curto reverter sem perder o suporte estrutural.'
        },
        BULL_BULL_NEUTRAL: {
          title: 'Impulso sem Regime Macro',
          text: 'Curto e médio comprados, mas o longo ainda não define regime. O movimento pode render, mas a convicção é menor sem o filtro do longo.'
        },
        NEUTRAL_BULL_NEUTRAL: {
          title: 'Viés de Alta sem Confirmação Macro',
          text: 'O médio puxa para cima, mas curto e longo ainda neutros. Espera o curto alinhar - a alta do swing sem suporte macro é mais frágil.'
        },
        BEAR_BULL_NEUTRAL: {
          title: 'Tensão Direcional',
          text: 'O médio sobe enquanto o curto cai e o longo é neutro. Conflito entre prazos - aguarda alinhamento antes de tomar posição.'
        },
        BULL_NEUTRAL_NEUTRAL: {
          title: 'Impulso Tático em Terreno Neutro',
          text: 'Reação compradora no curto em contexto lateral. Movimentos são mais curtos e reversíveis enquanto o médio e longo não confirmarem direção.'
        },
        NEUTRAL_NEUTRAL_NEUTRAL: {
          title: 'Equilíbrio Total',
          text: 'Todos os prazos sem direção definida. Aguarda o primeiro rompimento com fechamento e volume - não antecipa.'
        },
        BEAR_NEUTRAL_NEUTRAL: {
          title: 'Pressão de Curto em Contexto Lateral',
          text: 'O curto recua enquanto médio e longo ficam neutros. Pode ser rotação dentro de range - o contexto mais amplo ainda não define tendência.'
        },
        BULL_BEAR_NEUTRAL: {
          title: 'Conflito entre Curto e Swing',
          text: 'Curto sobe, médio cede, longo neutro. Ambiente de reversões rápidas - disciplina na execução e realizações parciais.'
        },
        NEUTRAL_BEAR_NEUTRAL: {
          title: 'Pressão no Swing sem Definição Macro',
          text: 'O médio conduz para baixo, mas curto e longo ficam neutros. Cautela até que o curto defina direção com mais clareza.'
        },
        BEAR_BEAR_NEUTRAL: {
          title: 'Baixa Pendente de Confirmação',
          text: 'Curto e médio vendedores, longo ainda neutro. A estrutura pende para baixa - o longo confirmando seria o sinal que falta.'
        },
        BULL_BULL_BEAR: {
          title: 'Repique em Regime de Baixa',
          text: 'Curto e médio sobem contra tendência de longo prazo. O movimento pode render - realiza cedo, a estrutura maior ainda é baixista.'
        },
        NEUTRAL_BULL_BEAR: {
          title: 'Rali de Alívio sob Baixa Estrutural',
          text: 'O médio se recupera, mas o longo ainda pesa para baixo. Sem melhora no longo, a recuperação do swing tende a ser absorvida pelo regime maior.'
        },
        BEAR_BULL_BEAR: {
          title: 'Rali de Médio em Regime Baixista',
          text: 'O swing tenta subir, mas curto e longo permanecem vendedores. Sinal de fragilidade - a estrutura maior ainda domina.'
        },
        BULL_NEUTRAL_BEAR: {
          title: 'Reação Curta contra Baixa Dominante',
          text: 'O curto reage, mas o médio não confirma e o longo é baixista. Posições longas devem ser enxutas - o contexto maior ainda é adverso.'
        },
        NEUTRAL_NEUTRAL_BEAR: {
          title: 'Baixa Macro com Pausa',
          text: 'O longo define o regime baixista enquanto curto e médio descansam. A próxima expansão tende a respeitar essa direção.'
        },
        BEAR_NEUTRAL_BEAR: {
          title: 'Baixa Estrutural com Pausa no Swing',
          text: 'Curto e longo vendedores, médio em equilíbrio. Continuidade baixista provável - repiques possíveis, mas sem catalisador de reversão estrutural.'
        },
        BULL_BEAR_BEAR: {
          title: 'Reação Curta contra Baixa Ampla',
          text: 'O curto reage, mas médio e longo seguem baixistas. Opera o repique com objetivos curtos e realização rápida.'
        },
        NEUTRAL_BEAR_BEAR: {
          title: 'Baixa Dominante com Respiro Tático',
          text: 'O curto pausa enquanto médio e longo continuam vendidos. Momento de reduzir exposição comprada, não de aumentar.'
        },
        BEAR_BEAR_BEAR: {
          title: 'Tendência Geral de Baixa',
          text: 'Todos os prazos vendidos. Repiques sem fechamento HTF são armadilhas. Postura defensiva até mudança de estrutura.'
        }
      };

      const base = map[this.comboKey];
      const state = this.getOverallState();
      if (base) {
        return { ...base, state };
      }

      return {
        state,
        title: 'Tendência Geral em Avaliação',
        text: 'A estrutura está em transição entre prazos. Aguarde confirmação de fluxo antes de aumentar convicção.'
      };
    },
    insightText() {
      return this.currentInsightText || this.limitInsight(FALLBACK_TREND_INSIGHT);
    },
    insightCandidates() {
      const candidates = getTrendInsightCandidates(this.comboKey, this.activeTimeframe)
        .map((text) => this.limitInsight(text))
        .filter(Boolean);

      return candidates.length
        ? candidates
        : [this.limitInsight(FALLBACK_TREND_INSIGHT)];
    },
    narrativeByRange() {
      const ranges = [
        { key: 'shortTerm', label: 'Curto prazo', timeframe: '15m', trend: this.shortTerm },
        { key: 'midTerm', label: 'Médio prazo', timeframe: '4h', trend: this.midTerm },
        { key: 'longTerm', label: 'Longo prazo', timeframe: '1D', trend: this.longTerm }
      ];

      return ranges.map((range) => ({
        ...range,
        ...this.describeTrendNarrative(range.trend, range.key, range.label)
      }));
    },
    selectedNarrativeItem() {
      return (
        this.narrativeByRange.find((item) => item.key === this.selectedRange) ||
        this.narrativeByRange[0]
      );
    },
    hasDayChange() {
      return Number.isFinite(this.dayChange.delta) && Number.isFinite(this.dayChange.pct);
    },
    dayChangeClass() {
      if (!this.hasDayChange) return 'day-change-neutral';
      if (this.dayChange.delta > 0) return 'day-change-up';
      if (this.dayChange.delta < 0) return 'day-change-down';
      return 'day-change-neutral';
    },
    dayChangeIcon() {
      if (!this.hasDayChange || this.dayChange.delta === 0) return 'fas fa-minus';
      return this.dayChange.delta > 0 ? 'fas fa-arrow-up' : 'fas fa-arrow-down';
    },
    formattedDayDelta() {
      if (!this.hasDayChange) return '--';
      const fractionDigits = this.getTokenPriceFractionDigits();
      const sign = this.dayChange.delta > 0 ? '+' : '';
      return `${sign}${Math.abs(this.dayChange.delta).toLocaleString('en-US', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
      })}`;
    },
    formattedDayPct() {
      if (!this.hasDayChange) return '--';
      const sign = this.dayChange.pct > 0 ? '+' : '';
      return `${sign}${Math.abs(this.dayChange.pct).toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}%`;
    },
    dayLevelItems() {
      return [
        { key: 'open', label: 'Abertura', value: this.formatLevelValue(this.dayLevels.open) },
        { key: 'high', label: 'Máxima', value: this.formatLevelValue(this.dayLevels.high) },
        { key: 'low', label: 'Mínima', value: this.formatLevelValue(this.dayLevels.low) }
      ];
    },
    sparklineRangeMeta() {
      const map = {
        shortTerm: {
          label: 'Fluxo curto',
          windowLabel: '15m • 6h',
          changeLabel: '6H',
          interval: '15',
          limit: 24,
          refreshMs: 20000
        },
        midTerm: {
          label: 'Fluxo swing',
          windowLabel: '4h • 4d',
          changeLabel: '4D',
          interval: '240',
          limit: 24,
          refreshMs: 45000
        },
        longTerm: {
          label: 'Fluxo macro',
          windowLabel: '1d • 30d',
          changeLabel: '30D',
          interval: 'D',
          limit: 30,
          refreshMs: 120000
        }
      };

      return map[this.selectedRange] || map.shortTerm;
    },
    sparklineHasData() {
      return this.sparklineSeries.length >= 2 && !!this.sparklineGeometry.linePath;
    },
    sparklineTrendState() {
      if (this.selectedRange === 'midTerm') return this.midTerm;
      if (this.selectedRange === 'longTerm') return this.longTerm;
      return this.shortTerm;
    },
    sparklineTrendKey() {
      return this.getTrendCssKey(this.sparklineTrendState);
    },
    sparklineStartPrice() {
      const first = this.toNumber(this.sparklineSeries?.[0]?.close);
      return Number.isFinite(first) ? first : null;
    },
    sparklineLastPrice() {
      const last = this.toNumber(this.sparklineSeries?.[this.sparklineSeries.length - 1]?.close);
      if (Number.isFinite(last)) return last;
      const live = this.toNumber(this.livePrice);
      return Number.isFinite(live) ? live : null;
    },
    sparklineDeltaPctValue() {
      const start = this.sparklineStartPrice;
      const end = this.sparklineLastPrice;
      if (!Number.isFinite(start) || !Number.isFinite(end) || start <= 0) return null;
      return ((end - start) / start) * 100;
    },
    sparklineDeltaClass() {
      const value = this.sparklineDeltaPctValue;
      if (!Number.isFinite(value)) return 'is-neutral';
      if (value > 0) return 'is-up';
      if (value < 0) return 'is-down';
      return 'is-neutral';
    },
    sparklineHeaderPrice() {
      const value = this.sparklineLastPrice;
      if (!Number.isFinite(value) || value <= 0) return '--';
      return `$${this.formatPrice(value)}`;
    },
    sparklineHeaderDelta() {
      const value = this.sparklineDeltaPctValue;
      if (!Number.isFinite(value)) return '--';
      const sign = value > 0 ? '+' : value < 0 ? '' : '';
      return `${sign}${value.toLocaleString('en-US', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2
      })}%`;
    },
    sparklineAreaGradientId() {
      return `spotlight-sparkline-fill-${String(this.selectedToken || 'btc').toLowerCase()}-${this.selectedRange}`;
    },
    sparklineGradientStops() {
      const map = {
        BTC: ['#ffd166', '#f59e0b', '#f97316'],
        ETH: ['#8ba5ff', '#5b7cff', '#3b82f6'],
        SOL: ['#a78bfa', '#7c6cff', '#38bdf8'],
        XRP: ['#f5f7ff', '#cbd5e1', '#94a3b8']
      };
      return map[this.selectedToken] || map.BTC;
    },
    sparklineVisualStops() {
      if (this.sparklineTrendState === 'BULL') {
        return ['#86efac', '#34d399', '#059669'];
      }
      if (this.sparklineTrendState === 'BEAR') {
        return ['#fca5a5', '#f87171', '#ef4444'];
      }
      return this.sparklineGradientStops;
    },
    sparklineLineStyle() {
      const [start, mid] = this.sparklineVisualStops;
      return {
        stroke: `color-mix(in srgb, ${mid} 88%, #ffffff 12%)`,
        filter: `drop-shadow(0 0 8px color-mix(in srgb, ${start} 22%, transparent))`
      };
    },
    sparklineGuides() {
      return [24, 49, 74].map((y, index) => ({
        key: index,
        path: `M 0 ${y} L 320 ${y}`
      }));
    },
    sparklineGeometry() {
      const values = this.sparklineSeries
        .map((point) => this.toNumber(point?.close))
        .filter((value) => Number.isFinite(value));

      if (values.length < 2) {
        return {
          linePath: '',
          areaPath: '',
          lastPoint: { x: 0, y: 0 }
        };
      }

      const width = 320;
      const height = 98;
      const paddingX = 4;
      const topPadding = 10;
      const bottomPadding = 10;
      const min = Math.min(...values);
      const max = Math.max(...values);
      const span = Math.max(max - min, Math.max(Math.abs(max) * 0.0015, 1e-8));
      const usableWidth = width - paddingX * 2;
      const usableHeight = height - topPadding - bottomPadding;

      const coords = values.map((value, index) => {
        const x = paddingX + (usableWidth * index) / (values.length - 1);
        const ratio = (value - min) / span;
        const y = topPadding + usableHeight - ratio * usableHeight;
        return {
          x: Number(x.toFixed(2)),
          y: Number(y.toFixed(2))
        };
      });

      const linePath = coords
        .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
        .join(' ');
      const lastPoint = coords[coords.length - 1];
      const areaPath = `${linePath} L ${lastPoint.x} ${height - bottomPadding} L ${coords[0].x} ${height - bottomPadding} Z`;

      return {
        linePath,
        areaPath,
        lastPoint
      };
    },
    activeSharedChartUrl() {
      const direct = String(this.sharedChartUrls?.[this.selectedToken] || '').trim();
      if (direct) return direct;

      const symbol = this.toBybitSymbol(this.selectedTokenPair);
      if (!symbol) return 'https://www.tradingview.com/';
      return `https://www.tradingview.com/symbols/${symbol}/?exchange=BYBIT`;
    },
    analysisVideoPublishedText() {
      const date = this.modalAnalysis?.date || '';
      const time = this.modalAnalysis?.time || '';
      if (date && time) return `${date} • ${time}`;
      if (date) return date;
      if (time) return time;
      return '--/--/---- • --:--';
    },
    analysisVideoThemeClass() {
      return `token-${String(this.selectedToken || 'BTC').toLowerCase()}`;
    },
    libraryShellStyle() {
      if (!this.libraryShellHeight) return null;
      return {
        height: `${this.libraryShellHeight}px`,
        minHeight: `${this.libraryShellHeight}px`,
        maxHeight: `${this.libraryShellHeight}px`
      };
    }
  },
  mounted() {
    this.loadVideoDurationCache();
    this.fetchAnalyses();
    this.startAnalysesAutoRefresh();
    this.startLiveSync();
    this.startDayLevelsSync();
    this.startSparklineSync();
    this.startNarrativeInsightRotation();
    this.prefetchVideoDurationsForToken(this.selectedToken);
    document.addEventListener('pointerdown', this.handleDocumentClick, true);
    window.addEventListener('keydown', this.handleGlobalKeydown);
    window.addEventListener('resize', this.syncLibraryShellHeight);
    this.setupLibraryShellObserver();
    this.applyNotificationRouteIntent();
    this.$nextTick(() => {
      this.syncLibraryShellHeight();
    });
  },
  beforeUnmount() {
    if (this.intervalRetryTimer) {
      clearTimeout(this.intervalRetryTimer);
    }
    this.stopAnalysesAutoRefresh();
    this.stopLiveSync();
    this.stopDayLevelsSync();
    this.stopSparklineSync();
    this.stopNarrativeInsightRotation();
    document.removeEventListener('pointerdown', this.handleDocumentClick, true);
    window.removeEventListener('keydown', this.handleGlobalKeydown);
    window.removeEventListener('resize', this.syncLibraryShellHeight);
    if (this.pricePanelResizeObserver) {
      this.pricePanelResizeObserver.disconnect();
      this.pricePanelResizeObserver = null;
    }
    document.body.style.overflow = '';
  },
  watch: {
    selectedToken() {
      this.isTokenMenuOpen = false;
      this.isHistoryOpen = false;
      this.currentInsightText = '';
      this.livePrice = null;
      this.prefetchVideoDurationsForToken(this.selectedToken);
      this.startLiveSync();
      this.syncDayLevels();
      this.startSparklineSync();
      this.refreshDayChange();
      this.startNarrativeInsightRotation();
      this.$nextTick(() => {
        this.syncLibraryShellHeight();
      });
    },
    activeAnalise() {
      this.refreshDayChange();
      this.$nextTick(() => {
        this.syncLibraryShellHeight();
      });
    },
    selectedRange() {
      this.currentInsightText = '';
      this.startSparklineSync();
      this.startNarrativeInsightRotation();
      this.$nextTick(() => {
        this.syncLibraryShellHeight();
      });
    },
    comboKey() {
      this.currentInsightText = '';
      this.startNarrativeInsightRotation();
    },
    '$route.query': {
      deep: true,
      handler() {
        this.applyNotificationRouteIntent();
      }
    },
  },
  methods: {
    setupLibraryShellObserver() {
      if (typeof ResizeObserver === 'undefined') return;
      const pricePanel = this.$refs.pricePanel;
      if (!pricePanel) return;
      this.pricePanelResizeObserver = new ResizeObserver(() => {
        this.syncLibraryShellHeight();
      });
      this.pricePanelResizeObserver.observe(pricePanel);
    },
    syncLibraryShellHeight() {
      if (typeof window !== 'undefined' && window.innerWidth <= 1024) {
        this.libraryShellHeight = null;
        return;
      }
      const pricePanel = this.$refs.pricePanel;
      if (!pricePanel) {
        this.libraryShellHeight = null;
        return;
      }
      this.libraryShellHeight = Math.ceil(pricePanel.getBoundingClientRect().height);
    },
    applyNotificationRouteIntent() {
      const query = this.$route?.query || {};
      if (String(query.openVideo || '') !== '1') return;

      const requestedToken = String(query.token || '').toUpperCase();
      const hasToken = this.tokens.some((item) => item.key === requestedToken);
      const token = hasToken ? requestedToken : this.selectedToken;
      if (token !== this.selectedToken) {
        this.selectedToken = token;
      }

      this.$nextTick(() => {
        const requestedAnalysisId = String(query.analysisId || '').trim();
        if (requestedAnalysisId) {
          const item = (this.analysisLibrary[token] || []).find((entry) => entry.id === requestedAnalysisId);
          if (item) {
            this.openAnalysisVideoModalForItem(item);
            return;
          }
        }
        this.openAnalysisVideoModal();
      });
    },
    handleGlobalKeydown(event) {
      if (event.key === 'Escape' && this.isAnalysisVideoModalOpen) {
        this.closeAnalysisVideoModal();
        return;
      }
    },
    syncOverlayScrollLock() {
      document.body.style.overflow = this.isAnalysisVideoModalOpen ? 'hidden' : '';
    },
    loadVideoDurationCache() {
      if (typeof window === 'undefined') return;
      try {
        const raw = window.localStorage.getItem('zima-analysis-video-durations');
        const parsed = raw ? JSON.parse(raw) : {};
        this.videoDurationById = parsed && typeof parsed === 'object' ? parsed : {};
      } catch (error) {
        console.warn('Falha ao carregar cache de durações dos vídeos:', error);
        this.videoDurationById = {};
      }
    },
    saveVideoDurationCache() {
      if (typeof window === 'undefined') return;
      try {
        window.localStorage.setItem(
          'zima-analysis-video-durations',
          JSON.stringify(this.videoDurationById)
        );
      } catch (error) {
        console.warn('Falha ao salvar cache de durações dos vídeos:', error);
      }
    },
    resolveVimeoVideoId(rawUrl) {
      const value = String(rawUrl || '').trim();
      if (!value) return '';
      if (/^\d+$/.test(value)) return value;

      const match = value.match(/vimeo\.com\/(?:video\/)?(\d+)/i)
        || value.match(/player\.vimeo\.com\/video\/(\d+)/i);
      return match?.[1] || '';
    },
    formatVideoDurationLabel(seconds) {
      const total = Number(seconds);
      if (!Number.isFinite(total) || total <= 0) return '--:--';

      const rounded = Math.round(total);
      const hours = Math.floor(rounded / 3600);
      const minutes = Math.floor((rounded % 3600) / 60);
      const secs = rounded % 60;

      if (hours > 0) {
        return `${hours}:${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
      }

      return `${String(minutes).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
    },
    getAnalysisDurationLabel(item) {
      const id = String(item?.id || '').trim();
      const cached = id ? this.videoDurationById[id] : null;
      if (cached) return cached;
      if (item?.duration) return item.duration;
      return '--:--';
    },
    async fetchVimeoDurationLabel(item) {
      const id = String(item?.id || '').trim();
      const vimeoId = this.resolveVimeoVideoId(item?.videoUrl);
      if (!id || !vimeoId) return '';
      if (this.videoDurationById[id]) return this.videoDurationById[id];
      if (this.videoDurationRequests[id]) return this.videoDurationRequests[id];

      const request = (async () => {
        try {
          const endpoint = `https://vimeo.com/api/oembed.json?url=${encodeURIComponent(`https://vimeo.com/${vimeoId}`)}`;
          const response = await fetch(endpoint);
          if (!response.ok) throw new Error(`HTTP ${response.status}`);
          const payload = await response.json();
          const label = this.formatVideoDurationLabel(payload?.duration);
          if (label && label !== '--:--') {
            this.videoDurationById = {
              ...this.videoDurationById,
              [id]: label
            };
            this.saveVideoDurationCache();
            return label;
          }
        } catch (error) {
          console.warn(`Falha ao buscar duração do vídeo ${id}:`, error);
        } finally {
          const next = { ...this.videoDurationRequests };
          delete next[id];
          this.videoDurationRequests = next;
        }

        return '';
      })();

      this.videoDurationRequests = {
        ...this.videoDurationRequests,
        [id]: request
      };

      return request;
    },
    prefetchVideoDurationsForToken(tokenKey) {
      const items = this.analysisLibrary[tokenKey] || [];
      items.forEach((item) => {
        this.fetchVimeoDurationLabel(item);
      });
    },
    resolveAnalysisVideoEmbedUrl(rawUrl) {
      const value = String(rawUrl || '').trim();
      if (!value) return '';

      // Vimeo ID puro: 123456789
      if (/^\d+$/.test(value)) {
        return `https://player.vimeo.com/video/${value}`;
      }

      // Vimeo URLs comuns (vimeo.com/123 ou player.vimeo.com/video/123)
      const vimeoMatch = value.match(/vimeo\.com\/(?:video\/)?(\d+)/i);
      if (vimeoMatch && vimeoMatch[1]) {
        return `https://player.vimeo.com/video/${vimeoMatch[1]}`;
      }

      // YouTube normal e curto
      const ytMatch =
        value.match(/[?&]v=([a-zA-Z0-9_-]{11})/) ||
        value.match(/youtu\.be\/([a-zA-Z0-9_-]{11})/) ||
        value.match(/youtube\.com\/embed\/([a-zA-Z0-9_-]{11})/);
      if (ytMatch && ytMatch[1]) {
        return `https://www.youtube.com/embed/${ytMatch[1]}`;
      }

      // Já embedável
      if (/^https?:\/\//i.test(value)) {
        return value;
      }

      return '';
    },
    openAnalysisVideoModalForItem(item) {
      if (!item) return;
      this.activeModalAnalysisId = item.id || null;
      const videoUrl = item.videoUrl || '';
      this.analysisVideoEmbedUrl = this.resolveAnalysisVideoEmbedUrl(videoUrl);
      this.isAnalysisVideoModalOpen = true;
      this.syncOverlayScrollLock();
    },
    openAnalysisVideoModal() {
      this.activeModalAnalysisId = this.selectedAnalysis?.id || null;
      const videoUrl = this.selectedAnalysis?.videoUrl || '';
      this.analysisVideoEmbedUrl = this.resolveAnalysisVideoEmbedUrl(videoUrl);
      this.isAnalysisVideoModalOpen = true;
      this.syncOverlayScrollLock();
    },
    openSharedChart() {
      const url = String(this.activeSharedChartUrl || '').trim();
      if (!url || typeof window === 'undefined') return;
      window.open(url, '_blank', 'noopener,noreferrer');
    },
    closeAnalysisVideoModal() {
      this.isAnalysisVideoModalOpen = false;
      this.analysisVideoEmbedUrl = '';
      this.activeModalAnalysisId = null;
      this.syncOverlayScrollLock();
    },
    handleDocumentClick(event) {
      if (this.isTokenMenuOpen) {
        const tokenMenu = this.$refs.tokenMenu;
        const tokenToggle = this.$refs.tokenToggle;
        const keepTokenOpen =
          tokenMenu?.contains(event.target) || tokenToggle?.contains(event.target);
        if (!keepTokenOpen) {
          this.isTokenMenuOpen = false;
        }
      }

      if (this.isHistoryOpen) {
        const menu = this.$refs.historyMenu;
        const toggle = this.$refs.historyToggle;
        const keepHistoryOpen = menu?.contains(event.target) || toggle?.contains(event.target);
        if (!keepHistoryOpen) {
          this.isHistoryOpen = false;
        }
      }
    },
    toggleTokenMenu() {
      this.isTokenMenuOpen = !this.isTokenMenuOpen;
      if (this.isTokenMenuOpen) {
        this.isHistoryOpen = false;
      }
    },
    selectTokenFromMenu(token) {
      this.selectToken(token);
      this.isTokenMenuOpen = false;
    },
    selectToken(token) {
      this.selectedToken = token;
      this.userSelected = true;
    },
    setRange(range) {
      this.selectedRange = range;
    },
    toggleHistoryMenu() {
      this.isHistoryOpen = !this.isHistoryOpen;
    },
    selectPinnedAnalysis() {
      this.isHistoryOpen = false;
    },
    getInsightContextKey() {
      return `${this.selectedToken}|${this.comboKey}|${this.activeTimeframe}`;
    },
    shuffleInsights(items) {
      const copy = [...items];
      for (let i = copy.length - 1; i > 0; i -= 1) {
        const j = Math.floor(Math.random() * (i + 1));
        [copy[i], copy[j]] = [copy[j], copy[i]];
      }
      return copy;
    },
    getNextInsightInCycle(contextKey, candidates) {
      const existing = this.insightCycleByContext[contextKey];
      const sameSet =
        existing &&
        existing.order.length === candidates.length &&
        existing.order.every((item) => candidates.includes(item));

      let order = sameSet ? existing.order : this.shuffleInsights(candidates);
      let index = sameSet ? existing.index : 0;

      if (index >= order.length) {
        const lastShown = order[order.length - 1];
        order = this.shuffleInsights(candidates);
        if (order.length > 1 && order[0] === lastShown) {
          [order[0], order[1]] = [order[1], order[0]];
        }
        index = 0;
      }

      const nextInsight = order[index];
      this.insightCycleByContext = {
        ...this.insightCycleByContext,
        [contextKey]: {
          order,
          index: index + 1
        }
      };
      return nextInsight;
    },
    rotateNarrativeInsight() {
      const candidates = this.insightCandidates;

      if (!candidates.length) {
        this.currentInsightText = this.limitInsight(FALLBACK_TREND_INSIGHT);
        return;
      }

      const contextKey = this.getInsightContextKey();
      this.currentInsightText = this.getNextInsightInCycle(contextKey, candidates);
    },
    startNarrativeInsightRotation() {
      this.stopNarrativeInsightRotation();
      this.rotateNarrativeInsight();

      if (this.insightCandidates.length <= 1) {
        return;
      }

      this.narrativeInsightTimer = setInterval(() => {
        this.rotateNarrativeInsight();
      }, 5200);
    },
    stopNarrativeInsightRotation() {
      if (this.narrativeInsightTimer) {
        clearInterval(this.narrativeInsightTimer);
        this.narrativeInsightTimer = null;
      }
    },
    startAnalysesAutoRefresh() {
      this.stopAnalysesAutoRefresh();
      this.analysesRefreshTimer = setInterval(() => {
        this.fetchAnalyses({ silent: true });
      }, 15000);
    },
    stopAnalysesAutoRefresh() {
      if (this.analysesRefreshTimer) {
        clearInterval(this.analysesRefreshTimer);
        this.analysesRefreshTimer = null;
      }
    },
    async fetchAnalyses(options = {}) {
      const { silent = false } = options;
      if (this.fetchAnalysesInFlight) return;

      if (!silent) {
        this.loading = true;
        this.error = null;
      }
      this.fetchAnalysesInFlight = true;

      try {
        const response = await fetch(this.apiUrl);
        const data = await response.json();

        if (data.success) {
          this.analises = data.data;
          this.error = null;
          if (!this.selectedToken && this.analises.length) {
            this.selectedToken = this.getTokenKeyFromPair(this.analises[0].par) || 'BTC';
          } else if (!this.activeAnalise && !this.userSelected && this.analises.length) {
            this.selectedToken = this.getTokenKeyFromPair(this.analises[0].par) || 'BTC';
          }
          this.ensureChartSymbol();
          this.refreshDayChange();
        } else {
          throw new Error('Erro ao buscar analises');
        }
      } catch (err) {
        if (!silent || !this.analises.length) {
          this.error = 'Nao foi possivel conectar ao servidor. Verifique se o backend esta rodando.';
        }
        console.error('Erro:', err);
      } finally {
        this.fetchAnalysesInFlight = false;
        if (!silent) {
          this.loading = false;
        }
      }
    },
    startDayLevelsSync() {
      this.stopDayLevelsSync();
      this.syncDayLevels();
      this.dayLevelsTimer = setInterval(() => {
        this.syncDayLevels();
      }, 60000);
    },
    stopDayLevelsSync() {
      if (this.dayLevelsTimer) {
        clearInterval(this.dayLevelsTimer);
        this.dayLevelsTimer = null;
      }
    },
    startSparklineSync() {
      this.stopSparklineSync();
      this.sparklineSeries = [];
      this.syncSparkline();
      this.sparklineSyncTimer = setInterval(() => {
        this.syncSparkline();
      }, this.sparklineRangeMeta.refreshMs);
    },
    stopSparklineSync() {
      if (this.sparklineSyncTimer) {
        clearInterval(this.sparklineSyncTimer);
        this.sparklineSyncTimer = null;
      }
    },
    async syncSparkline() {
      const symbol = this.toBybitSymbol(this.selectedTokenPair);
      if (!symbol) {
        this.sparklineSeries = [];
        return;
      }

      const requestKey = `${symbol}:${this.selectedRange}`;
      this.sparklineLoading = true;
      try {
        const { interval, limit } = this.sparklineRangeMeta;
        const url = `https://api.bybit.com/v5/market/kline?category=linear&symbol=${symbol}&interval=${interval}&limit=${limit}`;
        const response = await fetch(url);
        const data = await response.json();
        const list = Array.isArray(data?.result?.list) ? data.result.list : [];
        const ordered = [...list]
          .map((item) => ({
            time: Number(item?.[0]),
            close: Number(item?.[4])
          }))
          .filter((item) => Number.isFinite(item.time) && Number.isFinite(item.close))
          .sort((a, b) => a.time - b.time);

        if (!ordered.length) {
          this.sparklineSeries = [];
          return;
        }

        const live = this.toNumber(this.livePrice);
        if (Number.isFinite(live)) {
          ordered[ordered.length - 1] = {
            ...ordered[ordered.length - 1],
            close: live
          };
        }

        if (requestKey !== `${this.toBybitSymbol(this.selectedTokenPair)}:${this.selectedRange}`) {
          return;
        }

        this.sparklineSeries = ordered;
      } catch (error) {
        console.warn('Falha ao sincronizar mini gráfico:', error);
        if (requestKey === `${this.toBybitSymbol(this.selectedTokenPair)}:${this.selectedRange}`) {
          this.sparklineSeries = [];
        }
      } finally {
        if (requestKey === `${this.toBybitSymbol(this.selectedTokenPair)}:${this.selectedRange}`) {
          this.sparklineLoading = false;
          this.$nextTick(() => {
            this.syncLibraryShellHeight();
          });
        }
      }
    },
    formatLevelValue(value) {
      const numeric = this.toNumber(value);
      if (numeric === null || numeric <= 0) return '--';
      return `$${this.formatPrice(numeric)}`;
    },
    async syncDayLevels() {
      const symbol = this.toBybitSymbol(this.selectedTokenPair);
      if (!symbol) {
        this.dayLevels = { open: null, high: null, low: null };
        return;
      }
      try {
        const url = `https://api.bybit.com/v5/market/kline?category=linear&symbol=${symbol}&interval=D&limit=2`;
        const response = await fetch(url);
        const data = await response.json();
        const list = Array.isArray(data?.result?.list) ? data.result.list : [];
        if (!list.length) return;
        const dayStart = this.getUtcDayStartMs();
        const ordered = [...list]
          .map((item) => ({
            start: Number(item?.[0]),
            open: Number(item?.[1]),
            high: Number(item?.[2]),
            low: Number(item?.[3])
          }))
          .filter(
            (c) =>
              Number.isFinite(c.start) &&
              Number.isFinite(c.open) &&
              Number.isFinite(c.high) &&
              Number.isFinite(c.low)
          )
          .sort((a, b) => b.start - a.start);
        if (!ordered.length) return;

        const currentDayCandle =
          ordered.find((c) => c.start >= dayStart) || ordered[0];

        this.dayLevels = {
          open: currentDayCandle.open,
          high: currentDayCandle.high,
          low: currentDayCandle.low
        };
      } catch (err) {
        console.warn('Falha ao sincronizar niveis do dia:', err);
      }
    },
    normalizePair(pair) {
      return (pair || '').replace(/\s+/g, '').toUpperCase();
    },
    toBybitSymbol(pair) {
      return this.normalizePair(pair).replace('/', '').replace('.P', '');
    },
    startLiveSync() {
      this.stopLiveSync();
      this.syncLiveTicker();
      this.liveSyncTimer = setInterval(() => {
        this.syncLiveTicker();
      }, 1000);
    },
    stopLiveSync() {
      if (this.liveSyncTimer) {
        clearInterval(this.liveSyncTimer);
        this.liveSyncTimer = null;
      }
    },
    async syncLiveTicker() {
      const symbol = this.toBybitSymbol(this.selectedTokenPair);
      if (!symbol) return;
      try {
        const url = `https://api.bybit.com/v5/market/tickers?category=linear&symbol=${symbol}`;
        const response = await fetch(url);
        const data = await response.json();
        const item = Array.isArray(data?.result?.list) ? data.result.list[0] : null;
        const nextPrice = this.toNumber(item?.lastPrice);
        if (nextPrice === null) return;
        this.livePrice = nextPrice;
        if (this.sparklineSeries.length) {
          const nextSeries = this.sparklineSeries.slice();
          nextSeries[nextSeries.length - 1] = {
            ...nextSeries[nextSeries.length - 1],
            close: nextPrice
          };
          this.sparklineSeries = nextSeries;
        }
        this.refreshDayChange(nextPrice);
      } catch (err) {
        console.warn('Falha ao sincronizar ticker ao vivo:', err);
      }
    },
    getUtcDayStartMs() {
      const now = new Date();
      return Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate(), 0, 0, 0, 0);
    },
    async fetchLastDailyCloseFromBybit(symbol) {
      const dayKey = String(this.getUtcDayStartMs());
      const cacheKey = `${symbol}:${dayKey}`;
      const cached = this.dailyCloseCache[cacheKey];
      if (Number.isFinite(cached)) return cached;
      const url = `https://api.bybit.com/v5/market/kline?category=linear&symbol=${symbol}&interval=D&limit=10`;
      const response = await fetch(url);
      const data = await response.json();
      const list = Array.isArray(data?.result?.list) ? data.result.list : [];
      if (!list.length) return null;
      const dayStart = this.getUtcDayStartMs();
      const ordered = [...list]
        .map((item) => ({
          start: Number(item?.[0]),
          close: Number(item?.[4])
        }))
        .filter((c) => Number.isFinite(c.start) && Number.isFinite(c.close))
        .sort((a, b) => b.start - a.start);
      const closed = ordered.find((c) => c.start < dayStart);
      const result = closed ? closed.close : ordered.length > 1 ? ordered[1].close : ordered[0].close;
      this.dailyCloseCache = {
        ...this.dailyCloseCache,
        [cacheKey]: result
      };
      return result;
    },
    getDayOpenCandidate(analysis) {
      if (!analysis) return null;
      const candidates = [
        analysis.day_open_9am_brt,
        analysis.open_9am_brt,
        analysis.day_open,
        analysis.daily_open,
        analysis.open_day,
        analysis.abertura_dia
      ];
      for (const value of candidates) {
        const n = Number(value);
        if (Number.isFinite(n) && n > 0) return n;
      }
      return null;
    },
    async refreshDayChange(currentOverride) {
      const current = this.toNumber(currentOverride ?? this.currentPrice);
      if (!Number.isFinite(current) || current <= 0) {
        this.dayChange = { delta: null, pct: null, open: null };
        return;
      }

      let open = this.getDayOpenCandidate(this.activeAnalise);
      if (!Number.isFinite(open) || open <= 0) {
        try {
          open = await this.fetchLastDailyCloseFromBybit(this.toBybitSymbol(this.selectedTokenPair));
        } catch (err) {
          console.warn('Falha ao buscar fechamento diário na Bybit:', err);
        }
      }

      if (!Number.isFinite(open) || open <= 0) {
        this.dayChange = { delta: null, pct: null, open: null };
        return;
      }

      const delta = current - open;
      const pct = (delta / open) * 100;
      this.dayChange = { delta, pct, open };
    },
    getTokenKeyFromPair(pair) {
      const normalized = this.normalizePair(pair);
      if (normalized.includes('BTC')) return 'BTC';
      if (normalized.includes('ETH')) return 'ETH';
      if (normalized.includes('SOL')) return 'SOL';
      if (normalized.includes('XRP') || normalized.includes('RIPPLE')) return 'XRP';
      return null;
    },
    getPairForToken(tokenKey) {
      const token = this.tokens.find((t) => t.key === tokenKey);
      return token?.pair || '';
    },
    normalizeSymbol(pair) {
      const symbol = this.normalizePair(pair).replace('/', '');
      return symbol ? `BYBIT:${symbol}` : '';
    },
    injectTradingView() {
      if (window.TradingView) return;
      const script = document.createElement('script');
      script.src = 'https://s3.tradingview.com/tv.js';
      script.async = true;
      script.onload = () => this.ensureChartSymbol();
      document.body.appendChild(script);
    },
    ensureChartSymbol() {
      const symbol = this.normalizeSymbol(this.selectedTokenPair);
      if (symbol && symbol !== this.chartSymbol) {
        this.initTVWidget(symbol, {
          interval: this.getIntervalForRange(this.selectedRange),
          force: true
        });
      }
    },
    getIntervalForRange(range) {
      const intervals = {
        shortTerm: '15',
        midTerm: '240',
        longTerm: 'D'
      };
      return intervals[range] || '60';
    },
    mapIntervalToRange(interval) {
      const normalized = String(interval).toUpperCase();
      if (normalized === '15') return 'shortTerm';
      if (normalized === '240') return 'midTerm';
      if (normalized === 'D' || normalized === '1D') return 'longTerm';
      return null;
    },
    initTVWidget(symbol, { interval, force } = {}) {
      if (!window.TradingView || !this.$refs.tvContainer) return;
      const nextInterval = interval || this.getIntervalForRange(this.selectedRange);
      if (!force && symbol === this.chartSymbol && nextInterval === this.chartInterval) {
        return;
      }
      this.$refs.tvContainer.innerHTML = '';
      this.chartApi = null;
      this.tvWidget = new window.TradingView.widget({
        autosize: true,
        symbol,
        interval: nextInterval,
        timezone: 'Etc/UTC',
        theme: 'dark',
        style: '1',
        locale: 'br',
        allow_symbol_change: false,
        hide_top_toolbar: false,
        hide_side_toolbar: false,
        toolbar_bg: 'rgba(14,20,30,0.85)',
        studies: ['Ichimoku Cloud'],
        container_id: 'tv-chart'
      });
      this.chartSymbol = symbol;
      this.chartInterval = nextInterval;
      if (this.tvWidget?.onChartReady) {
        this.tvWidget.onChartReady(() => {
          this.chartApi = this.getChartApi();
          if (this.chartApi?.onIntervalChanged) {
            this.chartApi.onIntervalChanged().subscribe(null, (interval) => {
              this.selectedRange = this.mapIntervalToRange(interval);
            });
          }
        });
      }
    },
    getChartApi() {
      if (!this.tvWidget) return null;
      if (this.tvWidget.activeChart) return this.tvWidget.activeChart();
      if (this.tvWidget.chart) return this.tvWidget.chart();
      return null;
    },
    formatPrice(price) {
      const fractionDigits = this.getTokenPriceFractionDigits();
      const numeric = this.toNumber(price);
      const safeValue = numeric === null ? 0 : numeric;
      return safeValue.toLocaleString('en-US', {
        minimumFractionDigits: fractionDigits,
        maximumFractionDigits: fractionDigits
      });
    },
    getTokenPriceFractionDigits(tokenKey = this.selectedToken) {
      const normalized = String(tokenKey || '').trim().toUpperCase();
      if (normalized === 'XRP') return 4;
      return 2;
    },
    getTrendIcon(tendencia) {
      const icons = {
        BULL: 'fas fa-arrow-up',
        BEAR: 'fas fa-arrow-down',
        NEUTRAL: 'fas fa-minus'
      };
      return icons[tendencia] || icons.NEUTRAL;
    },
    getTrendText(tendencia) {
      const texts = {
        BULL: 'Alta',
        BEAR: 'Baixa',
        NEUTRAL: 'Neutro'
      };
      return texts[tendencia] || texts.NEUTRAL;
    },
    getTrendScore(state) {
      if (state === 'BULL') return 1;
      if (state === 'BEAR') return -1;
      return 0;
    },
    getOverallState() {
      const weighted =
        this.getTrendScore(this.shortTerm) * 1 +
        this.getTrendScore(this.midTerm) * 2 +
        this.getTrendScore(this.longTerm) * 3;
      if (weighted >= 2) return 'BULL';
      if (weighted <= -2) return 'BEAR';
      return 'NEUTRAL';
    },
    getTrendCssKey(state) {
      if (state === 'BULL') return 'alta';
      if (state === 'BEAR') return 'baixa';
      return 'neutra';
    },
    normalizeTrendState(value) {
      const raw = String(value || '').trim().toUpperCase();
      if (!raw) return null;
      if (raw === 'BULL' || raw === 'ALTA' || raw === 'UP') return 'BULL';
      if (raw === 'BEAR' || raw === 'BAIXA' || raw === 'DOWN') return 'BEAR';
      if (raw === 'NEUTRAL' || raw === 'NEUTRA' || raw === 'RANGE') return 'NEUTRAL';
      return null;
    },
    toNumber(value) {
      if (value === null || value === undefined || value === '') return null;
      const numeric = Number(value);
      return Number.isFinite(numeric) ? numeric : null;
    },
    getFirstNumeric(source, keys) {
      for (const key of keys) {
        const value = this.toNumber(source?.[key]);
        if (value !== null) return value;
      }
      return null;
    },
    inferTrendFromCloud(price, cloudA, cloudB) {
      const valid = [price, cloudA, cloudB].every((n) => typeof n === 'number' && Number.isFinite(n));
      if (!valid) return null;
      const top = Math.max(cloudA, cloudB);
      const bottom = Math.min(cloudA, cloudB);
      if (price > top) return 'BULL';
      if (price < bottom) return 'BEAR';
      return 'NEUTRAL';
    },
    resolveTrendState(range) {
      const analysis = this.activeAnalise || {};
      const alias = {
        shortTerm: ['short', 'cp'],
        midTerm: ['mid', 'mp'],
        longTerm: ['long', 'lp']
      };
      const names = alias[range] || [];
      const trendCandidates = [
        `trend_${names[0]}`,
        `trend_${names[1]}`,
        `${names[0]}Trend`,
        `${names[0]}_trend`,
        range
      ].filter(Boolean);

      const rangePrice = this.getFirstNumeric(analysis, [
        `close_${names[0]}`,
        `preco_${names[0]}`,
        `price_${names[0]}`
      ]);
      const currentPrice = this.toNumber(this.livePrice) ?? this.toNumber(analysis.preco_atual);
      const price = currentPrice ?? rangePrice;
      const cloudA = this.getFirstNumeric(analysis, [
        `cloud_a_${names[0]}`,
        `senkou_a_${names[0]}`,
        `nuvem_a_${names[0]}`
      ]);
      const cloudB = this.getFirstNumeric(analysis, [
        `cloud_b_${names[0]}`,
        `senkou_b_${names[0]}`,
        `nuvem_b_${names[0]}`
      ]);
      const inferred = this.inferTrendFromCloud(price, cloudA, cloudB);
      if (inferred) return inferred;

      for (const key of trendCandidates) {
        const normalized = this.normalizeTrendState(analysis[key]);
        if (normalized) return normalized;
      }

      return 'NEUTRAL';
    },
    limitInsight(text, max = 180) {
      const clean = String(text || '').replace(/\s+/g, ' ').trim();
      if (clean.length <= max) return clean;
      const cutoff = clean.slice(0, max + 1);
      const safe = cutoff.lastIndexOf(' ');
      return `${cutoff.slice(0, safe > 120 ? safe : max).trim()}...`;
    },
    formatCardBodyText(text) {
      const clean = String(text || '')
        .replace(/\s+/g, ' ')
        .replace(/\.\.\.+/g, '.')
        .trim();
      if (!clean) return '';
      const withoutTrailingDot = clean.replace(/\.\s*$/, '').trim();
      return withoutTrailingDot.replace(/\.\s+/g, ' / ');
    },
    describeTrendNarrative(trend, rangeKey, rangeLabel) {
      if (rangeKey === 'shortTerm') {
        if (trend === 'BULL') return {
          headline: 'Alta no curto prazo',
          text: 'Viés comprador no scalp. Correções rápidas são ponto de recarga, não sinal de reversão.',
          trend
        };
        if (trend === 'BEAR') return {
          headline: 'Baixa no curto prazo',
          text: 'Viés vendedor no scalp. Tentativas de retomada tendem a virar resistência enquanto a estrutura não mudar.',
          trend
        };
        return {
          headline: 'Lateral no curto prazo',
          text: 'Sem direção clara no scalp. Opera as bordas do range ou aguarda rompimento com fechamento confirmado.',
          trend
        };
      }

      if (rangeKey === 'midTerm') {
        if (trend === 'BULL') return {
          headline: 'Alta no médio prazo',
          text: 'Swing favorece longs. Pullbacks dentro da tendência são oportunidade — não saída.',
          trend
        };
        if (trend === 'BEAR') return {
          headline: 'Baixa no médio prazo',
          text: 'Swing favorece shorts. Repiques são armadilhas até que a estrutura de médio prazo se reconfigure.',
          trend
        };
        return {
          headline: 'Lateral no médio prazo',
          text: 'Swing sem tendência definida. O lado que romper primeiro com volume e fechamento é o próximo movimento.',
          trend
        };
      }

      if (rangeKey === 'longTerm') {
        if (trend === 'BULL') return {
          headline: 'Alta no longo prazo',
          text: 'Regime de alta estrutural. Esse é o filtro que valida qualquer long — enquanto ele se mantiver, a tendência é aliada.',
          trend
        };
        if (trend === 'BEAR') return {
          headline: 'Baixa no longo prazo',
          text: 'Regime de baixa estrutural. Longs nos prazos menores estão contra a corrente dominante até que a estrutura mude.',
          trend
        };
        return {
          headline: 'Lateral no longo prazo',
          text: 'Mercado em equilíbrio macro. O próximo rompimento com fechamento vai definir o regime por semanas.',
          trend
        };
      }

      const dir = { BULL: 'alta', BEAR: 'baixa', NEUTRAL: 'lateral' };
      return {
        headline: `${dir[trend] || 'indefinido'} — ${(rangeLabel || '').toLowerCase()}`,
        text: 'Sem tendência definida neste prazo.',
        trend
      };
    },
    toggleFullscreen() {
      const el = this.$refs.tvContainer?.parentElement;
      if (!el) return;
      if (document.fullscreenElement) {
        document.exitFullscreen?.();
        return;
      }
      el.requestFullscreen?.();
    }
  }
};
</script>

<style scoped>
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@400;500;600;700;800&family=Manrope:wght@500;600;700;800&family=Space+Grotesk:wght@500;600;700&display=swap');

:host {
  display: block;
}

.analysis-shell {
  --bg: #11131a;
  --panel: rgba(17, 20, 32, 0.92);
  --panel-2: rgba(19, 24, 39, 0.88);
  --border: rgba(115, 148, 240, 0.25);
  --accent: #4e73df;
  --accent-2: #9b59b6;
  --accent-3: #9ad9ff;
  --text: #f3f6ff;
  --muted: rgba(226, 232, 255, 0.75);
  --muted-2: rgba(226, 232, 255, 0.6);

  position: relative;
  min-height: 100vh;
  padding: calc(var(--app-header-h) + 10px) var(--app-gutter) 24px;
  color: var(--text);
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
  overflow: hidden;
  background: radial-gradient(circle at top left, rgba(78, 115, 223, 0.12), transparent 45%),
    radial-gradient(circle at top right, rgba(155, 89, 182, 0.14), transparent 40%),
    var(--bg);
}

.bg-grid,
.bg-glow {
  position: fixed;
  inset: 0;
  pointer-events: none;
  z-index: 0;
}

.bg-grid {
  background:
    linear-gradient(rgba(255, 255, 255, 0.04) 1px, transparent 1px),
    linear-gradient(90deg, rgba(255, 255, 255, 0.04) 1px, transparent 1px);
  background-size: 32px 32px;
  opacity: 0.4;
}

.bg-glow {
  background: transparent;
}

.page-header {
  position: relative;
  z-index: 1;
  display: flex;
  justify-content: space-between;
  align-items: center;
  max-width: 1180px;
  margin: 0 auto 24px;
  padding: 4px 4px 0;
  background: transparent;
  border: none;
  box-shadow: none;
}

.title-wrap {
  display: flex;
  gap: 12px;
  align-items: center;
  padding: 10px 16px;
  border-radius: 14px;
  background: rgba(17, 20, 32, 0.6);
  border: 1px solid rgba(115, 148, 240, 0.25);
  box-shadow: 0 10px 26px rgba(12, 18, 32, 0.45);
}

.title-wrap i {
  font-size: 26px;
  color: var(--accent-3);
  filter: drop-shadow(0 0 8px rgba(154, 217, 255, 0.35));
}

h1 {
  margin: 0;
  font-size: 1.6rem;
  color: var(--text);
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.status-badge {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  padding: 10px 14px;
  border-radius: 999px;
  font-weight: 600;
  background: rgba(20, 26, 38, 0.65);
  border: 1px solid var(--border);
  color: var(--muted);
}

.status-badge.connected .dot {
  background: #5ce6a7;
  box-shadow: 0 0 12px rgba(92, 230, 167, 0.65);
}

.status-badge.disconnected .dot {
  background: #ef4444;
  box-shadow: 0 0 12px rgba(239, 68, 68, 0.65);
}

.dot {
  width: 10px;
  height: 10px;
  border-radius: 50%;
  display: inline-block;
}

.token-switch {
  position: relative;
  z-index: 1;
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  margin-bottom: 16px;
}

.filter-group {
  display: inline-flex;
  gap: 10px;
  padding: 5px;
  border-radius: 999px;
  background: rgba(20, 26, 38, 0.6);
  border: 1px solid rgba(115, 148, 240, 0.25);
}

.filter-button {
  min-width: 120px;
  padding: 9px 16px;
  border-radius: 999px;
  border: none;
  background: transparent;
  color: rgba(226, 232, 255, 0.75);
  font-weight: 600;
  text-transform: uppercase;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
  cursor: pointer;
  transition: all 0.2s ease;
}

.filter-button:hover,
.filter-button.active {
  background: linear-gradient(120deg, rgba(78, 115, 223, 0.9), rgba(155, 89, 182, 0.9));
  color: #fff;
  box-shadow: 0 0 14px rgba(115, 148, 240, 0.4);
}

.last-sync {
  margin-left: auto;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: var(--muted);
  font-size: 0.9rem;
  padding: 8px 12px;
  border-radius: 10px;
  background: rgba(20, 26, 38, 0.75);
  border: 1px solid var(--border);
}

.analysis-card {
  position: relative;
  z-index: 1;
  background: linear-gradient(155deg, var(--panel), var(--panel-2));
  border: 1px solid rgba(115, 148, 240, 0.32);
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.45),
    inset 0 0 0 1px rgba(78, 115, 223, 0.18),
    inset 0 0 0 2px rgba(155, 89, 182, 0.08);
  border-radius: 22px;
  padding: 20px 20px 18px;
  transition: border-color 0.2s ease;
  max-width: 1180px;
  margin: 0 auto;
}

.analysis-card.trend-alta,
.analysis-card.trend-baixa,
.analysis-card.trend-neutra {
  border-color: rgba(115, 148, 240, 0.32);
}

.card-top {
  display: flex;
  justify-content: space-between;
  align-items: stretch;
  gap: 12px;
  margin-bottom: 12px;
}

.pair-row {
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
  min-width: 0;
}

.general-trend-card {
  flex: 1 1 260px;
  min-width: 0;
  position: relative;
  overflow: hidden;
  border: 1px solid var(--token-border-strong);
  border-radius: 10px;
  padding: 10px;
  background: rgba(10, 16, 29, 0.84);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 0 0 1px rgba(10, 15, 28, 0.35) inset;
  display: grid;
  gap: 6px;
  transition: border-color 0.2s ease, box-shadow 0.2s ease, background 0.2s ease;
}

.general-trend-card::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  box-shadow: 0 0 16px rgba(108, 145, 255, 0.16) inset;
  opacity: 0.45;
}

.general-trend-card.trend-alta {
  background: rgba(31, 223, 156, 0.14);
  border-color: rgba(16, 185, 129, 0.7);
}

.general-trend-card.trend-alta::after {
  box-shadow: 0 0 16px rgba(16, 185, 129, 0.18) inset;
}

.general-trend-card.trend-baixa {
  background: rgba(239, 68, 68, 0.14);
  border-color: rgba(239, 68, 68, 0.7);
}

.general-trend-card.trend-baixa::after {
  box-shadow: 0 0 16px rgba(239, 68, 68, 0.16) inset;
}

.general-trend-card.trend-neutra {
  border-color: rgba(250, 204, 21, 0.68) !important;
  background: linear-gradient(150deg, rgba(34, 27, 10, 0.86), rgba(12, 20, 35, 0.9));
}

.general-trend-card.trend-neutra::after {
  box-shadow: 0 0 16px rgba(148, 163, 184, 0.16) inset;
}

.general-title {
  margin: 0;
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  line-height: 1.2;
  max-width: 100%;
}

.general-text {
  margin: 0;
  color: #dbe5ff;
  font-size: 0.74rem;
  line-height: 1.42;
}

.general-trend-card.trend-alta .general-title {
  color: #1fdf9c;
}

.general-trend-card.trend-baixa .general-title {
  color: #ef4444;
}

.general-trend-card.trend-neutra .general-title {
  color: #facc15;
}

.asset-box,
.trend-box {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  background: rgba(17, 20, 32, 0.9);
  border: 1px solid var(--border);
  padding: 10px 14px;
  border-radius: 14px;
  box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.03);
}

.asset-box {
  min-height: 48px;
  padding: 12px 18px;
}

.asset-box {
  --asset-kicker-a: rgba(95, 136, 255, 0.26);
  --asset-kicker-b: rgba(157, 97, 216, 0.22);
  --asset-border-gradient: linear-gradient(122deg, rgba(95, 136, 255, 0.9), rgba(157, 97, 216, 0.9));
  --asset-sheen-a: rgba(95, 136, 255, 0.22);
  --asset-sheen-b: rgba(157, 97, 216, 0.16);
  background:
    linear-gradient(120deg, rgba(8, 13, 25, 0.92), rgba(15, 24, 42, 0.9)) padding-box,
    linear-gradient(120deg, var(--asset-kicker-a), var(--asset-kicker-b)) padding-box,
    var(--asset-border-gradient) border-box;
  background-blend-mode: normal, soft-light, normal;
  border: 1px solid transparent;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 0 0 1px rgba(10, 15, 28, 0.26),
    0 8px 16px rgba(7, 12, 23, 0.22);
}

.asset-box.asset-btc {
  --asset-kicker-a: rgba(255, 209, 102, 0.34);
  --asset-kicker-b: rgba(249, 115, 22, 0.28);
  --asset-border-gradient: linear-gradient(122deg, rgba(255, 209, 102, 0.95), rgba(249, 115, 22, 0.92));
  --asset-sheen-a: rgba(255, 209, 102, 0.28);
  --asset-sheen-b: rgba(249, 115, 22, 0.22);
}

.asset-box.asset-eth {
  --asset-kicker-a: rgba(111, 165, 255, 0.34);
  --asset-kicker-b: rgba(153, 197, 255, 0.28);
  --asset-border-gradient: linear-gradient(122deg, rgba(132, 186, 255, 0.95), rgba(77, 134, 255, 0.94));
  --asset-sheen-a: rgba(132, 186, 255, 0.26);
  --asset-sheen-b: rgba(77, 134, 255, 0.18);
}

.asset-box.asset-sol {
  --asset-kicker-a: rgba(167, 139, 250, 0.32);
  --asset-kicker-b: rgba(52, 152, 219, 0.28);
  --asset-border-gradient: linear-gradient(122deg, rgba(167, 139, 250, 0.94), rgba(52, 152, 219, 0.95));
  --asset-sheen-a: rgba(167, 139, 250, 0.24);
  --asset-sheen-b: rgba(52, 152, 219, 0.2);
}

.asset-box.asset-xrp {
  --asset-kicker-a: rgba(255, 255, 255, 0.3);
  --asset-kicker-b: rgba(0, 0, 0, 0.28);
  --asset-border-gradient: linear-gradient(122deg, rgba(255, 255, 255, 0.92), rgba(0, 0, 0, 0.96));
  --asset-sheen-a: rgba(255, 255, 255, 0.24);
  --asset-sheen-b: rgba(163, 163, 163, 0.2);
}

.trend-box {
  gap: 8px;
  flex-wrap: wrap;
}

.pair-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 22px;
  height: 22px;
}

.pair-icon i {
  font-size: 18px;
}

.pair-icon .fa-bitcoin {
  color: #f7b733;
}

.pair-icon .fa-ethereum {
  color: #94a3b8;
}

.xrp-symbol {
  width: 18px;
  height: 18px;
  display: block;
  object-fit: contain;
}

.solana-icon {
  width: 18px;
  height: 18px;
  display: block;
}

.pair {
  font-weight: 800;
  font-size: 1.08rem;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.trend-group {
  display: none;
}

.trend-pill {
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  padding: 8px 13px;
  min-height: 40px;
  border-radius: 10px;
  font-size: 0.78rem;
  font-weight: 800;
  border: 1px solid rgba(115, 148, 240, 0.22);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  background: rgba(17, 20, 32, 0.85);
  color: var(--muted);
  cursor: pointer;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    border-color 0.2s ease,
    background 0.2s ease;
}

.trend-pill .pill-copy {
  display: inline-flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 1px;
  min-width: 0;
}

.trend-pill .pill-label {
  opacity: 0.7;
  white-space: nowrap;
  line-height: 1.05;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.trend-pill .pill-timeframe {
  display: inline-flex;
  align-items: center;
  padding: 0;
  border: none;
  background: transparent;
  font-size: 0.52rem;
  font-weight: 900;
  letter-spacing: 0.08em;
  line-height: 1;
  opacity: 0.58;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.trend-pill .pill-value {
  color: var(--text);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 16px;
  font-size: 0.8rem;
  transition:
    transform 0.2s ease,
    opacity 0.2s ease;
}

.pill-alta {
  background: rgba(31, 223, 156, 0.14);
  color: #1fdf9c;
  border-color: rgba(16, 185, 129, 0.7);
}

.pill-alta .pill-value {
  color: #1fdf9c;
}

.pill-baixa {
  background: rgba(239, 68, 68, 0.14);
  color: #ef4444;
  border-color: rgba(239, 68, 68, 0.7);
}

.pill-baixa .pill-value {
  color: #ef4444;
}

.pill-neutra {
  background: rgba(148, 163, 184, 0.18);
  color: #cbd5f5;
  border-color: rgba(148, 163, 184, 0.7);
}

.pill-neutra .pill-value {
  color: #cbd5f5;
}

.trend-pill.is-active {
  transform: translateY(-1px);
  box-shadow: 0 0 16px currentColor;
  border-color: currentColor;
}

.trend-pill.is-active .pill-label,
.trend-pill:hover .pill-label {
  opacity: 0.94;
  transform: translateY(-0.5px);
}

.trend-pill.is-active .pill-timeframe,
.trend-pill:hover .pill-timeframe {
  opacity: 0.84;
  transform: translateY(0.5px);
}

.trend-pill.is-active .pill-value,
.trend-pill:hover .pill-value {
  transform: translateX(1px);
}

.trend-pill:hover {
  transform: translateY(-2px);
  box-shadow: 0 8px 18px rgba(10, 15, 30, 0.35);
}

.pill-alta:hover {
  border-color: rgba(52, 211, 153, 0.95);
  background: rgba(31, 223, 156, 0.22);
  box-shadow: 0 0 16px #1fdf9c;
}

.pill-baixa:hover {
  border-color: rgba(248, 113, 113, 0.95);
  background: rgba(239, 68, 68, 0.22);
  box-shadow: 0 0 16px #ef4444;
}

.pill-neutra:hover {
  border-color: rgba(148, 163, 184, 0.95);
  background: rgba(148, 163, 184, 0.24);
  box-shadow: 0 0 16px #cbd5f5;
}

.refresh {
  display: none;
}

.content-grid {
  display: grid;
  grid-template-columns: 360px 1fr;
  gap: 10px;
  align-items: stretch;
}

.price-panel {
  --token-core: rgba(78, 115, 223, 0.22);
  --token-edge: rgba(155, 89, 182, 0.18);
  --token-sheen: rgba(78, 115, 223, 0.18);
  --token-border: rgba(115, 148, 240, 0.3);
  --token-border-strong: rgba(115, 148, 240, 0.48);
  --token-line: rgba(115, 148, 240, 0.18);
  --token-kicker-a: rgba(95, 136, 255, 0.42);
  --token-kicker-b: rgba(157, 97, 216, 0.34);
  --token-glow: rgba(108, 145, 255, 0.52);
  --token-scroll-a: rgba(78, 115, 223, 0.8);
  --token-scroll-b: rgba(155, 89, 182, 0.72);
  --target-x: 81%;
  --target-y: 22%;
  --panel-bg:
    linear-gradient(164deg, rgba(9, 14, 28, 0.985), rgba(6, 10, 22, 0.97));
  --panel-overlay-a:
    radial-gradient(
      85% 72% at var(--target-x) var(--target-y),
      rgba(255, 255, 255, 0.006) 0%,
      transparent 65%
    );
  --panel-overlay-b:
    repeating-radial-gradient(
      circle at var(--target-x) var(--target-y),
      rgba(255, 255, 255, 0.024) 0,
      rgba(255, 255, 255, 0.024) 1px,
      transparent 1px,
      transparent 17px
    ),
    repeating-radial-gradient(
      circle at var(--target-x) var(--target-y),
      rgba(255, 255, 255, 0.012) 0,
      rgba(255, 255, 255, 0.012) 1px,
      transparent 1px,
      transparent 26px
    ),
    conic-gradient(
      from 235deg at var(--target-x) var(--target-y),
      transparent 0deg 52deg,
      rgba(255, 255, 255, 0.018) 68deg,
      transparent 108deg 360deg
    );
  --panel-overlay-c:
    repeating-linear-gradient(
      9deg,
      transparent 0,
      transparent 15px,
      rgba(255, 255, 255, 0.009) 15px,
      rgba(255, 255, 255, 0.009) 16px
    ),
    radial-gradient(
      130% 95% at 50% 114%,
      rgba(0, 0, 0, 0.64) 0%,
      rgba(0, 0, 0, 0.22) 34%,
      transparent 66%
    ),
    linear-gradient(180deg, rgba(0, 0, 0, 0.08), transparent 22%, rgba(0, 0, 0, 0.36));
  --panel-overlay-opacity: 0.78;

  position: relative;
  overflow: visible;
  background: var(--panel-bg);
  border: 1px solid var(--token-border);
  border-radius: 16px;
  padding: 14px;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    inset 0 -30px 45px rgba(4, 9, 20, 0.45),
    0 16px 28px rgba(3, 7, 16, 0.4);
  align-self: start;
  height: fit-content;
}

.price-panel::before {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: inherit;
  pointer-events: none;
  background: var(--panel-overlay-a), var(--panel-overlay-b);
}

.price-panel::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: inherit;
  pointer-events: none;
  background: var(--panel-overlay-c);
  opacity: var(--panel-overlay-opacity);
}

.price-panel > * {
  position: relative;
  z-index: 1;
}

.price-panel.theme-btc,
.analysis-library-shell.theme-btc {
  --token-core: rgba(255, 209, 102, 0.22);
  --token-edge: rgba(255, 209, 102, 0.16);
  --token-sheen: rgba(255, 209, 102, 0.12);
  --token-border: rgba(255, 209, 102, 0.36);
  --token-border-strong: rgba(255, 209, 102, 0.52);
  --token-line: rgba(255, 209, 102, 0.16);
  --token-kicker-a: rgba(255, 209, 102, 0.42);
  --token-kicker-b: rgba(255, 209, 102, 0.32);
  --token-glow: rgba(255, 209, 102, 0.48);
  --token-scroll-a: rgba(255, 209, 102, 0.68);
  --token-scroll-b: rgba(255, 209, 102, 0.56);
  --token-border-gradient: linear-gradient(122deg, rgba(255, 209, 102, 0.95), rgba(249, 115, 22, 0.9));
  --token-border-gradient-soft: linear-gradient(122deg, rgba(255, 209, 102, 0.72), rgba(249, 115, 22, 0.68));
}

.price-panel.theme-eth,
.analysis-library-shell.theme-eth {
  --token-core: rgba(111, 165, 255, 0.24);
  --token-edge: rgba(76, 129, 255, 0.2);
  --token-sheen: rgba(164, 210, 255, 0.14);
  --token-border: rgba(111, 165, 255, 0.4);
  --token-border-strong: rgba(94, 156, 255, 0.56);
  --token-line: rgba(121, 181, 255, 0.18);
  --token-kicker-a: rgba(111, 165, 255, 0.46);
  --token-kicker-b: rgba(153, 197, 255, 0.34);
  --token-glow: rgba(104, 173, 255, 0.54);
  --token-scroll-a: rgba(126, 182, 255, 0.74);
  --token-scroll-b: rgba(88, 149, 255, 0.66);
  --token-border-gradient: linear-gradient(122deg, rgba(132, 186, 255, 0.96), rgba(77, 134, 255, 0.94));
  --token-border-gradient-soft: linear-gradient(122deg, rgba(132, 186, 255, 0.74), rgba(77, 134, 255, 0.72));
}

.price-panel.theme-sol,
.analysis-library-shell.theme-sol {
  --token-core: rgba(167, 139, 250, 0.2);
  --token-edge: rgba(52, 152, 219, 0.2);
  --token-sheen: rgba(167, 139, 250, 0.12);
  --token-border: rgba(167, 139, 250, 0.34);
  --token-border-strong: rgba(52, 152, 219, 0.5);
  --token-line: rgba(52, 152, 219, 0.16);
  --token-kicker-a: rgba(167, 139, 250, 0.4);
  --token-kicker-b: rgba(52, 152, 219, 0.34);
  --token-glow: rgba(133, 145, 235, 0.48);
  --token-scroll-a: rgba(167, 139, 250, 0.66);
  --token-scroll-b: rgba(52, 152, 219, 0.62);
  --token-border-gradient: linear-gradient(122deg, rgba(167, 139, 250, 0.95), rgba(52, 152, 219, 0.95));
  --token-border-gradient-soft: linear-gradient(122deg, rgba(167, 139, 250, 0.7), rgba(52, 152, 219, 0.75));
}

.price-panel.theme-xrp,
.analysis-library-shell.theme-xrp {
  --token-core: rgba(255, 255, 255, 0.19);
  --token-edge: rgba(124, 124, 124, 0.2);
  --token-sheen: rgba(255, 255, 255, 0.11);
  --token-border: rgba(255, 255, 255, 0.34);
  --token-border-strong: rgba(255, 255, 255, 0.5);
  --token-line: rgba(255, 255, 255, 0.14);
  --token-kicker-a: rgba(255, 255, 255, 0.36);
  --token-kicker-b: rgba(120, 120, 120, 0.3);
  --token-glow: rgba(255, 255, 255, 0.42);
  --token-scroll-a: rgba(255, 255, 255, 0.68);
  --token-scroll-b: rgba(124, 124, 124, 0.58);
  --token-border-gradient: linear-gradient(122deg, rgba(255, 255, 255, 0.92), rgba(0, 0, 0, 0.95));
  --token-border-gradient-soft: linear-gradient(122deg, rgba(255, 255, 255, 0.7), rgba(28, 28, 28, 0.78));
}

.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) {
  border: 1px solid transparent;
  background:
    var(--panel-bg) padding-box,
    var(--token-border-gradient) border-box;
}

.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .analysis-spotlight,
.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .spotlight-mini-chart,
.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .quick-narrative,
.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .narrative-item,
.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .spotlight-kicker-link,
.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .history-toggle,
.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .spotlight-action,
.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .history-menu,
.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .history-item,
.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .narrative-insight {
  border: 1px solid transparent !important;
  border-image: none !important;
}

.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .analysis-spotlight {
  background:
    linear-gradient(152deg, rgba(20, 28, 45, 0.98), rgba(10, 15, 29, 0.96)) padding-box,
    var(--token-border-gradient) border-box;
}

.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .spotlight-mini-chart {
  background:
    linear-gradient(160deg, rgba(10, 15, 28, 0.96), rgba(7, 11, 22, 0.98)) padding-box,
    linear-gradient(135deg, color-mix(in srgb, var(--token-border-strong) 34%, transparent), rgba(255, 255, 255, 0.04)) border-box;
}

.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .quick-narrative {
  background:
    linear-gradient(150deg, rgba(14, 20, 36, 0.95), rgba(9, 14, 27, 0.95)) padding-box,
    var(--token-border-gradient) border-box;
}

.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .narrative-item {
  background:
    rgba(10, 16, 29, 0.84) padding-box,
    var(--token-border-gradient) border-box;
}

.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .spotlight-kicker-link {
  background:
    linear-gradient(120deg, rgba(10, 16, 31, 0.9), rgba(19, 30, 53, 0.9)) padding-box,
    var(--token-border-gradient) border-box;
}

.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .history-toggle {
  background:
    linear-gradient(120deg, rgba(8, 13, 25, 0.92), rgba(15, 24, 42, 0.9)) padding-box,
    var(--token-border-gradient) border-box;
}

.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .spotlight-action {
  background:
    linear-gradient(105deg, rgba(7, 12, 23, 0.96), rgba(13, 21, 38, 0.94)) padding-box,
    var(--token-border-gradient) border-box;
}

.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .history-menu {
  background:
    linear-gradient(170deg, rgba(10, 14, 27, 0.98), rgba(11, 16, 30, 0.98)) padding-box,
    var(--token-border-gradient) border-box;
}

.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .history-item {
  background:
    rgba(14, 21, 37, 0.9) padding-box,
    var(--token-border-gradient) border-box;
}

.price-panel:is(.theme-btc, .theme-eth, .theme-sol, .theme-xrp) .narrative-insight {
  background:
    linear-gradient(145deg, rgba(6, 17, 35, 0.9), rgba(10, 28, 52, 0.86)) padding-box,
    linear-gradient(122deg, var(--token-kicker-a), var(--token-kicker-b)) padding-box,
    var(--token-border-gradient) border-box;
  background-blend-mode: normal, screen, normal;
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--token-glow) 45%, transparent) inset,
    0 0 18px color-mix(in srgb, var(--token-glow) 55%, transparent);
}

.price-panel.theme-sol .narrative-item.trend-alta {
  border-color: rgba(16, 185, 129, 0.72) !important;
  background: linear-gradient(150deg, rgba(8, 28, 28, 0.88), rgba(8, 20, 31, 0.88));
}

.price-panel.theme-sol .narrative-item.trend-alta::after {
  box-shadow: 0 0 18px rgba(16, 185, 129, 0.22) inset;
}

.price-panel.theme-sol .narrative-item.trend-alta .range {
  color: #34d399;
}

.price-panel.theme-sol .narrative-item.trend-baixa {
  border-color: rgba(239, 68, 68, 0.72) !important;
  background: linear-gradient(150deg, rgba(36, 12, 18, 0.88), rgba(14, 18, 34, 0.9));
}

.price-panel.theme-sol .narrative-item.trend-baixa::after {
  box-shadow: 0 0 18px rgba(239, 68, 68, 0.2) inset;
}

.price-panel.theme-sol .narrative-item.trend-baixa .range {
  color: #f87171;
}

.price-panel.theme-sol .narrative-item.trend-neutra {
  border-color: rgba(250, 204, 21, 0.68) !important;
  background: linear-gradient(150deg, rgba(34, 27, 10, 0.86), rgba(12, 20, 35, 0.9));
}

.price-panel.theme-sol .narrative-item.trend-neutra::after {
  box-shadow: 0 0 18px rgba(250, 204, 21, 0.2) inset;
}

.price-panel.theme-sol .narrative-item.trend-neutra .range {
  color: #facc15;
}

.price-panel.theme-btc .narrative-item.trend-alta {
  border-color: rgba(16, 185, 129, 0.72) !important;
  background: linear-gradient(150deg, rgba(8, 28, 28, 0.88), rgba(18, 22, 31, 0.9));
}

.price-panel.theme-btc .narrative-item.trend-alta::after {
  box-shadow: 0 0 18px rgba(16, 185, 129, 0.22) inset;
}

.price-panel.theme-btc .narrative-item.trend-alta .range {
  color: #34d399;
}

.price-panel.theme-btc .narrative-item.trend-baixa {
  border-color: rgba(239, 68, 68, 0.72) !important;
  background: linear-gradient(150deg, rgba(36, 12, 18, 0.88), rgba(22, 18, 30, 0.9));
}

.price-panel.theme-btc .narrative-item.trend-baixa::after {
  box-shadow: 0 0 18px rgba(239, 68, 68, 0.2) inset;
}

.price-panel.theme-btc .narrative-item.trend-baixa .range {
  color: #f87171;
}

.price-panel.theme-btc .narrative-item.trend-neutra {
  border-color: rgba(250, 204, 21, 0.72) !important;
  background: linear-gradient(150deg, rgba(34, 27, 10, 0.86), rgba(20, 21, 33, 0.9));
}

.price-panel.theme-btc .narrative-item.trend-neutra::after {
  box-shadow: 0 0 18px rgba(250, 204, 21, 0.2) inset;
}

.price-panel.theme-btc .narrative-item.trend-neutra .range {
  color: #ffd166;
}

.price-panel.theme-eth .narrative-item.trend-alta {
  border-color: rgba(16, 185, 129, 0.72) !important;
  background: linear-gradient(150deg, rgba(8, 27, 30, 0.88), rgba(14, 22, 38, 0.9));
}

.price-panel.theme-eth .narrative-item.trend-alta::after {
  box-shadow: 0 0 18px rgba(16, 185, 129, 0.22) inset;
}

.price-panel.theme-eth .narrative-item.trend-alta .range {
  color: #34d399;
}

.price-panel.theme-eth .narrative-item.trend-baixa {
  border-color: rgba(239, 68, 68, 0.72) !important;
  background: linear-gradient(150deg, rgba(36, 12, 18, 0.88), rgba(15, 19, 36, 0.9));
}

.price-panel.theme-eth .narrative-item.trend-baixa::after {
  box-shadow: 0 0 18px rgba(239, 68, 68, 0.2) inset;
}

.price-panel.theme-eth .narrative-item.trend-baixa .range {
  color: #f87171;
}

.price-panel.theme-eth .narrative-item.trend-neutra {
  border-color: rgba(250, 204, 21, 0.68) !important;
  background: linear-gradient(150deg, rgba(35, 33, 18, 0.84), rgba(14, 22, 38, 0.9));
}

.price-panel.theme-eth .narrative-item.trend-neutra::after {
  box-shadow: 0 0 18px rgba(250, 204, 21, 0.18) inset;
}

.price-panel.theme-eth .narrative-item.trend-neutra .range {
  color: #facc15;
}

.price-panel.theme-xrp .narrative-item.trend-alta {
  border-color: rgba(34, 197, 94, 0.72) !important;
  background: linear-gradient(150deg, rgba(8, 25, 18, 0.88), rgba(14, 20, 18, 0.9));
}

.price-panel.theme-xrp .narrative-item.trend-alta::after {
  box-shadow: 0 0 18px rgba(34, 197, 94, 0.2) inset;
}

.price-panel.theme-xrp .narrative-item.trend-alta .range {
  color: #4ade80;
}

.price-panel.theme-xrp .narrative-item.trend-baixa {
  border-color: rgba(239, 68, 68, 0.72) !important;
  background: linear-gradient(150deg, rgba(40, 13, 13, 0.88), rgba(24, 18, 18, 0.9));
}

.price-panel.theme-xrp .narrative-item.trend-baixa::after {
  box-shadow: 0 0 18px rgba(239, 68, 68, 0.2) inset;
}

.price-panel.theme-xrp .narrative-item.trend-baixa .range {
  color: #f87171;
}

.price-panel.theme-xrp .narrative-item.trend-neutra {
  border-color: rgba(250, 204, 21, 0.68) !important;
  background: linear-gradient(150deg, rgba(38, 38, 38, 0.9), rgba(24, 24, 24, 0.92));
}

.price-panel.theme-xrp .narrative-item.trend-neutra::after {
  box-shadow: 0 0 18px rgba(250, 204, 21, 0.18) inset;
}

.price-panel.theme-xrp .narrative-item.trend-neutra .range {
  color: #facc15;
}

.price-value {
  margin: 2px 0 0;
  font-size: clamp(1.6rem, 4vw, 2.05rem);
  letter-spacing: -0.45px;
  font-weight: 800;
  color: #eef3ff;
  line-height: 1.05;
  min-width: 0;
}

.price-line {
  display: grid;
  grid-template-columns: minmax(0, 1fr) 124px;
  align-items: stretch;
  column-gap: 8px;
  margin-bottom: 10px;
}

.price-main {
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  justify-content: center;
  gap: 5px;
  min-width: 0;
  overflow: hidden;
  min-height: 74px;
  padding: 2px 0;
}

.price-value-row {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  max-width: 100%;
  min-width: 0;
}

.price-main-emblem {
  position: relative;
  flex: 0 0 auto;
  width: 40px;
  height: 40px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  opacity: 0.18;
  pointer-events: none;
  background:
    radial-gradient(circle at 50% 50%, color-mix(in srgb, var(--token-core) 38%, transparent) 0%, transparent 72%);
  filter: saturate(0.9);
  animation: price-emblem-breathe 4.8s ease-in-out infinite;
}

.price-main-emblem::before {
  content: '';
  position: absolute;
  inset: 5px;
  border-radius: 11px;
  border: 1px solid color-mix(in srgb, var(--token-border-strong) 28%, transparent);
  background: linear-gradient(145deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0));
}

.price-main-emblem i {
  font-size: 1.7rem;
  line-height: 1;
}

.price-main-emblem .solana-icon,
.price-main-emblem .xrp-symbol {
  width: 24px;
  height: 24px;
}

.price-panel.theme-btc .price-main-emblem {
  color: rgba(255, 209, 102, 0.9);
  filter: sepia(0.14) saturate(1.02);
}

.price-panel.theme-eth .price-main-emblem {
  color: rgba(132, 186, 255, 0.9);
}

.price-panel.theme-sol .price-main-emblem {
  color: rgba(167, 139, 250, 0.9);
}

.price-panel.theme-sol .price-main-emblem .solana-icon {
  filter: saturate(1.04) brightness(1.02);
}

.price-panel.theme-xrp .price-main-emblem {
  color: rgba(255, 255, 255, 0.72);
}

.price-panel.theme-xrp .price-main-emblem .xrp-symbol {
  filter: grayscale(0.04) brightness(1.04);
}

.day-change {
  margin: 0;
  display: inline-flex;
  align-items: center;
  flex: 0 1 auto;
  min-width: 0;
  gap: 8px;
  font-size: 0.83rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  white-space: nowrap;
}

.day-change i {
  font-size: 0.72rem;
}

.day-change-up {
  color: #22c55e;
}

.day-change-down {
  color: #ef4444;
}

.day-change-neutral {
  color: #cbd5f5;
}

.day-change-pct {
  opacity: 0.96;
}

.day-levels {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: stretch;
  gap: 4px;
  width: 124px;
  min-width: 124px;
}

.day-level-chip {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1px;
  padding: 4px 6px;
  border-radius: 10px;
  border: 1px solid transparent;
  background:
    linear-gradient(
      150deg,
      color-mix(in srgb, var(--token-core) 28%, rgba(8, 14, 27, 0.95)),
      color-mix(in srgb, var(--token-edge) 24%, rgba(7, 12, 24, 0.95))
    ) padding-box,
    var(--token-border-gradient-soft) border-box;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.025),
    inset 0 0 0 1px color-mix(in srgb, var(--token-core) 14%, rgba(10, 16, 30, 0.82)),
    0 0 0 1px color-mix(in srgb, var(--token-glow) 10%, transparent),
    0 4px 8px rgba(2, 7, 18, 0.2);
  width: 100%;
  min-width: 0;
}

.day-level-label {
  font-size: 0.48rem;
  font-weight: 800;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  background: var(--token-border-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  white-space: nowrap;
  text-align: center;
}

.day-level-value {
  font-size: 0.62rem;
  font-weight: 800;
  color: #eef3ff;
  text-shadow: 0 0 6px rgba(255, 255, 255, 0.12);
  white-space: nowrap;
  line-height: 1.05;
  text-align: center;
}

.price-panel.theme-xrp .day-level-label,
.price-panel.theme-xrp .day-level-value {
  background: none;
  -webkit-background-clip: initial;
  background-clip: initial;
  -webkit-text-fill-color: #ffffff;
  color: #ffffff;
  text-shadow: none;
}

.price-panel.theme-eth .day-level-label {
  background: none;
  -webkit-background-clip: initial;
  background-clip: initial;
  -webkit-text-fill-color: #7fa7ff;
  color: #7fa7ff;
  text-shadow: 0 0 8px rgba(95, 132, 255, 0.16);
}


.analysis-spotlight {
  position: relative;
  margin-top: 12px;
  padding: 12px;
  border-radius: 15px;
  background:
    linear-gradient(152deg, rgba(20, 28, 45, 0.98), rgba(10, 15, 29, 0.96));
  border: 1px solid var(--token-border);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    inset 0 0 0 1px rgba(13, 19, 35, 0.45),
    0 12px 24px rgba(8, 11, 22, 0.5);
}

.quick-narrative {
  position: relative;
  overflow: visible;
  margin-top: 12px;
  padding: 12px;
  border-radius: 14px;
  border: 1px solid var(--token-border-strong);
  background:
    linear-gradient(150deg, rgba(14, 20, 36, 0.95), rgba(9, 14, 27, 0.95));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 0 0 1px rgba(10, 15, 28, 0.4) inset,
    0 10px 20px rgba(5, 8, 18, 0.36),
    0 0 20px rgba(108, 145, 255, 0.1);
}

.quick-narrative::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  box-shadow:
    0 0 0 1px var(--token-glow) inset,
    0 0 24px rgba(108, 145, 255, 0.18);
  opacity: 0.4;
}

.narrative-list {
  display: grid;
  gap: 0;
}

.narrative-item {
  position: relative;
  overflow: visible;
  border: 1px solid var(--token-border-strong);
  border-radius: 10px;
  padding: 10px;
  background: rgba(10, 16, 29, 0.84);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 0 0 1px rgba(10, 15, 28, 0.35) inset;
}

.narrative-item::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  box-shadow: 0 0 16px rgba(108, 145, 255, 0.16) inset;
  opacity: 0.45;
}

.narrative-item-head {
  margin: 0 0 6px;
  display: flex;
  justify-content: flex-start;
  align-items: center;
  gap: 10px;
}

.narrative-item-head .range {
  font-size: 0.74rem;
  font-weight: 700;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  line-height: 1.2;
  max-width: 80%;
}

.narrative-item-text {
  margin: 0;
  color: #dbe5ff;
  font-size: 0.74rem;
  line-height: 1.42;
}

.insight-hub {
  position: relative;
  margin-left: auto;
  margin-right: 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.insight-toggle {
  position: relative;
  width: 10px;
  height: 10px;
  border: none;
  padding: 0;
  border-radius: 50%;
  background: #facc15;
  cursor: pointer;
  box-shadow: 0 0 8px #facc15;
  transition: transform 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
}

.insight-toggle::after {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100%;
  height: 100%;
  border-radius: 999px;
  transform: translate(-50%, -50%) scale(1);
  background: inherit;
  opacity: 0.6;
  filter: blur(0.5px);
  animation: insight-ping 1.6s ease-out infinite;
}

.insight-toggle.trend-alta {
  background: #4ade80;
  box-shadow: 0 0 8px #4ade80;
}

.insight-toggle.trend-neutra {
  background: #facc15;
  box-shadow: 0 0 8px #facc15;
}

.insight-toggle.trend-baixa {
  background: #ef4444;
  box-shadow: 0 0 8px #ef4444;
}

.insight-toggle:hover,
.insight-toggle.open {
  transform: scale(1.24);
  filter: saturate(1.12);
}

@keyframes insight-ping {
  0% { transform: translate(-50%, -50%) scale(1); opacity: 0.5; }
  60% { transform: translate(-50%, -50%) scale(2.2); opacity: 0; }
  100% { transform: translate(-50%, -50%) scale(2.4); opacity: 0; }
}

.narrative-insight {
  position: absolute;
  top: calc(100% + 10px);
  right: -2px;
  width: 290px;
  max-width: min(290px, calc(100vw - 72px));
  z-index: 30;
  overflow: hidden;
  padding: 10px 11px;
  border-radius: 10px;
  border: 1px solid var(--token-border-strong);
  background:
    linear-gradient(150deg, rgba(15, 18, 35, 0.94), rgba(8, 25, 50, 0.9));
  box-shadow:
    0 0 0 1px rgba(10, 15, 28, 0.35) inset,
    0 0 22px var(--token-glow);
  backdrop-filter: blur(8px) saturate(1.08);
  -webkit-backdrop-filter: blur(8px) saturate(1.08);
}

.narrative-insight::before {
  content: '';
  position: absolute;
  inset: -65% auto auto -30%;
  width: 70%;
  height: 220%;
  background: linear-gradient(
    120deg,
    rgba(255, 255, 255, 0) 0%,
    color-mix(in srgb, var(--token-glow) 16%, transparent) 40%,
    color-mix(in srgb, var(--token-glow) 40%, transparent) 50%,
    rgba(255, 255, 255, 0) 60%
  );
  transform: rotate(18deg);
  animation: insight-shimmer 2.6s ease-in-out infinite;
  pointer-events: none;
}

.insight-label {
  margin: 0 0 4px;
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #f7f9ff;
  background: var(--token-border-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  text-shadow: 0 0 8px color-mix(in srgb, var(--token-glow) 56%, transparent);
}

.insight-text {
  margin: 0;
  color: #d9e7ff;
  font-size: 0.71rem;
  line-height: 1.35;
}

.insight-reveal-enter-active,
.insight-reveal-leave-active {
  transition: opacity 0.24s ease, transform 0.24s ease, filter 0.24s ease;
}

.insight-reveal-enter-from,
.insight-reveal-leave-to {
  opacity: 0;
  transform: translateY(-10px) scale(0.92);
  filter: blur(1px);
}

.insight-reveal-enter-to,
.insight-reveal-leave-from {
  transform: translateY(0) scale(1);
  filter: blur(0);
}

@keyframes insight-shimmer {
  0% { transform: translateX(-6%) rotate(18deg); opacity: 0; }
  35% { opacity: 0.9; }
  100% { transform: translateX(110%) rotate(18deg); opacity: 0; }
}

.narrative-item.trend-alta {
  border-color: rgba(16, 185, 129, 0.62);
  background: linear-gradient(150deg, rgba(8, 28, 28, 0.84), rgba(8, 20, 31, 0.88));
}

.narrative-item.trend-alta::after {
  box-shadow: 0 0 16px rgba(16, 185, 129, 0.18) inset;
}

.narrative-item.trend-alta .range {
  color: #34d399;
}

.narrative-item.trend-baixa {
  border-color: rgba(239, 68, 68, 0.62);
  background: linear-gradient(150deg, rgba(36, 12, 18, 0.84), rgba(14, 18, 34, 0.9));
}

.narrative-item.trend-baixa::after {
  box-shadow: 0 0 16px rgba(239, 68, 68, 0.16) inset;
}

.narrative-item.trend-baixa .range {
  color: #f87171;
}

.narrative-item.trend-neutra {
  border-color: rgba(148, 163, 184, 0.62);
  background: linear-gradient(150deg, rgba(24, 26, 35, 0.88), rgba(12, 20, 35, 0.9));
}

.narrative-item.trend-neutra::after {
  box-shadow: 0 0 16px rgba(148, 163, 184, 0.16) inset;
}

.narrative-item.trend-neutra .range {
  color: #cbd5f5;
}

.analysis-spotlight::after {
  content: '';
  position: absolute;
  inset: 0;
  z-index: 0;
  border-radius: inherit;
  pointer-events: none;
  background: linear-gradient(180deg, var(--token-sheen), transparent 38%);
}

.analysis-spotlight > * {
  position: relative;
  z-index: 1;
}

.spotlight-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 10px;
}

.spotlight-kicker-link {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  padding: 7px 11px;
  border-radius: 999px;
  border: 1px solid var(--token-border-strong);
  background:
    linear-gradient(120deg, rgba(10, 16, 31, 0.9), rgba(19, 30, 53, 0.9)),
    linear-gradient(120deg, var(--token-kicker-a), var(--token-kicker-b));
  background-blend-mode: normal, screen;
  color: #edf2ff;
  font-size: 0.64rem;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  font-weight: 800;
  box-shadow:
    0 0 0 1px rgba(10, 15, 28, 0.34) inset,
    0 8px 15px rgba(8, 12, 24, 0.28);
  text-decoration: none;
  transition: transform 0.2s ease, border-color 0.2s ease, box-shadow 0.2s ease, filter 0.2s ease;
}

.spotlight-kicker-link i {
  font-size: 0.62rem;
  opacity: 0.9;
  background: var(--token-border-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.spotlight-kicker-link:hover {
  transform: translateY(-1px);
  border-color: var(--token-border-strong);
  filter: saturate(1.12);
  box-shadow:
    0 11px 18px rgba(7, 12, 23, 0.4),
    0 0 0 1px var(--token-glow),
    0 0 0 1px rgba(10, 15, 28, 0.34) inset;
}

.history-toggle {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  border-radius: 10px;
  border: 1px solid var(--token-border-strong);
  background:
    linear-gradient(120deg, rgba(8, 13, 25, 0.92), rgba(15, 24, 42, 0.9)),
    linear-gradient(120deg, var(--token-kicker-a), var(--token-kicker-b));
  background-blend-mode: normal, soft-light;
  color: var(--text);
  font-size: 0.66rem;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  font-weight: 800;
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    box-shadow 0.2s ease,
    transform 0.2s ease,
    background 0.2s ease;
}

.history-toggle:hover {
  transform: translateY(-1px);
  border-color: var(--token-border-strong);
  filter: saturate(1.12);
  box-shadow:
    0 10px 18px rgba(7, 12, 23, 0.44),
    0 0 0 1px var(--token-glow);
}

.history-toggle i {
  font-size: 0.7rem;
  background: var(--token-border-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  transition: transform 0.2s ease;
}

.history-toggle.open {
  border-color: var(--token-border-strong);
  box-shadow:
    0 10px 20px rgba(7, 12, 23, 0.44),
    0 0 0 1px var(--token-glow);
}

.history-toggle.open i {
  transform: rotate(180deg);
}

.spotlight-title,
.spotlight-title span,
.spotlight-meta,
.meta-date,
.meta-time,
.history-item-title,
.history-item-meta,
.analysis-video-title,
.analysis-video-kicker {
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif !important;
}

.spotlight-title {
  margin: 0;
  display: flex;
  align-items: flex-start;
  gap: 8px;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
  color: #f2f6ff;
  font-size: 0.95rem;
  line-height: 1.35;
  font-weight: 800;
}

.spotlight-title i {
  margin-top: 2px;
  font-size: 0.78rem;
  background: var(--token-border-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  filter: drop-shadow(0 0 8px var(--token-glow));
}

.spotlight-meta i {
  background: var(--token-border-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.spotlight-title span {
  background: linear-gradient(180deg, #ffffff 0%, #d9e7ff 100%);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.spotlight-meta {
  margin: 8px 0 12px;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', sans-serif;
  color: var(--muted);
  font-size: 0.75rem;
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.meta-date,
.meta-time {
  font-weight: 600;
  color: rgba(226, 232, 255, 0.86);
}

.meta-sep {
  opacity: 0.5;
  font-size: 0.65rem;
}

.spotlight-action {
  appearance: none;
  cursor: pointer;
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 10px;
  width: 100%;
  padding: 11px 15px;
  border-radius: 12px;
  border: 1px solid var(--token-border-strong);
  background:
    linear-gradient(105deg, rgba(7, 12, 23, 0.96), rgba(13, 21, 38, 0.94)),
    linear-gradient(120deg, var(--token-kicker-a), var(--token-kicker-b));
  background-blend-mode: normal, soft-light;
  color: #f7f9ff;
  text-transform: uppercase;
  letter-spacing: 0.07em;
  font-weight: 800;
  font-size: 0.73rem;
  text-decoration: none;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.1),
    0 8px 14px rgba(8, 12, 24, 0.28),
    0 0 0 1px rgba(10, 15, 28, 0.32) inset;
  transition:
    transform 0.22s ease,
    box-shadow 0.22s ease,
    border-color 0.22s ease,
    filter 0.22s ease,
    background 0.22s ease;
}

.spotlight-action::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(90deg, rgba(255, 255, 255, 0) 0%, color-mix(in srgb, var(--token-glow) 22%, transparent) 48%, rgba(255, 255, 255, 0) 100%);
  opacity: 0;
  transform: translateX(-55%);
  transition:
    opacity 0.24s ease,
    transform 0.34s ease;
}

.spotlight-action:focus-visible {
  outline: 2px solid rgba(143, 181, 255, 0.85);
  outline-offset: 2px;
}

.spotlight-action:hover {
  transform: translateY(-2px);
  filter: none;
  border-color: var(--token-border-strong);
  background:
    linear-gradient(105deg, rgba(9, 14, 25, 0.96), rgba(17, 24, 40, 0.94)) padding-box,
    linear-gradient(122deg, color-mix(in srgb, var(--token-core) 34%, transparent), color-mix(in srgb, var(--token-edge) 28%, transparent)) padding-box,
    var(--token-border-gradient) border-box;
  background-blend-mode: normal, screen, normal;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.16),
    0 12px 22px rgba(10, 15, 30, 0.38),
    0 0 0 1px color-mix(in srgb, var(--token-glow) 22%, transparent) inset,
    0 0 24px color-mix(in srgb, var(--token-glow) 18%, transparent);
}

.spotlight-action:hover::before {
  opacity: 1;
  transform: translateX(55%);
}

.spotlight-action i {
  font-size: 0.72rem;
  opacity: 0.98;
  filter: none;
  background: var(--token-border-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.history-menu {
  position: absolute;
  left: 0;
  right: 0;
  top: auto;
  bottom: calc(100% + 10px);
  z-index: 12;
  display: grid;
  gap: 8px;
  padding: 10px;
  border-radius: 12px;
  background:
    linear-gradient(170deg, rgba(10, 14, 27, 0.98), rgba(11, 16, 30, 0.98));
  border: 1px solid var(--token-border-strong);
  box-shadow:
    0 18px 36px rgba(3, 6, 16, 0.68),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
  max-height: 218px;
  overflow-y: auto;
  transform-origin: bottom center;
}

.history-menu::-webkit-scrollbar {
  width: 8px;
}

.history-menu::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.04);
  border-radius: 8px;
}

.history-menu::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, var(--token-scroll-a), var(--token-scroll-b));
  border-radius: 8px;
}

.history-item {
  position: relative;
  overflow: hidden;
  display: grid;
  gap: 4px;
  width: 100%;
  border: 1px solid var(--token-border);
  background: rgba(14, 21, 37, 0.9);
  border-radius: 10px;
  padding: 10px;
  text-align: left;
  color: var(--text);
  cursor: pointer;
  transition:
    border-color 0.2s ease,
    transform 0.2s ease,
    background 0.2s ease,
    box-shadow 0.2s ease;
}

.history-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, var(--token-scroll-a), var(--token-scroll-b));
  opacity: 0.26;
  transition: opacity 0.2s ease;
}

.history-item:hover {
  transform: translateY(-1px) scale(1.01);
  border-color: var(--token-border-strong);
  background: rgba(24, 34, 58, 0.96);
  box-shadow: 0 9px 16px rgba(8, 14, 28, 0.45);
}

.history-item:hover::before {
  opacity: 0.75;
}

.history-item.active {
  border-color: var(--token-border-strong);
  background:
    linear-gradient(130deg, rgba(10, 16, 31, 0.9), rgba(15, 24, 42, 0.92)),
    linear-gradient(120deg, var(--token-kicker-a), var(--token-kicker-b));
  background-blend-mode: normal, screen;
  box-shadow:
    0 0 0 1px var(--token-glow) inset,
    0 9px 16px rgba(7, 14, 28, 0.42);
}

.history-item.active::before {
  opacity: 1;
}

.price-panel.theme-sol .history-item.active {
  background:
    linear-gradient(130deg, rgba(10, 16, 31, 0.9), rgba(15, 24, 42, 0.92)) padding-box,
    linear-gradient(122deg, rgba(167, 139, 250, 0.72), rgba(52, 152, 219, 0.72)) padding-box,
    var(--token-border-gradient) border-box;
  background-blend-mode: normal, screen, normal;
  box-shadow:
    0 0 0 1px rgba(133, 145, 235, 0.42) inset,
    0 10px 18px rgba(7, 14, 28, 0.48);
}

.price-panel.theme-btc .history-item:hover {
  border-color: rgba(255, 190, 92, 0.92);
  background:
    linear-gradient(130deg, rgba(16, 18, 22, 0.94), rgba(22, 20, 15, 0.94)) padding-box,
    linear-gradient(122deg, rgba(255, 209, 102, 0.62), rgba(249, 115, 22, 0.6)) padding-box,
    var(--token-border-gradient) border-box;
  background-blend-mode: normal, screen, normal;
  box-shadow:
    0 0 0 1px rgba(255, 209, 102, 0.34) inset,
    0 11px 18px rgba(13, 11, 6, 0.42);
}

.price-panel.theme-btc .history-item:hover::before {
  opacity: 0.95;
}

.price-panel.theme-btc .history-item.active {
  border-color: rgba(255, 209, 102, 0.96);
  background:
    linear-gradient(130deg, rgba(16, 18, 22, 0.96), rgba(24, 20, 14, 0.96)) padding-box,
    linear-gradient(122deg, rgba(255, 209, 102, 0.78), rgba(249, 115, 22, 0.74)) padding-box,
    var(--token-border-gradient) border-box;
  background-blend-mode: normal, screen, normal;
  box-shadow:
    0 0 0 1px rgba(255, 209, 102, 0.46) inset,
    0 12px 20px rgba(15, 11, 6, 0.48);
}

.price-panel.theme-eth .history-item:hover {
  border-color: rgba(132, 186, 255, 0.94);
  background:
    linear-gradient(130deg, rgba(12, 18, 34, 0.95), rgba(20, 24, 34, 0.95)) padding-box,
    linear-gradient(122deg, rgba(132, 186, 255, 0.42), rgba(77, 134, 255, 0.46)) padding-box,
    var(--token-border-gradient) border-box;
  background-blend-mode: normal, screen, normal;
  box-shadow:
    0 0 0 1px rgba(111, 165, 255, 0.34) inset,
    0 11px 18px rgba(8, 12, 22, 0.45);
}

.price-panel.theme-eth .history-item:hover::before {
  opacity: 0.95;
}

.price-panel.theme-eth .history-item.active {
  border-color: rgba(146, 198, 255, 0.96);
  background:
    linear-gradient(130deg, rgba(12, 18, 34, 0.96), rgba(21, 24, 33, 0.96)) padding-box,
    linear-gradient(122deg, rgba(132, 186, 255, 0.58), rgba(77, 134, 255, 0.62)) padding-box,
    var(--token-border-gradient) border-box;
  background-blend-mode: normal, screen, normal;
  box-shadow:
    0 0 0 1px rgba(111, 165, 255, 0.42) inset,
    0 12px 20px rgba(8, 12, 22, 0.5);
}

.price-panel.theme-xrp .history-item:hover {
  border-color: rgba(255, 255, 255, 0.92);
  background:
    linear-gradient(130deg, rgba(20, 20, 20, 0.95), rgba(10, 10, 10, 0.95)) padding-box,
    linear-gradient(122deg, rgba(255, 255, 255, 0.62), rgba(45, 45, 45, 0.66)) padding-box,
    var(--token-border-gradient) border-box;
  background-blend-mode: normal, screen, normal;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.3) inset,
    0 11px 18px rgba(0, 0, 0, 0.45);
}

.price-panel.theme-xrp .history-item:hover::before {
  opacity: 0.95;
}

.price-panel.theme-xrp .history-item.active {
  border-color: rgba(255, 255, 255, 0.96);
  background:
    linear-gradient(130deg, rgba(22, 22, 22, 0.96), rgba(8, 8, 8, 0.96)) padding-box,
    linear-gradient(122deg, rgba(255, 255, 255, 0.72), rgba(50, 50, 50, 0.76)) padding-box,
    var(--token-border-gradient) border-box;
  background-blend-mode: normal, screen, normal;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.4) inset,
    0 12px 20px rgba(0, 0, 0, 0.5);
}

.history-item-title {
  font-size: 0.77rem;
  font-weight: 700;
  line-height: 1.3;
}

.history-item-meta {
  font-size: 0.69rem;
  color: var(--muted);
}

.menu-fade-enter-active,
.menu-fade-leave-active {
  transition: opacity 0.16s ease, transform 0.16s ease;
}

.menu-fade-enter-from,
.menu-fade-leave-to {
  opacity: 0;
  transform: translateY(6px);
}

.chart-panel {
  position: relative;
  background: rgba(17, 23, 35, 0.85);
  border: 1px solid rgba(115, 148, 240, 0.18);
  border-radius: 16px;
  padding: 6px;
  min-height: 430px;
  overflow: hidden;
}

.tv-container {
  width: 100%;
  height: 100%;
  min-height: 410px;
}

.fullscreen-btn {
  position: absolute;
  top: 10px;
  right: 42px;
  z-index: 5;
  width: 32px;
  height: 32px;
  border-radius: 9px;
  border: 1px solid rgba(115, 148, 240, 0.35);
  background: rgba(12, 16, 26, 0.85);
  color: var(--text);
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
}

.fullscreen-btn {
  box-shadow:
    0 10px 18px rgba(8, 12, 22, 0.55),
    0 0 0 1px rgba(12, 16, 26, 0.6) inset;
}

.fullscreen-btn:hover {
  transform: translateY(-1px);
  border-color: rgba(115, 148, 240, 0.7);
  box-shadow: 0 8px 18px rgba(10, 15, 30, 0.35);
}

.fullscreen-btn i {
  font-size: 0.9rem;
}

/* Recovery overrides (estado avançado antes do revert) */
.token-switch,
.filter-group,
.filter-button {
  display: none !important;
}

.analysis-card {
  --analysis-border-gradient: linear-gradient(122deg, rgba(95, 136, 255, 0.92), rgba(157, 97, 216, 0.86));
  --menu-border-gradient: linear-gradient(122deg, rgba(95, 136, 255, 0.92), rgba(157, 97, 216, 0.86));
  --menu-surface: linear-gradient(160deg, rgba(9, 14, 28, 0.98), rgba(11, 17, 33, 0.98));
  background:
    linear-gradient(155deg, var(--panel), var(--panel-2)) padding-box,
    var(--analysis-border-gradient) border-box;
  border: 1px solid transparent;
}

.analysis-card.token-btc {
  --analysis-border-gradient: linear-gradient(122deg, rgba(255, 209, 102, 0.95), rgba(249, 115, 22, 0.92));
  --menu-border-gradient: linear-gradient(122deg, rgba(255, 209, 102, 0.95), rgba(249, 115, 22, 0.92));
  box-shadow:
    0 20px 60px rgba(0, 0, 0, 0.45),
    inset 0 0 0 1px rgba(255, 209, 102, 0.22),
    inset 0 0 0 2px rgba(249, 115, 22, 0.12);
}

.analysis-card.token-eth {
  --analysis-border-gradient: linear-gradient(122deg, rgba(132, 186, 255, 0.95), rgba(77, 134, 255, 0.94));
  --menu-border-gradient: linear-gradient(122deg, rgba(132, 186, 255, 0.95), rgba(77, 134, 255, 0.94));
}

.analysis-card.token-sol {
  --analysis-border-gradient: linear-gradient(122deg, rgba(167, 139, 250, 0.94), rgba(52, 152, 219, 0.95));
  --menu-border-gradient: linear-gradient(122deg, rgba(167, 139, 250, 0.94), rgba(52, 152, 219, 0.95));
}

.analysis-card.token-xrp {
  --analysis-border-gradient: linear-gradient(122deg, rgba(255, 255, 255, 0.92), rgba(0, 0, 0, 0.95));
  --menu-border-gradient: linear-gradient(122deg, rgba(255, 255, 255, 0.92), rgba(0, 0, 0, 0.95));
}

.card-top {
  position: relative;
  z-index: 40;
}

.content-grid {
  position: relative;
  z-index: 1;
}

.asset-selector {
  position: relative;
  z-index: 120;
}

.asset-toggle {
  cursor: pointer;
  color: #f3f6ff;
  min-height: 48px;
  padding: 12px 18px;
  position: relative;
  overflow: hidden;
  transition:
    transform 0.2s ease,
    box-shadow 0.2s ease,
    filter 0.2s ease,
    border-color 0.2s ease;
}

.asset-toggle::after {
  content: '';
  position: absolute;
  top: -85%;
  left: -40%;
  width: 64%;
  height: 240%;
  transform: rotate(18deg);
  background: linear-gradient(
    120deg,
    rgba(255, 255, 255, 0) 0%,
    var(--asset-sheen-a) 42%,
    var(--asset-sheen-b) 56%,
    rgba(255, 255, 255, 0) 76%
  );
  opacity: 0;
  mix-blend-mode: screen;
  filter: blur(0.2px);
  pointer-events: none;
  transition: opacity 0.22s ease, transform 0.34s ease;
}

.asset-toggle .pair,
.asset-toggle .token-chevron {
  color: #f3f6ff;
}

.asset-toggle:hover,
.asset-toggle.open {
  transform: translateY(-1px);
  filter: saturate(1.08);
  border-color: rgba(255, 255, 255, 0.28);
}

.asset-toggle:hover::after,
.asset-toggle.open::after {
  opacity: 0.85;
  transform: translateX(155%) rotate(18deg);
}

.asset-toggle:hover .pair-icon,
.asset-toggle.open .pair-icon {
  transform: scale(1.05);
}

.asset-toggle .pair-icon {
  transition: transform 0.22s ease;
}

.asset-toggle.asset-btc:hover,
.asset-toggle.asset-btc.open {
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 0 0 1px rgba(255, 209, 102, 0.34),
    0 0 20px rgba(255, 170, 74, 0.24),
    0 10px 20px rgba(26, 17, 6, 0.35);
}

.asset-toggle.asset-eth:hover,
.asset-toggle.asset-eth.open {
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 0 0 1px rgba(132, 186, 255, 0.36),
    0 0 20px rgba(111, 165, 255, 0.22),
    0 10px 20px rgba(8, 12, 22, 0.35);
}

.asset-toggle.asset-sol:hover,
.asset-toggle.asset-sol.open {
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.08),
    0 0 0 1px rgba(167, 139, 250, 0.34),
    0 0 20px rgba(133, 145, 235, 0.22),
    0 10px 20px rgba(10, 12, 24, 0.35);
}

.asset-toggle.asset-xrp:hover,
.asset-toggle.asset-xrp.open {
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.1),
    0 0 0 1px rgba(255, 255, 255, 0.34),
    0 0 20px rgba(255, 255, 255, 0.16),
    0 10px 20px rgba(0, 0, 0, 0.42);
}

.token-chevron {
  font-size: 0.7rem;
  opacity: 0.85;
  transition: transform 0.2s ease, opacity 0.2s ease;
}

.asset-toggle.open .token-chevron {
  transform: rotate(180deg);
  opacity: 1;
}

.token-menu {
  position: absolute;
  top: calc(100% + 10px);
  left: 0;
  min-width: 220px;
  padding: 8px;
  border-radius: 12px;
  background:
    var(--menu-surface) padding-box,
    var(--menu-border-gradient) border-box;
  border: 1px solid transparent;
  box-shadow:
    0 14px 28px rgba(3, 7, 16, 0.66),
    inset 0 1px 0 rgba(255, 255, 255, 0.06);
  display: grid;
  gap: 6px;
  z-index: 140;
}

.token-menu-item {
  --item-accent-a: rgba(113, 146, 236, 0.86);
  --item-accent-b: rgba(42, 47, 58, 0.9);
  position: relative;
  overflow: hidden;
  display: inline-flex;
  align-items: center;
  gap: 10px;
  width: 100%;
  border-radius: 10px;
  padding: 9px 11px;
  border: 1px solid transparent;
  background: transparent;
  color: #e8eeff;
  font-weight: 700;
  font-size: 0.75rem;
  letter-spacing: 0.05em;
  text-transform: uppercase;
  text-align: left;
  cursor: pointer;
  transition: transform 0.2s ease, border-color 0.2s ease, background 0.2s ease;
}

.token-menu-item::before {
  content: '';
  position: absolute;
  left: 0;
  top: 0;
  bottom: 0;
  width: 3px;
  background: linear-gradient(180deg, var(--item-accent-a), var(--item-accent-b));
  opacity: 0.26;
  transition: opacity 0.2s ease;
}

.token-menu-item:hover {
  transform: translateY(-1px);
  box-shadow: 0 8px 14px rgba(6, 11, 22, 0.36);
}

.token-menu-item:hover::before {
  opacity: 0.75;
}

.token-menu-item.active::before {
  opacity: 1;
}

.token-menu-item.asset-btc {
  --item-accent-a: rgba(255, 209, 102, 0.92);
  --item-accent-b: rgba(249, 115, 22, 0.9);
}

.token-menu-item.asset-eth {
  --item-accent-a: rgba(132, 186, 255, 0.92);
  --item-accent-b: rgba(77, 134, 255, 0.9);
}

.token-menu-item.asset-sol {
  --item-accent-a: rgba(167, 139, 250, 0.92);
  --item-accent-b: rgba(52, 152, 219, 0.9);
}

.token-menu-item.asset-xrp {
  --item-accent-a: rgba(255, 255, 255, 0.92);
  --item-accent-b: rgba(32, 32, 32, 0.9);
}

.token-menu-item.asset-btc:hover,
.token-menu-item.asset-btc.active {
  border: 1px solid transparent;
  background:
    linear-gradient(130deg, rgba(20, 17, 11, 0.96), rgba(31, 22, 12, 0.96)) padding-box,
    linear-gradient(122deg, rgba(255, 209, 102, 0.95), rgba(249, 115, 22, 0.92)) border-box;
}

.token-menu-item.asset-eth:hover,
.token-menu-item.asset-eth.active {
  border: 1px solid transparent;
  background:
    linear-gradient(130deg, rgba(12, 18, 34, 0.96), rgba(21, 24, 33, 0.96)) padding-box,
    linear-gradient(122deg, rgba(132, 186, 255, 0.95), rgba(77, 134, 255, 0.94)) border-box;
}

.token-menu-item.asset-sol:hover,
.token-menu-item.asset-sol.active {
  border: 1px solid transparent;
  background:
    linear-gradient(130deg, rgba(15, 17, 34, 0.96), rgba(15, 25, 41, 0.96)) padding-box,
    linear-gradient(122deg, rgba(167, 139, 250, 0.94), rgba(52, 152, 219, 0.95)) border-box;
}

.token-menu-item.asset-xrp:hover,
.token-menu-item.asset-xrp.active {
  border: 1px solid transparent;
  background:
    linear-gradient(130deg, rgba(16, 16, 16, 0.96), rgba(7, 7, 7, 0.96)) padding-box,
    linear-gradient(122deg, rgba(255, 255, 255, 0.9), rgba(18, 18, 18, 0.95)) border-box;
}

.analysis-spotlight {
  z-index: 6;
  overflow: visible;
}

.quick-narrative {
  z-index: 1;
}

.spotlight-head {
  justify-content: stretch;
  position: relative;
  z-index: 50;
}

.history-dropdown {
  flex: 1 1 100%;
  min-width: 0;
}

.history-dropdown {
  position: relative;
  z-index: 120;
  width: 100%;
}

.history-toggle {
  justify-content: center;
  width: 100%;
  height: 40px;
  padding: 7px 11px;
  font-size: 0.72rem;
  letter-spacing: 0.08em;
}

.history-menu {
  right: 0;
  left: auto;
  top: calc(100% + 8px);
  bottom: auto;
  width: min(360px, calc(100vw - 64px));
  z-index: 140;
  border: 1px solid transparent;
  background:
    linear-gradient(170deg, rgba(10, 14, 27, 0.98), rgba(11, 16, 30, 0.98)) padding-box,
    var(--menu-border-gradient) border-box;
  transform-origin: top center;
  overflow: hidden;
  overflow-y: auto;
  scrollbar-gutter: stable;
}

.history-menu::-webkit-scrollbar-track {
  margin: 4px 2px;
}

.history-menu::-webkit-scrollbar-thumb {
  border: 1px solid rgba(8, 12, 24, 0.6);
}

.chart-panel {
  --chart-border-gradient: linear-gradient(122deg, rgba(95, 136, 255, 0.92), rgba(157, 97, 216, 0.86));
  background:
    linear-gradient(155deg, rgba(17, 23, 35, 0.9), rgba(12, 18, 31, 0.88)) padding-box,
    var(--chart-border-gradient) border-box;
  border: 1px solid transparent;
  padding: 1px;
}

.chart-panel.theme-btc {
  --chart-border-gradient: linear-gradient(122deg, rgba(255, 209, 102, 0.95), rgba(249, 115, 22, 0.92));
}

.chart-panel.theme-eth {
  --chart-border-gradient: linear-gradient(122deg, rgba(132, 186, 255, 0.95), rgba(77, 134, 255, 0.94));
}

.chart-panel.theme-sol {
  --chart-border-gradient: linear-gradient(122deg, rgba(167, 139, 250, 0.94), rgba(52, 152, 219, 0.95));
}

.chart-panel.theme-xrp {
  --chart-border-gradient: linear-gradient(122deg, rgba(255, 255, 255, 0.92), rgba(0, 0, 0, 0.95));
}

.tv-container {
  position: relative;
  border-radius: 15px;
  overflow: hidden;
  background: #0b0f1b;
}

.tv-container::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  box-shadow: 0 0 0 1px #0b0f1b inset;
}

.tv-container > *,
.tv-container iframe {
  border: 0 !important;
  border-radius: 15px;
}

.fullscreen-btn {
  top: 6px;
  right: 48px;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  border: 1px solid rgba(47, 76, 146, 0.7);
  background: #131722;
  box-shadow: 0 0 0 1px rgba(12, 16, 26, 0.5) inset;
}

.fullscreen-btn:hover {
  transform: none;
  border-color: rgba(70, 116, 221, 0.9);
  box-shadow:
    0 0 0 1px rgba(70, 116, 221, 0.35),
    0 0 0 1px rgba(12, 16, 26, 0.5) inset;
}

.analysis-shell {
  box-sizing: border-box;
  min-height: calc(100vh - var(--app-header-h));
  height: auto;
  padding: calc(var(--app-header-h) + 6px) var(--app-gutter) 16px;
  overflow: visible;
}

.analysis-card {
  height: auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
}

.content-grid {
  flex: 0 0 auto;
  min-height: 0;
}

.trend-box {
  border: none !important;
  background: transparent !important;
  box-shadow: none !important;
  padding: 0 !important;
}

.general-trend-card,
.narrative-item {
  position: relative;
  overflow: visible;
  border-radius: 10px;
  animation: card-border-pulse 2.8s ease-in-out infinite;
}

.general-trend-card::before,
.narrative-item::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: inherit;
  border: 1px solid color-mix(in srgb, var(--ring-a) 78%, transparent);
  pointer-events: none;
  opacity: 0;
  transform: scale(0.993);
  animation: card-border-breathe 2.8s ease-out infinite;
}

.general-trend-card {
  text-align: center;
}

.general-title {
  font-size: 0.74rem;
  font-weight: 800;
  letter-spacing: 0.03em;
  text-transform: uppercase;
  line-height: 1.22;
}

.general-title > span {
  position: relative;
  display: inline-block;
  padding-bottom: 4px;
}

.general-title > span::after {
  content: '';
  position: absolute;
  left: 0;
  bottom: 0;
  width: 100%;
  height: 3px;
  border-radius: 999px;
  background: currentColor;
  opacity: 0.82;
  box-shadow:
    0 0 8px color-mix(in srgb, currentColor 54%, transparent),
    0 0 0 1px color-mix(in srgb, currentColor 24%, transparent) inset;
  animation: general-title-line-pulse-fixed 1.8s ease-in-out infinite;
}

.general-text {
  text-transform: uppercase;
  font-size: 0.72rem;
  line-height: 1.38;
  font-weight: 700;
  text-align: center;
}

.narrative-item-head .range {
  font-size: 0.74rem;
  font-weight: 800;
}

.narrative-item-text {
  font-size: 0.74rem;
  line-height: 1.4;
  font-weight: 700;
  text-transform: uppercase;
}

.narrative-item.trend-alta,
.general-trend-card.trend-alta {
  --ring-a: rgba(31, 223, 156, 0.82);
  border-color: rgba(16, 185, 129, 0.7) !important;
  background: rgba(31, 223, 156, 0.14) !important;
}

.narrative-item.trend-alta .range,
.general-trend-card.trend-alta .general-title {
  color: #1fdf9c !important;
}

.narrative-item.trend-alta .narrative-item-text,
.general-trend-card.trend-alta .general-text {
  color: #34d399 !important;
}

.narrative-item.trend-baixa,
.general-trend-card.trend-baixa {
  --ring-a: rgba(239, 68, 68, 0.84);
  border-color: rgba(239, 68, 68, 0.7) !important;
  background: rgba(239, 68, 68, 0.14) !important;
}

.narrative-item.trend-baixa .range,
.general-trend-card.trend-baixa .general-title {
  color: #ef4444 !important;
}

.narrative-item.trend-baixa .narrative-item-text,
.general-trend-card.trend-baixa .general-text {
  color: #f87171 !important;
}

.narrative-item.trend-neutra,
.general-trend-card.trend-neutra {
  --ring-a: rgba(250, 204, 21, 0.82);
  border-color: rgba(250, 204, 21, 0.68) !important;
  background: rgba(250, 204, 21, 0.14) !important;
}

.narrative-item.trend-neutra .range,
.general-trend-card.trend-neutra .general-title {
  color: #facc15 !important;
}

.narrative-item.trend-neutra .narrative-item-text,
.general-trend-card.trend-neutra .general-text {
  color: #ffd166 !important;
}

.insight-label {
  color: #f7f9ff;
  background: var(--token-border-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.narrative-insight-strip {
  --ring-a: rgba(250, 204, 21, 0.82);
  position: relative;
  z-index: 1;
  display: flex;
  flex-direction: column;
  justify-content: center;
  overflow: visible;
  margin-top: 14px;
  height: 84px;
  padding: 12px 14px;
  border: 1px solid rgba(250, 204, 21, 0.68);
  border-radius: 14px;
  background: rgba(250, 204, 21, 0.14);
  box-shadow:
    0 10px 24px rgba(3, 8, 18, 0.24),
    0 0 0 1px rgba(255, 255, 255, 0.02) inset;
  animation: card-border-pulse 2.8s ease-in-out infinite;
}

.narrative-insight-strip::before {
  content: '';
  position: absolute;
  inset: -1px;
  border-radius: inherit;
  border: 1px solid color-mix(in srgb, var(--ring-a) 78%, transparent);
  opacity: 0;
  transform: scale(0.993);
  animation: card-border-breathe 2.8s ease-out infinite;
  pointer-events: none;
}

.narrative-insight-strip::after {
  content: '';
  position: absolute;
  inset: 1px;
  border-radius: inherit;
  pointer-events: none;
  box-shadow: 0 0 16px rgba(250, 204, 21, 0.18) inset;
}

.narrative-insight-strip-text {
  position: relative;
  z-index: 1;
}

.narrative-insight-strip-text {
  margin: 0;
  color: #ffd166;
  font-size: 0.74rem;
  line-height: 1.4;
  font-weight: 700;
  text-transform: uppercase;
  max-width: 100%;
  max-height: calc(1.4em * 3);
  overflow: hidden;
  display: -webkit-box;
  -webkit-box-orient: vertical;
  -webkit-line-clamp: 3;
  line-clamp: 3;
}

.narrative-insight-strip.trend-alta .narrative-insight-strip-text {
  color: #34d399;
}

.narrative-insight-strip.trend-baixa .narrative-insight-strip-text {
  color: #f87171;
}

.narrative-insight-strip.trend-neutra .narrative-insight-strip-text {
  color: #ffd166;
}

.narrative-insight-strip.trend-alta {
  --ring-a: rgba(31, 223, 156, 0.82);
  border-color: rgba(16, 185, 129, 0.7);
  background: rgba(31, 223, 156, 0.14);
}

.narrative-insight-strip.trend-alta::after {
  box-shadow: 0 0 16px rgba(16, 185, 129, 0.18) inset;
}

.narrative-insight-strip.trend-baixa {
  --ring-a: rgba(239, 68, 68, 0.84);
  border-color: rgba(239, 68, 68, 0.7);
  background: rgba(239, 68, 68, 0.14);
}

.narrative-insight-strip.trend-baixa::after {
  box-shadow: 0 0 16px rgba(239, 68, 68, 0.16) inset;
}

.insight-carousel-enter-active,
.insight-carousel-leave-active {
  transition: opacity 0.28s ease, transform 0.28s ease, filter 0.28s ease;
}

.insight-carousel-enter-from,
.insight-carousel-leave-to {
  opacity: 0;
  transform: translateY(6px);
  filter: blur(0.8px);
}

.insight-carousel-enter-to,
.insight-carousel-leave-from {
  opacity: 1;
  transform: translateY(0);
  filter: blur(0);
}

@media (max-width: 640px) {
  .spotlight-mini-chart {
    padding: 10px;
  }

  .spotlight-mini-chart-head {
    gap: 8px;
  }

  .spotlight-mini-chart-kicker {
    font-size: 0.58rem;
  }

  .spotlight-mini-chart-stats {
    gap: 6px;
  }

  .spotlight-mini-chart-price {
    font-size: 0.68rem;
  }

  .spotlight-mini-chart-delta {
    font-size: 0.56rem;
  }

  .spotlight-mini-chart-window {
    font-size: 0.54rem;
  }

  .spotlight-mini-chart-stage,
  .spotlight-mini-chart-empty {
    min-height: 82px;
    height: 82px;
  }

  .narrative-insight-strip {
    height: 82px;
    padding: 12px;
  }

  .narrative-insight-strip-text {
    font-size: 0.72rem;
    max-height: calc(1.4em * 3);
  }
}

.insight-text {
  font-weight: 700;
  color: #f7f9ff;
  background: var(--token-border-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
}

.chart-panel {
  min-height: clamp(260px, 46vh, 410px);
}

.tv-container {
  min-height: clamp(240px, 43vh, 390px);
}

.content-grid {
  grid-template-columns: minmax(380px, 430px) minmax(0, 1fr);
  gap: 14px;
  align-items: stretch;
}

.analysis-featured-card,
.analysis-library-panel {
  position: relative;
  overflow: hidden;
  margin-top: 12px;
  border-radius: 16px;
  border: 1px solid transparent;
  background:
    linear-gradient(155deg, rgba(13, 19, 34, 0.96), rgba(8, 13, 25, 0.98)) padding-box,
    var(--token-border-gradient) border-box;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.05),
    0 18px 28px rgba(3, 7, 16, 0.28);
}

.analysis-featured-card::before,
.analysis-library-panel::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(circle at top left, color-mix(in srgb, var(--token-glow) 22%, transparent), transparent 44%),
    linear-gradient(120deg, rgba(255, 255, 255, 0.04), transparent 28%);
  opacity: 0.95;
}

.analysis-featured-card > *,
.analysis-library-panel > * {
  position: relative;
  z-index: 1;
}

.analysis-featured-card {
  padding: 14px;
}

.analysis-featured-head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 10px;
}

.analysis-featured-kicker {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.analysis-featured-tag,
.analysis-featured-count {
  display: inline-flex;
  align-items: center;
  min-height: 28px;
  padding: 0 11px;
  border-radius: 999px;
  font-size: 0.7rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.analysis-featured-tag {
  color: #f7f9ff;
  background:
    linear-gradient(135deg, rgba(104, 74, 255, 0.72), rgba(56, 151, 255, 0.68));
  box-shadow:
    0 0 18px color-mix(in srgb, var(--token-glow) 28%, transparent),
    inset 0 1px 0 rgba(255, 255, 255, 0.16);
}

.analysis-featured-count {
  color: rgba(214, 224, 255, 0.88);
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
}

.analysis-featured-shortcut {
  appearance: none;
  cursor: pointer;
  border: 1px solid rgba(255, 255, 255, 0.08);
  background: rgba(255, 255, 255, 0.04);
  color: #f7f9ff;
  border-radius: 12px;
  min-height: 40px;
  padding: 0 14px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition:
    transform 0.18s ease,
    border-color 0.18s ease,
    background 0.18s ease;
}

.analysis-featured-shortcut:hover {
  transform: translateY(-1px);
  border-color: color-mix(in srgb, var(--token-glow) 48%, transparent);
  background: rgba(255, 255, 255, 0.07);
}

.analysis-featured-stage {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  margin-top: 14px;
  color: rgba(191, 207, 255, 0.86);
  font-size: 0.72rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.analysis-featured-stage-label {
  color: color-mix(in srgb, #ffffff 68%, var(--token-glow));
}

.analysis-featured-stage-divider {
  width: 24px;
  height: 1px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--token-glow) 56%, transparent);
  box-shadow: 0 0 14px color-mix(in srgb, var(--token-glow) 32%, transparent);
}

.analysis-featured-title {
  margin: 14px 0 0;
  color: #f7f9ff;
  font-size: clamp(1.35rem, 1.05rem + 0.7vw, 1.82rem);
  line-height: 1.02;
  font-weight: 900;
  letter-spacing: -0.03em;
}

.analysis-featured-description {
  margin: 12px 0 0;
  color: rgba(220, 228, 250, 0.82);
  font-size: 0.94rem;
  line-height: 1.58;
}

.analysis-featured-meta {
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  margin-top: 14px;
}

.analysis-featured-meta-item {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  min-height: 34px;
  padding: 0 11px;
  border-radius: 11px;
  color: rgba(226, 233, 255, 0.9);
  font-size: 0.76rem;
  font-weight: 700;
  background: rgba(255, 255, 255, 0.045);
  border: 1px solid rgba(255, 255, 255, 0.08);
}

.analysis-featured-actions {
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto;
  gap: 10px;
  margin-top: 16px;
}

.analysis-featured-primary {
  margin-top: 0;
}

.analysis-featured-secondary {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 9px;
  min-height: 46px;
  padding: 0 16px;
  border-radius: 12px;
  border: 1px solid rgba(255, 255, 255, 0.08);
  color: #f7f9ff;
  background: rgba(255, 255, 255, 0.04);
  font-size: 0.8rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  text-decoration: none;
  transition:
    transform 0.18s ease,
    border-color 0.18s ease,
    background 0.18s ease;
}

.analysis-featured-secondary:hover {
  transform: translateY(-1px);
  border-color: color-mix(in srgb, var(--token-glow) 44%, transparent);
  background: rgba(255, 255, 255, 0.07);
}

.spotlight-head-stack {
  align-items: flex-start;
}

.spotlight-chip-row {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.spotlight-price-line {
  margin-bottom: 12px;
  padding-bottom: 10px;
  border-bottom: 1px solid rgba(255, 166, 87, 0.14);
}

.spotlight-mini-chart {
  position: relative;
  overflow: hidden;
  margin-bottom: 14px;
  padding: 10px 12px 12px;
  border-radius: 14px;
  border: 1px solid transparent;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 10px 18px rgba(5, 8, 16, 0.18);
  transition:
    transform 0.22s ease,
    box-shadow 0.22s ease,
    border-color 0.22s ease;
}

.spotlight-mini-chart::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    radial-gradient(circle at 14% 26%, color-mix(in srgb, var(--token-core) 32%, transparent), transparent 38%),
    linear-gradient(180deg, rgba(255, 255, 255, 0.02), transparent 24%);
  opacity: 0.88;
  transition:
    opacity 0.22s ease,
    transform 0.32s ease;
}

.spotlight-mini-chart-head,
.spotlight-mini-chart-stage,
.spotlight-mini-chart-empty {
  position: relative;
  z-index: 1;
}

.spotlight-mini-chart-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  margin-bottom: 10px;
}

.spotlight-mini-chart:hover {
  transform: translateY(-1px);
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.06),
    0 14px 24px rgba(5, 8, 16, 0.24),
    0 0 0 1px color-mix(in srgb, var(--token-border-strong) 12%, transparent) inset;
}

.spotlight-mini-chart:hover::before {
  opacity: 1;
  transform: translateX(1.5%);
}

.spotlight-mini-chart-kicker {
  display: inline-flex;
  align-items: center;
  gap: 7px;
  color: rgba(236, 241, 255, 0.92);
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.1em;
  text-transform: uppercase;
}

.spotlight-mini-chart-kicker-dot {
  width: 7px;
  height: 7px;
  display: inline-block;
  position: relative;
  border-radius: 999px;
  background: var(--token-border-gradient);
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--token-border-strong) 22%, transparent),
    0 0 12px color-mix(in srgb, var(--token-glow) 36%, transparent);
}

.spotlight-mini-chart-kicker-dot::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: 999px;
  transform-origin: center;
  transform: scale(1);
  background: inherit;
  opacity: 0.6;
  filter: blur(0.5px);
  animation: status-ping 1.6s ease-out infinite;
}

.spotlight-mini-chart-kicker-dot.trend-alta {
  background: #34d399;
  box-shadow:
    0 0 0 1px rgba(16, 185, 129, 0.24),
    0 0 14px rgba(52, 211, 153, 0.34);
}

.spotlight-mini-chart-kicker-dot.trend-neutra {
  background: #facc15;
  box-shadow:
    0 0 0 1px rgba(250, 204, 21, 0.24),
    0 0 14px rgba(250, 204, 21, 0.3);
}

.spotlight-mini-chart-kicker-dot.trend-baixa {
  background: #ef4444;
  box-shadow:
    0 0 0 1px rgba(239, 68, 68, 0.24),
    0 0 14px rgba(239, 68, 68, 0.3);
}

.spotlight-mini-chart-window {
  color: rgba(201, 212, 242, 0.7);
  font-size: 0.58rem;
  font-weight: 700;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  white-space: nowrap;
  display: inline-flex;
  align-items: center;
  opacity: 0.78;
  transition:
    opacity 0.2s ease,
    transform 0.2s ease;
}

.spotlight-mini-chart-window::before {
  content: '•';
  margin-right: 6px;
  color: rgba(201, 212, 242, 0.38);
}

.spotlight-mini-chart-stats {
  display: inline-flex;
  align-items: baseline;
  justify-content: flex-end;
  gap: 8px;
  min-width: 0;
  white-space: nowrap;
  transition:
    transform 0.22s ease,
    opacity 0.22s ease;
}

.spotlight-mini-chart-price {
  color: rgba(244, 247, 255, 0.96);
  font-size: 0.76rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  transition:
    color 0.22s ease,
    text-shadow 0.22s ease;
}

.spotlight-mini-chart-delta {
  font-size: 0.62rem;
  font-weight: 800;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  transition: color 0.18s ease, text-shadow 0.18s ease, opacity 0.18s ease;
}

.spotlight-mini-chart-stats.trend-alta .spotlight-mini-chart-price {
  color: color-mix(in srgb, #f4f7ff 82%, #34d399 18%);
}

.spotlight-mini-chart-stats.trend-neutra .spotlight-mini-chart-price {
  color: color-mix(in srgb, #f4f7ff 84%, #facc15 16%);
}

.spotlight-mini-chart-stats.trend-baixa .spotlight-mini-chart-price {
  color: color-mix(in srgb, #f4f7ff 82%, #f87171 18%);
}

.spotlight-mini-chart-stats.is-up .spotlight-mini-chart-delta {
  color: #34d399;
  text-shadow: 0 0 10px rgba(52, 211, 153, 0.14);
}

.spotlight-mini-chart-stats.is-down .spotlight-mini-chart-delta {
  color: #f87171;
  text-shadow: 0 0 10px rgba(248, 113, 113, 0.12);
}

.spotlight-mini-chart-stats.is-neutral .spotlight-mini-chart-delta {
  color: #facc15;
  text-shadow: 0 0 10px rgba(250, 204, 21, 0.12);
  opacity: 0.86;
}

.spotlight-mini-chart:hover .spotlight-mini-chart-stats {
  transform: translateX(1px);
}

.spotlight-mini-chart:hover .spotlight-mini-chart-price {
  text-shadow: 0 0 12px color-mix(in srgb, var(--token-glow) 16%, transparent);
}

.spotlight-mini-chart:hover .spotlight-mini-chart-window {
  opacity: 0.86;
  transform: translateX(1px);
}

.spotlight-mini-chart-stage {
  height: 92px;
  border-radius: 12px;
  overflow: hidden;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0)),
    repeating-linear-gradient(
      to right,
      rgba(255, 255, 255, 0.028) 0,
      rgba(255, 255, 255, 0.028) 1px,
      transparent 1px,
      transparent 40px
    );
}

.spotlight-mini-chart-svg {
  display: block;
  width: 100%;
  height: 100%;
}

.spotlight-mini-chart-guide {
  fill: none;
  stroke: rgba(255, 255, 255, 0.08);
  stroke-width: 1;
  stroke-dasharray: 4 6;
  opacity: 0.65;
}

.spotlight-mini-chart-area {
  opacity: 1;
}

.spotlight-mini-chart-line {
  fill: none;
  stroke: color-mix(in srgb, var(--token-border-strong) 92%, #ffffff 8%);
  stroke-width: 2.4;
  stroke-linecap: round;
  stroke-linejoin: round;
  filter: drop-shadow(0 0 8px color-mix(in srgb, var(--token-glow) 22%, transparent));
}

.spotlight-mini-chart-point-pulse {
  fill: color-mix(in srgb, var(--token-glow) 38%, transparent);
  transform-box: fill-box;
  transform-origin: center;
  animation: spotlight-point-pulse 1.85s ease-out infinite;
}

.spotlight-mini-chart-point-glow {
  fill: color-mix(in srgb, var(--token-glow) 40%, transparent);
  animation: spotlight-point-breathe 1.85s ease-in-out infinite;
}

.spotlight-mini-chart-point {
  fill: #f8fbff;
  stroke: color-mix(in srgb, var(--token-border-strong) 90%, transparent);
  stroke-width: 2;
  filter: drop-shadow(0 0 8px color-mix(in srgb, var(--token-glow) 34%, transparent));
}

.spotlight-mini-chart-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 92px;
  border-radius: 12px;
  color: rgba(201, 212, 242, 0.64);
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.03em;
  text-align: center;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.02), rgba(255, 255, 255, 0));
}

.spotlight-mini-chart.is-empty {
  padding-bottom: 10px;
}

.spotlight-kicker-text {
  margin: 12px 0 0;
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 8px;
  color: rgba(201, 212, 242, 0.88);
  font-size: 0.72rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
}

.spotlight-kicker-line {
  width: 26px;
  height: 1px;
  border-radius: 999px;
  background: color-mix(in srgb, var(--token-glow) 54%, transparent);
}

.analysis-library-shell {
  background:
    linear-gradient(180deg, rgba(14, 18, 32, 0.985), rgba(9, 12, 24, 0.985)) padding-box,
    var(--token-border-gradient) border-box;
  border: 1px solid transparent;
  border-radius: 16px;
  padding: 14px;
  box-shadow:
    0 16px 34px rgba(0, 0, 0, 0.24),
    inset 0 1px 0 rgba(255, 255, 255, 0.05);
  display: flex;
  flex-direction: column;
  position: relative;
}

.analysis-library-shell::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  pointer-events: none;
  background:
    radial-gradient(circle at bottom left, color-mix(in srgb, var(--token-core) 18%, transparent), transparent 34%);
  opacity: 1;
}

.library-feature-card,
.library-archive-panel {
  position: relative;
  overflow: hidden;
  border-radius: 14px;
  border: 1px solid transparent;
  background:
    linear-gradient(155deg, rgba(11, 16, 30, 0.99), rgba(7, 11, 22, 0.99)) padding-box,
    linear-gradient(135deg, color-mix(in srgb, var(--token-border-strong) 44%, transparent), rgba(255, 255, 255, 0.05)) border-box;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    0 10px 22px rgba(0, 0, 0, 0.16);
}

.library-archive-panel::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.02), transparent 22%);
  opacity: 0.72;
}

.library-feature-current {
  position: relative;
  z-index: 1;
}

.library-feature-badges {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  margin-bottom: 10px;
}

.library-feature-current {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-height: 24px;
  padding: 0 12px;
  border-radius: 0;
  color: color-mix(in srgb, #ffffff 82%, var(--token-glow));
  font-size: 0.6rem;
  font-weight: 800;
  letter-spacing: 0.12em;
  text-transform: uppercase;
  position: relative;
  isolation: isolate;
  box-shadow: 0 6px 12px rgba(0, 0, 0, 0.14);
  transition:
    transform 0.22s ease,
    filter 0.22s ease;
}

.library-feature-current::before {
  content: '';
  position: absolute;
  inset: 0;
  clip-path: polygon(10px 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0 50%);
  background:
    linear-gradient(135deg, color-mix(in srgb, var(--token-border-strong) 94%, transparent), color-mix(in srgb, var(--token-core) 78%, transparent));
  box-shadow:
    inset 0 0 0 1px color-mix(in srgb, var(--token-border-strong) 26%, transparent);
  z-index: -2;
  animation: latest-chip-outline 2.8s ease-in-out infinite;
}

.library-feature-current::after {
  content: '';
  position: absolute;
  inset: 1px;
  clip-path: polygon(10px 0, calc(100% - 10px) 0, 100% 50%, calc(100% - 10px) 100%, 10px 100%, 0 50%);
  background:
    linear-gradient(180deg, rgba(14, 17, 28, 0.96), rgba(10, 13, 23, 0.98));
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.12),
    inset 0 0 0 1px color-mix(in srgb, var(--token-border-strong) 30%, transparent);
  opacity: 0.98;
  z-index: -1;
  animation: latest-chip-sheen 5.4s ease-in-out infinite;
}

.library-archive-panel {
  padding: 14px;
  flex: 1 1 auto;
  min-height: 0;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  position: relative;
  z-index: 1;
  background:
    linear-gradient(180deg, rgba(9, 13, 27, 0.99), rgba(6, 9, 19, 0.99)) padding-box,
    linear-gradient(145deg, color-mix(in srgb, var(--token-border-strong) 36%, transparent), rgba(255, 255, 255, 0.04)) border-box;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    inset 0 0 0 1px rgba(255, 255, 255, 0.02),
    0 12px 24px rgba(0, 0, 0, 0.18);
}

.analysis-library-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
}

.analysis-library-grid-inline {
  grid-template-columns: repeat(2, minmax(0, 1fr));
  grid-auto-rows: minmax(142px, auto);
  gap: 14px;
  overflow-y: auto;
  padding: 4px 8px 4px 2px;
  flex: 1 1 auto;
  min-height: 0;
  align-content: start;
}

.analysis-library-card-featured {
  grid-column: 1 / -1;
  min-height: 164px;
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--token-core) 10%, rgba(8, 13, 26, 0.99)), rgba(5, 9, 19, 0.99)) padding-box,
    linear-gradient(135deg, color-mix(in srgb, var(--token-border-strong) 52%, transparent), rgba(255, 255, 255, 0.05)) border-box;
}

.analysis-library-grid-inline::-webkit-scrollbar {
  width: 8px;
}

.analysis-library-grid-inline::-webkit-scrollbar-track {
  background: rgba(255, 255, 255, 0.03);
  border-radius: 999px;
}

.analysis-library-grid-inline::-webkit-scrollbar-thumb {
  background: linear-gradient(180deg, color-mix(in srgb, var(--token-glow) 72%, transparent), color-mix(in srgb, var(--token-core) 34%, transparent));
  border-radius: 999px;
  border: 1px solid rgba(12, 14, 24, 0.8);
}

.analysis-library-shell.theme-btc .analysis-library-grid-inline::-webkit-scrollbar-thumb {
  background:
    linear-gradient(180deg, rgba(255, 209, 102, 0.96), rgba(249, 115, 22, 0.94));
  border: 1px solid rgba(88, 51, 11, 0.72);
  box-shadow:
    0 0 0 1px rgba(255, 209, 102, 0.18) inset,
    0 0 12px rgba(249, 115, 22, 0.22);
}

.analysis-library-shell.theme-eth .analysis-library-grid-inline::-webkit-scrollbar-thumb {
  background:
    linear-gradient(180deg, rgba(132, 186, 255, 0.96), rgba(77, 134, 255, 0.94));
  border: 1px solid rgba(46, 78, 132, 0.78);
  box-shadow:
    0 0 0 1px rgba(132, 186, 255, 0.14) inset,
    0 0 10px rgba(77, 134, 255, 0.18);
}

.analysis-library-shell.theme-sol .analysis-library-grid-inline::-webkit-scrollbar-thumb {
  background:
    linear-gradient(180deg, rgba(167, 139, 250, 0.96), rgba(52, 152, 219, 0.94));
  border: 1px solid rgba(43, 63, 108, 0.76);
  box-shadow:
    0 0 0 1px rgba(167, 139, 250, 0.14) inset,
    0 0 10px rgba(52, 152, 219, 0.18);
}

.analysis-library-shell.theme-xrp .analysis-library-grid-inline::-webkit-scrollbar-thumb {
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.94), rgba(70, 70, 70, 0.96));
  border: 1px solid rgba(38, 38, 38, 0.82);
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.12) inset,
    0 0 10px rgba(255, 255, 255, 0.1);
}

.analysis-library-card {
  appearance: none;
  cursor: pointer;
  width: 100%;
  min-height: 142px;
  padding: 0;
  border: 1px solid transparent;
  border-radius: 16px;
  background:
    linear-gradient(180deg, rgba(8, 13, 26, 0.99), rgba(5, 9, 19, 0.99)) padding-box,
    linear-gradient(135deg, color-mix(in srgb, var(--token-border-strong) 42%, transparent), rgba(255, 255, 255, 0.04)) border-box;
  text-align: left;
  overflow: hidden;
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  position: relative;
  box-shadow:
    inset 0 1px 0 rgba(255, 255, 255, 0.04),
    inset 0 0 0 1px rgba(255, 255, 255, 0.02),
    0 12px 24px rgba(0, 0, 0, 0.18);
  transition:
    transform 0.22s ease,
    border-color 0.22s ease,
    box-shadow 0.22s ease,
    background 0.22s ease;
}

.analysis-library-card::before {
  content: '';
  position: absolute;
  inset: 0;
  pointer-events: none;
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.035), transparent 20%),
    radial-gradient(circle at top right, color-mix(in srgb, var(--token-glow) 18%, transparent), transparent 36%),
    radial-gradient(circle at bottom left, color-mix(in srgb, var(--token-core) 14%, transparent), transparent 30%);
  opacity: 1;
  transition:
    opacity 0.22s ease,
    transform 0.3s ease,
    filter 0.22s ease;
}

.analysis-library-card::after {
  content: none;
}

.analysis-library-card:hover,
.analysis-library-card.active {
  transform: translateY(-3px);
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--token-border-strong) 24%, transparent) inset,
    inset 0 0 0 1px color-mix(in srgb, var(--token-glow) 8%, transparent),
    0 18px 30px rgba(0, 0, 0, 0.24);
}

.analysis-library-card:hover::before,
.analysis-library-card.active::before {
  opacity: 1;
  transform: translate3d(0, -1px, 0);
  background:
    linear-gradient(180deg, rgba(255, 255, 255, 0.045), transparent 18%),
    radial-gradient(circle at top right, color-mix(in srgb, var(--token-glow) 24%, transparent), transparent 34%),
    radial-gradient(circle at bottom left, color-mix(in srgb, var(--token-core) 18%, transparent), transparent 28%);
}

.analysis-library-card.variant-0 {
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--token-core) 12%, rgba(8, 13, 26, 0.99)), rgba(5, 9, 19, 0.99)) padding-box,
    linear-gradient(135deg, color-mix(in srgb, var(--token-core) 48%, transparent), rgba(255, 255, 255, 0.04)) border-box;
  box-shadow:
    inset 0 0 0 1px color-mix(in srgb, var(--token-core) 14%, transparent),
    0 12px 24px rgba(0, 0, 0, 0.18);
}

.analysis-library-card.variant-1 {
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--token-glow) 10%, rgba(8, 13, 26, 0.99)), rgba(5, 9, 19, 0.99)) padding-box,
    linear-gradient(135deg, color-mix(in srgb, var(--token-glow) 46%, transparent), rgba(255, 255, 255, 0.04)) border-box;
  box-shadow:
    inset 0 0 0 1px color-mix(in srgb, var(--token-glow) 14%, transparent),
    0 12px 24px rgba(0, 0, 0, 0.18);
}

.analysis-library-card.variant-2 {
  background:
    linear-gradient(180deg, color-mix(in srgb, var(--token-edge) 12%, rgba(8, 13, 26, 0.99)), rgba(5, 9, 19, 0.99)) padding-box,
    linear-gradient(135deg, color-mix(in srgb, var(--token-edge) 48%, transparent), rgba(255, 255, 255, 0.04)) border-box;
  box-shadow:
    inset 0 0 0 1px color-mix(in srgb, var(--token-edge) 14%, transparent),
    0 12px 24px rgba(0, 0, 0, 0.18);
}

.analysis-library-card:hover .analysis-library-card-title,
.analysis-library-card.active .analysis-library-card-title {
  color: #ffffff;
  text-shadow: 0 0 16px color-mix(in srgb, var(--token-glow) 16%, transparent);
}

.analysis-library-card:hover .analysis-library-card-topline,
.analysis-library-card.active .analysis-library-card-topline {
  color: rgba(236, 242, 255, 0.92);
  border-color: color-mix(in srgb, var(--token-border-strong) 24%, rgba(255, 255, 255, 0.06));
}

.analysis-library-card:hover .analysis-library-card-summary,
.analysis-library-card.active .analysis-library-card-summary {
  color: color-mix(in srgb, #ffffff 82%, var(--token-glow));
  transform: translateX(1px);
}

.analysis-library-card:hover .library-feature-current,
.analysis-library-card.active .library-feature-current {
  transform: translateX(1px);
  filter: brightness(1.06);
}

.analysis-library-shell.theme-btc .analysis-library-card {
  background:
    linear-gradient(180deg, rgba(10, 13, 22, 0.99), rgba(7, 9, 17, 0.99)) padding-box,
    linear-gradient(135deg, rgba(255, 209, 102, 0.18), rgba(249, 115, 22, 0.14)) border-box;
}

.analysis-library-shell.theme-btc .analysis-library-card.variant-0 {
  background:
    linear-gradient(180deg, rgba(18, 16, 10, 0.72), rgba(7, 9, 17, 0.99)) padding-box,
    linear-gradient(135deg, rgba(255, 209, 102, 0.38), rgba(249, 115, 22, 0.2)) border-box;
}

.analysis-library-shell.theme-btc .analysis-library-card.variant-1 {
  background:
    linear-gradient(180deg, rgba(16, 14, 9, 0.7), rgba(7, 9, 17, 0.99)) padding-box,
    linear-gradient(135deg, rgba(255, 209, 102, 0.44), rgba(249, 115, 22, 0.24)) border-box;
}

.analysis-library-shell.theme-btc .analysis-library-card.variant-2,
.analysis-library-shell.theme-btc .analysis-library-card-featured {
  background:
    linear-gradient(180deg, rgba(20, 15, 8, 0.72), rgba(7, 9, 17, 0.99)) padding-box,
    linear-gradient(135deg, rgba(255, 209, 102, 0.48), rgba(249, 115, 22, 0.26)) border-box;
}

.analysis-library-shell.theme-btc .analysis-library-card:hover,
.analysis-library-shell.theme-btc .analysis-library-card.active {
  border-color: rgba(255, 190, 92, 0.92);
  background:
    linear-gradient(130deg, rgba(16, 18, 22, 0.96), rgba(24, 20, 14, 0.95)) padding-box,
    linear-gradient(122deg, rgba(255, 209, 102, 0.44), rgba(249, 115, 22, 0.34)) padding-box,
    var(--token-border-gradient) border-box;
  background-blend-mode: normal, screen, normal;
  box-shadow:
    0 0 0 1px rgba(255, 209, 102, 0.28) inset,
    inset 0 0 0 1px rgba(255, 179, 71, 0.08);
}

.analysis-library-shell.theme-eth .analysis-library-card {
  background:
    linear-gradient(180deg, rgba(8, 12, 24, 0.99), rgba(7, 10, 19, 0.99)) padding-box,
    linear-gradient(135deg, rgba(132, 186, 255, 0.2), rgba(77, 134, 255, 0.16)) border-box;
}

.analysis-library-shell.theme-eth .analysis-library-card.variant-0 {
  background:
    linear-gradient(180deg, rgba(11, 16, 31, 0.74), rgba(7, 10, 19, 0.99)) padding-box,
    linear-gradient(135deg, rgba(132, 186, 255, 0.36), rgba(77, 134, 255, 0.18)) border-box;
}

.analysis-library-shell.theme-eth .analysis-library-card.variant-1,
.analysis-library-shell.theme-eth .analysis-library-card-featured {
  background:
    linear-gradient(180deg, rgba(14, 22, 40, 0.8), rgba(7, 10, 19, 0.99)) padding-box,
    linear-gradient(135deg, rgba(146, 198, 255, 0.48), rgba(77, 134, 255, 0.24)) border-box;
}

.analysis-library-shell.theme-eth .analysis-library-card.variant-2 {
  background:
    linear-gradient(180deg, rgba(10, 15, 28, 0.74), rgba(7, 10, 19, 0.99)) padding-box,
    linear-gradient(135deg, rgba(111, 165, 255, 0.38), rgba(77, 134, 255, 0.18)) border-box;
}

.analysis-library-shell.theme-eth .analysis-library-card:hover,
.analysis-library-shell.theme-eth .analysis-library-card.active {
  border-color: rgba(132, 186, 255, 0.94);
  background:
    linear-gradient(130deg, rgba(12, 18, 34, 0.96), rgba(20, 24, 34, 0.95)) padding-box,
    linear-gradient(122deg, rgba(132, 186, 255, 0.3), rgba(77, 134, 255, 0.26)) padding-box,
    var(--token-border-gradient) border-box;
  background-blend-mode: normal, screen, normal;
  box-shadow:
    0 0 0 1px rgba(132, 186, 255, 0.24) inset,
    inset 0 0 0 1px rgba(111, 165, 255, 0.08);
}

.analysis-library-shell.theme-sol .analysis-library-card {
  background:
    linear-gradient(180deg, rgba(8, 12, 24, 0.99), rgba(7, 10, 19, 0.99)) padding-box,
    linear-gradient(135deg, rgba(167, 139, 250, 0.18), rgba(52, 152, 219, 0.16)) border-box;
}

.analysis-library-shell.theme-sol .analysis-library-card.variant-0 {
  background:
    linear-gradient(180deg, rgba(16, 13, 31, 0.72), rgba(7, 10, 19, 0.99)) padding-box,
    linear-gradient(135deg, rgba(167, 139, 250, 0.38), rgba(52, 152, 219, 0.18)) border-box;
}

.analysis-library-shell.theme-sol .analysis-library-card.variant-1,
.analysis-library-shell.theme-sol .analysis-library-card-featured {
  background:
    linear-gradient(180deg, rgba(12, 18, 32, 0.76), rgba(7, 10, 19, 0.99)) padding-box,
    linear-gradient(135deg, rgba(167, 139, 250, 0.4), rgba(52, 152, 219, 0.22)) border-box;
}

.analysis-library-shell.theme-sol .analysis-library-card.variant-2 {
  background:
    linear-gradient(180deg, rgba(10, 16, 29, 0.74), rgba(7, 10, 19, 0.99)) padding-box,
    linear-gradient(135deg, rgba(133, 145, 235, 0.36), rgba(52, 152, 219, 0.2)) border-box;
}

.analysis-library-shell.theme-sol .analysis-library-card:hover,
.analysis-library-shell.theme-sol .analysis-library-card.active {
  border-color: rgba(133, 145, 235, 0.92);
  background:
    linear-gradient(130deg, rgba(14, 18, 34, 0.96), rgba(16, 20, 34, 0.95)) padding-box,
    linear-gradient(122deg, rgba(167, 139, 250, 0.34), rgba(52, 152, 219, 0.3)) padding-box,
    var(--token-border-gradient) border-box;
  background-blend-mode: normal, screen, normal;
  box-shadow:
    0 0 0 1px rgba(133, 145, 235, 0.24) inset,
    inset 0 0 0 1px rgba(133, 145, 235, 0.08);
}

.analysis-library-shell.theme-xrp .analysis-library-card {
  background:
    linear-gradient(180deg, rgba(12, 12, 12, 0.99), rgba(8, 8, 8, 0.99)) padding-box,
    linear-gradient(135deg, rgba(255, 255, 255, 0.14), rgba(70, 70, 70, 0.16)) border-box;
}

.analysis-library-shell.theme-xrp .analysis-library-card.variant-0 {
  background:
    linear-gradient(180deg, rgba(20, 20, 20, 0.72), rgba(8, 8, 8, 0.99)) padding-box,
    linear-gradient(135deg, rgba(255, 255, 255, 0.28), rgba(70, 70, 70, 0.18)) border-box;
}

.analysis-library-shell.theme-xrp .analysis-library-card.variant-1,
.analysis-library-shell.theme-xrp .analysis-library-card-featured {
  background:
    linear-gradient(180deg, rgba(18, 18, 18, 0.76), rgba(8, 8, 8, 0.99)) padding-box,
    linear-gradient(135deg, rgba(255, 255, 255, 0.32), rgba(70, 70, 70, 0.2)) border-box;
}

.analysis-library-shell.theme-xrp .analysis-library-card.variant-2 {
  background:
    linear-gradient(180deg, rgba(16, 16, 16, 0.74), rgba(8, 8, 8, 0.99)) padding-box,
    linear-gradient(135deg, rgba(220, 220, 220, 0.26), rgba(70, 70, 70, 0.2)) border-box;
}

.analysis-library-shell.theme-xrp .analysis-library-card:hover,
.analysis-library-shell.theme-xrp .analysis-library-card.active {
  border-color: rgba(255, 255, 255, 0.88);
  background:
    linear-gradient(130deg, rgba(20, 20, 20, 0.96), rgba(10, 10, 10, 0.95)) padding-box,
    linear-gradient(122deg, rgba(255, 255, 255, 0.22), rgba(45, 45, 45, 0.28)) padding-box,
    var(--token-border-gradient) border-box;
  background-blend-mode: normal, screen, normal;
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.18) inset,
    inset 0 0 0 1px rgba(255, 255, 255, 0.06);
}

.analysis-library-card-body {
  display: flex;
  flex-direction: column;
  justify-content: flex-start;
  gap: 12px;
  padding: 14px 15px 13px;
  flex: 1 1 auto;
  min-height: 0;
  position: relative;
  z-index: 1;
  transition: transform 0.22s ease;
}

.analysis-library-card-featured .analysis-library-card-body {
  gap: 14px;
  padding: 16px 17px 15px;
}

.analysis-library-card:hover .analysis-library-card-body,
.analysis-library-card.active .analysis-library-card-body {
  transform: translateY(-1px);
}

.analysis-library-card-body > *:not(.analysis-library-watermark) {
  position: relative;
  z-index: 1;
}

.analysis-library-watermark {
  position: absolute;
  left: 10px;
  top: 50%;
  width: 76px;
  height: 76px;
  display: grid;
  place-items: center;
  transform: translateY(-50%);
  opacity: 0.08;
  pointer-events: none;
  z-index: 0;
  transition:
    transform 0.28s ease,
    opacity 0.28s ease,
    filter 0.28s ease;
}

.analysis-library-watermark i {
  font-size: 4.15rem;
  line-height: 1;
}

.analysis-library-watermark .solana-icon,
.analysis-library-watermark .xrp-symbol {
  width: 68px;
  height: 68px;
}

.analysis-library-card:hover .analysis-library-watermark,
.analysis-library-card.active .analysis-library-watermark {
  transform: translateY(-50%) translateX(20px) scale(0.97);
  opacity: 0.115;
}

.analysis-library-shell.theme-btc .analysis-library-watermark i {
  color: rgba(255, 209, 102, 0.88);
  filter: drop-shadow(0 0 8px rgba(255, 179, 71, 0.06));
}

.analysis-library-shell.theme-btc .analysis-library-watermark .solana-icon,
.analysis-library-shell.theme-btc .analysis-library-watermark .xrp-symbol {
  opacity: 0.92;
  filter: saturate(0.92) sepia(0.32) hue-rotate(-12deg) brightness(1.04);
}

.analysis-library-shell.theme-eth .analysis-library-watermark i {
  color: rgba(132, 186, 255, 0.86);
  filter: drop-shadow(0 0 8px rgba(77, 134, 255, 0.06));
}

.analysis-library-shell.theme-eth .analysis-library-watermark .solana-icon,
.analysis-library-shell.theme-eth .analysis-library-watermark .xrp-symbol {
  filter: saturate(0.86) hue-rotate(180deg) brightness(0.98);
}

.analysis-library-shell.theme-sol .analysis-library-watermark i {
  color: rgba(167, 139, 250, 0.84);
  filter: drop-shadow(0 0 8px rgba(52, 152, 219, 0.05));
}

.analysis-library-shell.theme-sol .analysis-library-watermark .solana-icon,
.analysis-library-shell.theme-sol .analysis-library-watermark .xrp-symbol {
  filter: saturate(1.08) brightness(1.02) drop-shadow(0 0 8px rgba(52, 152, 219, 0.04));
}

.analysis-library-shell.theme-xrp .analysis-library-watermark i {
  color: rgba(255, 255, 255, 0.6);
  filter: drop-shadow(0 0 6px rgba(255, 255, 255, 0.03));
}

.analysis-library-shell.theme-xrp .analysis-library-watermark .solana-icon,
.analysis-library-shell.theme-xrp .analysis-library-watermark .xrp-symbol {
  filter: grayscale(0.1) brightness(1.06);
}

.analysis-library-card-topline {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: nowrap;
  gap: 12px;
  color: #e2e8ffdb;
  font-size: 0.68rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  padding-bottom: 10px;
  border-bottom: 1px solid color-mix(in srgb, var(--token-border-strong) 16%, rgba(255, 255, 255, 0.04));
  transition:
    border-color 0.22s ease,
    color 0.22s ease;
}

.analysis-library-card-meta {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  min-height: 20px;
  padding: 0;
  flex: 1 1 0;
  min-width: 0;
  transition:
    color 0.22s ease,
    opacity 0.22s ease;
}

.analysis-library-card-date,
.analysis-library-card-time,
.analysis-library-card-duration {
  white-space: nowrap;
}

.analysis-library-card-time {
  justify-content: center;
}

.analysis-library-card-duration {
  justify-content: flex-end;
}

.analysis-library-card-meta i {
  font-size: 0.78rem;
  background: var(--token-border-gradient);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent;
  filter: drop-shadow(0 0 7px color-mix(in srgb, var(--token-glow) 34%, transparent));
  opacity: 0.96;
}

.analysis-library-card-title {
  display: block;
  margin: 0;
  color: #f2f6ff;
  font-size: 0.94rem;
  line-height: 1.24;
  font-weight: 800;
  min-height: 2.48em;
  letter-spacing: -0.01em;
  text-wrap: balance;
  transition:
    color 0.22s ease,
    text-shadow 0.22s ease;
}

.analysis-library-card-featured .analysis-library-card-title {
  font-size: 1.12rem;
  line-height: 1.14;
  min-height: 0;
  max-width: min(100%, 520px);
}

.analysis-library-card-summary {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  color: color-mix(in srgb, #ffffff 72%, var(--token-glow));
  font-size: 0.68rem;
  line-height: 1.36;
  font-weight: 800;
  margin-top: auto;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  opacity: 0.92;
  transition:
    color 0.22s ease,
    transform 0.22s ease,
    opacity 0.22s ease;
}

.analysis-library-card-summary-featured {
  width: auto;
  justify-content: flex-start;
  gap: 12px;
  flex-wrap: wrap;
}

.analysis-library-card-summary-featured-action {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}

.analysis-library-card-summary .library-feature-current {
  min-height: 24px;
  padding: 0 11px;
}

.analysis-library-card-summary-dot {
  position: relative;
  width: 9px;
  height: 9px;
  flex: 0 0 9px;
  border-radius: 999px;
  display: inline-block;
  z-index: 1;
  isolation: isolate;
  background: var(--token-border-gradient);
  box-shadow:
    0 0 0 1px color-mix(in srgb, var(--token-border-strong) 26%, transparent),
    0 0 12px color-mix(in srgb, var(--token-glow) 38%, transparent);
}

.analysis-library-card-summary-dot::after {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  transform-origin: center;
  transform: scale(1);
  background: color-mix(in srgb, var(--token-glow) 74%, transparent);
  opacity: 0.6;
  filter: blur(0.5px);
  animation: status-ping 1.6s ease-out infinite;
  z-index: -1;
}

.analysis-library-card-summary-dot::before {
  content: '';
  position: absolute;
  inset: 1px;
  border-radius: inherit;
  background: linear-gradient(180deg, rgba(255, 255, 255, 0.34), rgba(255, 255, 255, 0.04));
  opacity: 0.72;
}

.analysis-library-shell.theme-btc .analysis-library-card-summary-dot {
  box-shadow:
    0 0 0 1px rgba(255, 209, 102, 0.24),
    0 0 12px rgba(249, 115, 22, 0.34);
}

.analysis-library-shell.theme-eth .analysis-library-card-summary-dot {
  box-shadow:
    0 0 0 1px rgba(132, 186, 255, 0.24),
    0 0 12px rgba(111, 165, 255, 0.3);
}

.analysis-library-shell.theme-sol .analysis-library-card-summary-dot {
  box-shadow:
    0 0 0 1px rgba(133, 145, 235, 0.24),
    0 0 12px rgba(133, 145, 235, 0.34);
}

.analysis-library-shell.theme-xrp .analysis-library-card-summary-dot {
  box-shadow:
    0 0 0 1px rgba(255, 255, 255, 0.2),
    0 0 12px rgba(255, 255, 255, 0.24);
}

.analysis-library-shell.theme-btc .analysis-library-card-summary,
.analysis-library-shell.theme-btc .analysis-library-card-featured .analysis-library-card-title {
  text-shadow: 0 0 14px rgba(255, 187, 92, 0.14);
}

.analysis-library-shell.theme-eth .analysis-library-card-summary,
.analysis-library-shell.theme-eth .analysis-library-card-featured .analysis-library-card-title {
  text-shadow: 0 0 14px rgba(111, 165, 255, 0.16);
}

.analysis-library-shell.theme-eth .library-feature-current {
  color: color-mix(in srgb, #ffffff 78%, rgba(162, 209, 255, 0.92));
}

.analysis-library-shell.theme-eth .library-feature-current::before {
  background:
    linear-gradient(135deg, rgba(146, 198, 255, 0.96), rgba(77, 134, 255, 0.82));
  box-shadow:
    inset 0 0 0 1px rgba(146, 198, 255, 0.28),
    0 0 14px rgba(111, 165, 255, 0.14);
}

.analysis-library-shell.theme-eth .library-feature-current::after {
  background:
    linear-gradient(180deg, rgba(12, 18, 34, 0.97), rgba(9, 13, 24, 0.99));
  box-shadow:
    inset 0 1px 0 rgba(227, 239, 255, 0.12),
    inset 0 0 0 1px rgba(132, 186, 255, 0.24);
}

.analysis-library-shell.theme-sol .analysis-library-card-summary,
.analysis-library-shell.theme-sol .analysis-library-card-featured .analysis-library-card-title {
  text-shadow: 0 0 14px rgba(133, 145, 235, 0.16);
}

.analysis-library-shell.theme-xrp .analysis-library-card-summary,
.analysis-library-shell.theme-xrp .analysis-library-card-featured .analysis-library-card-title {
  text-shadow: 0 0 14px rgba(255, 255, 255, 0.12);
}

.analysis-library-card.active .analysis-library-card-summary {
  color: rgba(236, 241, 255, 0.88);
}

.library-archive-empty {
  min-height: 160px;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 14px;
  border: 1px dashed rgba(255, 255, 255, 0.12);
  color: rgba(196, 206, 233, 0.72);
  background: rgba(255, 255, 255, 0.03);
  text-align: center;
  padding: 18px;
}

@keyframes card-border-breathe {
  0% {
    opacity: 0;
    transform: scale(0.993);
  }
  35% {
    opacity: 0.8;
  }
  100% {
    opacity: 0;
    transform: scale(1.01);
  }
}

@keyframes analysis-dot-breathe {
  0%,
  100% {
    transform: scale(1);
    opacity: 0.92;
  }
  50% {
    transform: scale(1.16);
    opacity: 1;
  }
}

@keyframes status-ping {
  0% { transform: scale(1); opacity: 0.5; }
  60% { transform: scale(2.2); opacity: 0; }
  100% { transform: scale(2.4); opacity: 0; }
}

@keyframes spotlight-point-pulse {
  0% {
    transform: scale(0.88);
    opacity: 0.58;
  }
  70% {
    transform: scale(2.35);
    opacity: 0;
  }
  100% {
    transform: scale(2.5);
    opacity: 0;
  }
}

@keyframes spotlight-point-breathe {
  0%,
  100% {
    opacity: 0.58;
  }
  50% {
    opacity: 0.92;
  }
}

@keyframes price-emblem-breathe {
  0%,
  100% {
    opacity: 0.14;
    transform: scale(0.98);
  }
  50% {
    opacity: 0.2;
    transform: scale(1.02);
  }
}

@keyframes analysis-dot-pulse {
  0% {
    transform: translate(-50%, -50%) scale(0.95);
    opacity: 0.62;
  }
  65% {
    transform: translate(-50%, -50%) scale(2.35);
    opacity: 0;
  }
  100% {
    transform: translate(-50%, -50%) scale(2.55);
    opacity: 0;
  }
}

@keyframes latest-chip-outline {
  0%,
  100% {
    opacity: 0.88;
    filter: brightness(1);
    box-shadow:
      inset 0 0 0 1px color-mix(in srgb, var(--token-border-strong) 24%, transparent),
      0 0 10px color-mix(in srgb, var(--token-border-strong) 14%, transparent);
  }
  50% {
    opacity: 1;
    filter: brightness(1.14);
    box-shadow:
      inset 0 0 0 1px color-mix(in srgb, var(--token-border-strong) 38%, transparent),
      0 0 16px color-mix(in srgb, var(--token-border-strong) 28%, transparent);
  }
}

@keyframes latest-chip-sheen {
  0%,
  18%,
  100% {
    opacity: 0.94;
    filter: brightness(1);
  }
  40% {
    opacity: 1;
    filter: brightness(1.1);
  }
  58% {
    opacity: 0.96;
    filter: brightness(1.04);
  }
}

@media (prefers-reduced-motion: reduce) {
  .trend-pill,
  .trend-pill .pill-label,
  .trend-pill .pill-timeframe,
  .trend-pill .pill-value,
  .spotlight-mini-chart,
  .spotlight-mini-chart::before,
  .spotlight-mini-chart-stats,
  .spotlight-mini-chart-price,
  .spotlight-mini-chart-window,
  .analysis-library-card,
  .analysis-library-card::before,
  .analysis-library-card-body,
  .analysis-library-watermark,
  .analysis-library-card-topline,
  .analysis-library-card-meta,
  .analysis-library-card-title,
  .analysis-library-card-summary,
  .library-feature-current,
  .library-feature-current::before,
  .library-feature-current::after {
    transition: none !important;
    animation: none !important;
  }
}

@keyframes card-border-pulse {
  0%,
  100% {
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.06),
      0 0 0 1px rgba(10, 15, 28, 0.35) inset,
      0 0 0 rgba(0, 0, 0, 0);
  }
  50% {
    box-shadow:
      inset 0 1px 0 rgba(255, 255, 255, 0.08),
      0 0 0 1px color-mix(in srgb, var(--ring-a) 42%, transparent) inset,
      0 0 14px color-mix(in srgb, var(--ring-a) 36%, transparent);
  }
}

@keyframes general-title-line-pulse-fixed {
  0%,
  100% {
    opacity: 0.6;
    filter: brightness(1);
    box-shadow:
      0 0 6px color-mix(in srgb, currentColor 42%, transparent),
      0 0 0 1px color-mix(in srgb, currentColor 20%, transparent) inset;
  }
  50% {
    opacity: 1;
    filter: brightness(1.2);
    box-shadow:
      0 0 12px color-mix(in srgb, currentColor 65%, transparent),
      0 0 0 1px color-mix(in srgb, currentColor 34%, transparent) inset;
  }
}


.error-message {
  margin-top: 16px;
  padding: 14px;
  border-radius: 12px;
  background: rgba(239, 68, 68, 0.12);
  border: 1px solid rgba(239, 68, 68, 0.4);
  color: #fecdd3;
}

.loading {
  margin-top: 18px;
  text-align: center;
  color: var(--muted);
}

.analysis-video-overlay {
  position: fixed;
  inset: 0;
  z-index: 2600;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background:
    radial-gradient(circle at 14% 12%, rgba(78, 115, 223, 0.32), transparent 42%),
    radial-gradient(circle at 86% 82%, rgba(155, 89, 182, 0.28), transparent 44%),
    radial-gradient(circle at 50% 100%, rgba(45, 183, 255, 0.12), transparent 58%),
    rgba(6, 8, 14, 0.84);
  backdrop-filter: blur(12px) saturate(115%);
}

.analysis-video-modal {
  --video-border-a: rgba(95, 136, 255, 0.8);
  --video-border-b: rgba(103, 202, 255, 0.58);
  --video-border-c: rgba(189, 114, 255, 0.8);
  --video-bg-a: rgba(10, 17, 34, 0.94);
  --video-bg-b: rgba(8, 14, 28, 0.96);
  --video-accent: rgba(208, 222, 255, 0.84);
  --video-frame-border: rgba(102, 144, 255, 0.65);
  --video-close-border: rgba(139, 167, 255, 0.46);
  --video-close-a: rgba(78, 115, 223, 0.7);
  --video-close-b: rgba(155, 89, 182, 0.68);
  --video-close-hover-border: rgba(176, 197, 255, 0.8);
  --video-close-hover-shadow: rgba(8, 13, 28, 0.45);
  position: relative;
  width: min(1160px, 100%);
  max-height: calc(100dvh - 16px);
  overflow: auto;
  border-radius: 20px;
  border: 1px solid transparent;
  background:
    linear-gradient(160deg, var(--video-bg-a), var(--video-bg-b)) padding-box,
    linear-gradient(125deg, var(--video-border-a), var(--video-border-b), var(--video-border-c)) border-box;
  padding: 18px 18px 14px;
  box-shadow:
    0 28px 58px rgba(0, 0, 0, 0.55),
    0 0 0 1px rgba(164, 187, 255, 0.16) inset,
    0 0 34px rgba(73, 118, 255, 0.16);
  font-family: 'Space Grotesk', 'Manrope', 'Inter', 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif;
}

.analysis-video-modal.token-btc {
  --video-border-a: rgba(255, 209, 102, 0.95);
  --video-border-b: rgba(249, 115, 22, 0.86);
  --video-border-c: rgba(255, 189, 82, 0.9);
  --video-bg-a: rgba(30, 21, 8, 0.95);
  --video-bg-b: rgba(19, 13, 6, 0.96);
  --video-accent: rgba(255, 224, 143, 0.88);
  --video-frame-border: rgba(249, 159, 44, 0.72);
  --video-close-border: rgba(255, 196, 109, 0.56);
  --video-close-a: rgba(255, 173, 66, 0.72);
  --video-close-b: rgba(249, 115, 22, 0.7);
  --video-close-hover-border: rgba(255, 216, 141, 0.9);
  --video-close-hover-shadow: rgba(58, 31, 9, 0.52);
}

.analysis-video-modal.token-eth {
  --video-border-a: rgba(132, 186, 255, 0.96);
  --video-border-b: rgba(95, 156, 255, 0.88);
  --video-border-c: rgba(77, 134, 255, 0.92);
  --video-bg-a: rgba(12, 18, 34, 0.95);
  --video-bg-b: rgba(10, 14, 26, 0.96);
  --video-accent: rgba(210, 227, 255, 0.92);
  --video-frame-border: rgba(132, 186, 255, 0.74);
  --video-close-border: rgba(168, 205, 255, 0.58);
  --video-close-a: rgba(132, 186, 255, 0.76);
  --video-close-b: rgba(95, 156, 255, 0.72);
  --video-close-hover-border: rgba(210, 227, 255, 0.9);
  --video-close-hover-shadow: rgba(16, 22, 44, 0.55);
}

.analysis-video-modal.token-sol {
  --video-border-a: rgba(167, 139, 250, 0.94);
  --video-border-b: rgba(52, 152, 219, 0.9);
  --video-border-c: rgba(136, 120, 240, 0.88);
  --video-bg-a: rgba(17, 15, 36, 0.95);
  --video-bg-b: rgba(10, 20, 33, 0.96);
  --video-accent: rgba(205, 190, 255, 0.9);
  --video-frame-border: rgba(121, 170, 242, 0.72);
  --video-close-border: rgba(167, 150, 244, 0.56);
  --video-close-a: rgba(167, 139, 250, 0.72);
  --video-close-b: rgba(52, 152, 219, 0.72);
  --video-close-hover-border: rgba(202, 189, 255, 0.88);
  --video-close-hover-shadow: rgba(20, 27, 50, 0.55);
}

.analysis-video-modal.token-xrp {
  --video-border-a: rgba(255, 255, 255, 0.9);
  --video-border-b: rgba(149, 149, 149, 0.72);
  --video-border-c: rgba(12, 12, 12, 0.94);
  --video-bg-a: rgba(18, 18, 18, 0.95);
  --video-bg-b: rgba(6, 6, 6, 0.96);
  --video-accent: rgba(241, 245, 249, 0.92);
  --video-frame-border: rgba(214, 214, 214, 0.58);
  --video-close-border: rgba(215, 215, 215, 0.52);
  --video-close-a: rgba(229, 231, 235, 0.62);
  --video-close-b: rgba(58, 58, 58, 0.72);
  --video-close-hover-border: rgba(255, 255, 255, 0.88);
  --video-close-hover-shadow: rgba(0, 0, 0, 0.58);
}

.analysis-video-modal::before {
  content: '';
  position: absolute;
  inset: 0;
  border-radius: inherit;
  background:
    radial-gradient(circle at 10% 0, rgba(91, 143, 255, 0.16), transparent 44%),
    radial-gradient(circle at 88% 12%, rgba(186, 119, 255, 0.12), transparent 44%);
  pointer-events: none;
}

.analysis-video-close {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 32px;
  height: 32px;
  padding: 0;
  border-radius: 999px;
  border: 1px solid var(--video-close-border);
  background:
    linear-gradient(165deg, rgba(18, 25, 43, 0.8), rgba(12, 17, 30, 0.82)) padding-box,
    linear-gradient(135deg, var(--video-close-a), var(--video-close-b)) border-box;
  -webkit-appearance: none;
  appearance: none;
  color: #fff;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  line-height: 1;
  box-shadow: 0 8px 20px rgba(0, 0, 0, 0.35);
  transition: transform 0.2s ease, box-shadow 0.2s ease, border-color 0.2s ease;
  z-index: 4;
}

.analysis-video-close i {
  font-size: 0.82rem;
}

.analysis-video-close:hover {
  transform: translateY(-1px);
  border-color: var(--video-close-hover-border);
  box-shadow: 0 10px 24px var(--video-close-hover-shadow);
}

.analysis-video-close:focus-visible {
  outline: 2px solid rgba(143, 181, 255, 0.85);
  outline-offset: 2px;
}

.analysis-video-kicker {
  margin: 0 0 8px;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif;
  color: var(--video-accent);
  font-size: 0.8rem;
  text-transform: uppercase;
  letter-spacing: 0.09em;
  padding-right: 44px;
  position: relative;
  z-index: 1;
}

.analysis-video-asset {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}

.analysis-video-kicker-sep {
  margin: 0 8px;
  opacity: 0.72;
}

.analysis-video-kicker .asset-icon {
  font-size: 0.8em;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.analysis-video-kicker .asset-icon i {
  font-size: 1em;
}

.analysis-video-kicker .asset-icon .solana-icon {
  width: 0.82em;
  height: 0.82em;
  display: block;
}

.analysis-video-kicker .asset-icon .xrp-symbol {
  width: 0.82em;
  height: 0.82em;
  display: block;
}

.analysis-video-kicker .asset-label {
  font-weight: 700;
}

.analysis-video-title {
  margin: 0 46px 10px 0;
  font-family: 'Space Grotesk', 'Manrope', 'Inter', 'Apple Color Emoji', 'Segoe UI Emoji', 'Noto Color Emoji', sans-serif;
  color: #f3f6ff;
  font-size: clamp(1.12rem, 2.4vw, 1.6rem);
  font-weight: 800;
  line-height: 1.2;
  position: relative;
  z-index: 1;
}

.analysis-video-frame-shell {
  width: 100%;
  aspect-ratio: 16 / 9;
  max-height: min(72dvh, calc(100dvh - 190px));
  border-radius: 16px;
  overflow: hidden;
  background: #05070d;
  border: 1px solid var(--video-frame-border);
  box-shadow:
    inset 0 0 0 1px rgba(183, 204, 255, 0.08),
    0 12px 34px rgba(5, 8, 18, 0.55);
  position: relative;
  z-index: 1;
}

.analysis-video-frame-shell iframe {
  width: 100%;
  height: 100%;
  display: block;
  border-radius: 16px;
  background: #05070d;
}

.analysis-video-empty {
  width: 100%;
  height: 100%;
  min-height: 220px;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  gap: 10px;
  color: rgba(220, 230, 255, 0.86);
  text-align: center;
  padding: 16px;
}

.analysis-video-empty a {
  color: #9fd3ff;
  text-decoration: none;
  border-bottom: 1px dashed rgba(159, 211, 255, 0.52);
}

.analysis-video-empty a:hover {
  color: #d5edff;
  border-bottom-color: rgba(213, 237, 255, 0.76);
}

.analysis-video-fade-enter-active,
.analysis-video-fade-leave-active {
  transition: opacity 0.2s ease;
}

.analysis-video-fade-enter-from,
.analysis-video-fade-leave-to {
  opacity: 0;
}

@media (max-width: 1024px) {
  .content-grid {
    grid-template-columns: 1fr;
  }

  .analysis-featured-actions {
    grid-template-columns: 1fr;
  }

  .library-feature-frame {
    grid-template-columns: 1fr;
  }

  .library-feature-media {
    min-height: 120px;
  }

  .analysis-library-shell {
    min-height: auto;
    max-height: none;
    height: auto;
  }

  .library-archive-panel {
    min-height: auto;
  }

  .analysis-library-grid-inline {
    grid-auto-rows: auto;
    overflow: visible;
    padding-right: 0;
  }

  .library-archive-head {
    align-items: flex-start;
    flex-direction: column;
  }

  .library-archive-caption {
    max-width: none;
    text-align: left;
  }
}

@media (max-width: 720px) {
  .spotlight-head-stack {
    flex-direction: column;
  }

  .analysis-featured-head {
    flex-direction: column;
  }

  .analysis-featured-shortcut {
    width: 100%;
    justify-content: center;
  }

  .analysis-library-grid {
    grid-template-columns: 1fr;
  }

  .analysis-library-grid-inline {
    grid-template-columns: 1fr;
    grid-auto-rows: auto;
  }

  .library-feature-primary {
    width: 100%;
    min-width: 0;
  }
}

@media (max-width: 767px) {
  .analysis-shell {
    min-height: calc(100vh - var(--app-header-h));
    height: auto;
    padding: calc(var(--app-header-h) + 10px) var(--app-gutter) 14px;
  }

  .page-header {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .card-top {
    flex-direction: column;
    align-items: stretch;
  }

  .general-trend-card {
    flex: 0 0 auto;
    width: 100%;
  }

  .last-sync {
    width: 100%;
  }

  .footer-grid {
    grid-template-columns: 1fr;
  }

  .price-line {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    row-gap: 8px;
  }

  .day-levels {
    width: 100%;
    min-width: 0;
  }
}
</style>
